import Database, {
  DeleteMessageMode,
  STATE_KEY_SESSION_ID,
} from "../utilities/database";
import { v1 as uuid, v1 } from "uuid";
import {
  addWeeks,
  addDays,
  isBefore,
  endOfToday,
  isToday,
  isAfter,
  startOfDay,
  differenceInDays,
  getDate,
  endOfDay,
  differenceInCalendarDays,
  differenceInWeeks,
  differenceInMonths,
  endOfTomorrow,
  startOfTomorrow,
  format,
  isWeekend,
  differenceInSeconds,
  differenceInMinutes,
  addMinutes,
  startOfToday,
  addHours,
  parseISO,
} from "date-fns";
import Cookies, {
  COOKIE_KEY_POSITION_TICKS,
  COOKIE_KEY_SELECTED_SESSION_USER_ID,
} from "../utilities/cookies";
import {
  CountType,
  AvailabilityType,
  ControlType,
  LockTypes,
  LockCountType,
  AuditActionTypes,
  ItemTypes,
  EventType,
  EventDay,
  TaskType,
  ProfileRole,
  PendingUpdateType,
  AvailabilityDayType,
  JavascriptDay,
  HygieneBreak,
  LovensePresetName,
  ProofStatus,
  PenaltyType,
  TraceLevel,
  PreselectType,
} from "../ts/enums";
import {
  MessageType,
  NotificationType,
  messageRequiresInteraction,
  sendHolidayModeMessageToMember,
  sendHouseRules,
  sendLoanedMessage,
  sendLoaneeMessage,
  sendMessageToMember,
  sendMessageToOwner,
  sendMessageToSelf,
  sendMessageToSystem,
  sendOfferMessageToMember,
  sendOwnedMessage,
  sendPenaltyAddedMessageToOwner,
  sendPilloryAddedMessageToOwner,
  sendPreviewMessageToMember,
  sendRequiredEventsMessageToMember,
  sendSharedMessage,
  sendShareeMessage,
  sendUrlRequestPurchase,
  sendUrlRequestToPurchase,
} from "./messaging";
import debounce from 'lodash/debounce';
import sample from 'lodash/sample';
import { Unsubscribe } from "firebase/database";
import Settings, { RANDOM_GAME_OF_TYPE_PREFIX } from "./settings";
import { ListenerType } from '../services/SubscriptionManager';
import ProfileSettings from "./ProfileSettings";
import ConfigCache from "../utilities/ConfigCache";
import { stripSettings } from "../utilities/settingsManager";
import {
  canHandleDiscordCalls,
  handleDiscordOwnedItemsTicker,
  handleDiscordPausedLockTicker,
  handleDiscordPilloryTickerDown,
  handleDiscordPilloryTickerUp,
  handleDiscordVoteTickerDown,
  handleDiscordVoteTickerRoulette,
  handleDiscordVoteTickerUp,
} from "../utilities/discord";
import Emitter, { ShowOptionPickerDialog } from "../utilities/Emitter";
import { CacheRecord, IEntityCache } from "./CacheRecord";
import { pushLovense } from "../utilities/externalApiUtils";
import { getProfileImage } from "../utilities/imageUtils";
import {
  BaseBasketItem,
  TaskBasketItem,
  PenaltyBasketItem,
  BaseTimedBasketItem,
  IEndEventTriggered,
  ITaskBasketInfo,
  IPermissionBasketInfo,
  ISchedulerTimerInfo,
  IAuditLogsDateCollection,
  PilloryTopic,
  ILockReceipt,
  VoteTask,
  IAuditScoreHistory,
  IAttendanceRequest,
  IAuditBankDateCollection,
  IMemberMessage,
  IMemberSentReceipt,
  LovenseToy,
  IOwnerProfileSession,
  ControlRecord,
  ISessionSettings,
  OwnedItemsCollection,
  IShowNotificationState,
  ITaskItem,
  TaskItem,
  IPositionBasketInfo,
  AuditMessage,
  IPilloryTopic,
  IVoteTask,
  MessageContents,
  EncryptedOwnedItemsCollection,
  IPendingItem,
  IProofRequest,
  ILinesRequest,
  IRouletteRequest,
  IRequestLockState,
  IReportRequest,
  IPermissionItem,
  PermissionItem,
  IPermissionSettings,
  IScheduledItem,
  IBasket,
  ISettingsItem,
  IPenaltyTimerInfo,
  ITaskTimerInfo,
  ScheduleBasketItem,
  PermissionBasketItem,
  PositionBasketItem,
  IPenaltyItem,
  ILockState,
  IGeneralSettings,
  UserActionAuditMessage,
  RollState,
  IEventItem,
  IControl,
  IOwnerHistory,
  IUserRecord,
  ICategoryItem,
  IScheduledInfo,
  IItemSupport,
  ITaskSafety,
  IFavourite,
  ItemAdditionOptions,
  Holiday,
  IPermissionTimerInfo,
  ITaskProofRequestItem,
  IPenaltyProofRequestItem,
  ITaskVoteConfig,
  LovenseCommand,
  PositionItem,
  PenaltyItem,
  EventItem,
  EventBasketItem,
  ICustomSchedule,
  ITodoItem,
  IShoppingRequest,
  ICustomScheduleEvent,
  IGame,
  IGameResult,
  IScheduledGame,
  GameTypes,
  getGameTypeName,
  ITaskRating,
  IWizardTemplate,
  IWeeklyTargetProgress,
} from "../ts/Definitions";
import {
  isTimedItem,
  isAvailableItem,
  decrypt,
  cloneObject,
  getDecimalRandom,
  flipCoin,
  encrypt,
  getItemTypeDisplayName,
  getOwnerUserId,
  getRandomItemWithCache,
  memberOwnerKeySuffix,
  isCategoryLimited,
  isRecord,
  convertToCountTypeString,
  diffObjects,
  getRandomItem,
  getPilloryTopicDefaults,
  getCountMinutes,
  toTitleCase,
} from "../utilities/utils";
import {
  notifications_page,
  editProfile_page,
  home_page,
} from "../controls/ProtectedPage";
import {
  convertToEventDayFromDate,
  isWeekendDay,
  convertToDateDay,
  getNextEventDateTime,
  getDateForDay,
  formatValidDate,
  convertDateToDay,
  getScheduleTime,
  asDate,
  getLastScheduleOffsetTimeForDay,
  getSODStartTime,
} from "../utilities/dateUtils";

// TEMPORARY: cold-start load performance logging — remove before next deploy
const loadPerf = (label: string, ...extra: unknown[]) => {
  if (typeof performance === 'undefined') return;
  if (import.meta.env.VITE_ENABLE_LOAD_PERF !== 'true') return;
  const g = globalThis as any;
  if (!g.__loadPerfT0) g.__loadPerfT0 = performance.now();
  console.log(`[LOAD_PERF] +${(performance.now() - g.__loadPerfT0).toFixed(0)}ms | ${label}`, ...extra);
};

// wrapper around settings from db
// runs business rules based on settings loaded from db
// settings instance is for a user

// State key constants — defined in stateKeys.ts, re-exported here for backward compatibility.
export {
  STATE_KEY_FAVOURITE_ITEMS,
  STATE_KEY_POSITIONS,
  STATE_KEY_METRONOME,
  STATE_KEY_SITE_EFFECTS,
  STATE_KEY_REPORTCOMPLETE,
  STATE_KEY_HISTORY_CREDITS,
  STATE_KEY_HISTORY_POSITIONS,
  STATE_KEY_HISTORY_PENALTIES,
  STATE_KEY_HISTORY_EVENTTASKS,
  STATE_KEY_HISTORY_TASK_OPTIONS,
  STATE_KEY_LINES_IN_PROGRESS,
  STATE_KEY_HISTORY_TASK_ITEMS_USED,
  STATE_KEY_OVERRIDE_TIMES,
  STATE_KEY_PROOF_REQUESTS,
  STATE_KEY_CURRENT_DAY,
  PREVIEW_EVENT_TASKS,
  STATE_KEY_COMMUNITY_FILTERS,
  STATE_KEY_SESSION_VIEW,
  STATE_KEY_LAST_LEAST_USED_CHECK,
  STATE_KEY_TOUR_STATE,
  STATE_KEY_CUSTOM_DAY,
} from './stateKeys';
import {
  STATE_KEY_FAVOURITE_ITEMS,
  STATE_KEY_CREDITS,
  STATE_KEY_PERMISSIONS,
  STATE_KEY_POSITIONS,
  STATE_KEY_METRONOME,
  STATE_KEY_SITE_EFFECTS,
  STATE_KEY_SCHEDULER,
  STATE_KEY_REPORTCOMPLETE,
  STATE_KEY_MORNINGREPORT,
  STATE_KEY_DAILYREPORT,
  STATE_KEY_LAST_RUN_DATES,
  STATE_KEY_PREVIOUS_ITEM_SELECTIONS,
  STATE_KEY_HISTORY_CREDITS,
  STATE_KEY_HISTORY_POSITIONS,
  STATE_KEY_HISTORY_PENALTIES,
  STATE_KEY_HISTORY_EVENTTASKS,
  STATE_KEY_HISTORY_TASK_OPTIONS,
  STATE_KEY_LINES_IN_PROGRESS,
  STATE_KEY_HISTORY_TASK_ITEMS_USED,
  STATE_KEY_PROOF_REQUEST_CAPTURE,
  STATE_KEY_LINES,
  STATE_KEY_ROULETTE,
  STATE_KEY_REQUEST_LOCK,
  STATE_KEY_WORK_DAYS,
  STATE_KEY_WEEKEND_DAYS,
  STATE_KEY_OVERRIDE_TIMES,
  STATE_KEY_PREVIEW_DAYS,
  STATE_KEY_PREVIEW_BASKET,
  STATE_KEY_UNAVAILABLE_DAYS,
  STATE_KEY_HOLIDAYS,
  STATE_KEY_PROOF_REQUESTS,
  STATE_KEY_CURRENT_DAY,
  STATE_KEY_ENABLE_SOUNDS,
  PREVIEW_EVENT_TASKS,
  STATE_KEY_ACTIVE_SESSION_NAME,
  STATE_KEY_LOCK_STATE,
  STATE_KEY_COMMUNITY_FILTERS,
  STATE_KEY_SESSION_VIEW,
  STATE_KEY_LAST_HYGIENE,
  STATE_KEY_SUMMARY_TEXT_FLAGS,
  STATE_KEY_LAST_LEAST_USED_CHECK,
  STATE_KEY_TOUR_STATE,
  STATE_KEY_CUSTOM_DAY,
} from './stateKeys';

export const canRequireProof = (item: BaseBasketItem): boolean => {
  if (
    (item as TaskBasketItem | PenaltyBasketItem).proofStatus ===
    ProofStatus.neverRequestProof
  )
    return false;
  const timed =
    item instanceof BaseTimedBasketItem &&
    (isTimedItem((item as BaseTimedBasketItem).countType) ||
      (item as IEndEventTriggered).endTriggerEvent ||
      (item as TaskBasketItem).permanent);
  if (item.isComplete || !timed) return false; // can't prove something not in progress or completed
  if (item.itemType === ItemTypes.position) return false; // posiitons don't support it
  if (
    (item as TaskBasketItem).selectedCondition &&
    (item.itemType === ItemTypes.permission ||
      item.itemType === ItemTypes.event)
  )
    return true;
  if ((item as TaskBasketItem).creditTaskType === TaskType.acknowledgement)
    return false;
  if ((item as PenaltyBasketItem).penaltyType === PenaltyType.acknowledgement)
    return false;
  return true;
};

export default class StatefulSettings extends Settings {
  private inStateTransaction = false;
  private state: Record<string, string> = {};
  private pendingState: IPendingItem[] = [];
  private pendingStateUpdates: Record<string, string> = {};
  private onPendingStateReadyCallback?: () => void;
  private awaitingState = true;
  private awaitingOwnerProfiles = true;
  private awaitingOwnedItems = true;
  private taskSchedule: ITaskBasketInfo = {};
  private permissionSchedule: IPermissionBasketInfo = {};
  private eventSchedule: ISchedulerTimerInfo[] = [];

  private systemAuditLogsCache:
    | Record<string, IAuditLogsDateCollection>
    | undefined;
  private auditLogsCache: IAuditLogsDateCollection | undefined;
  private pilloriesCache: PilloryTopic[] | undefined;
  private taskLocksCache:
    | Record<string, Record<string, ILockReceipt>>
    | undefined;
  private proofRequestsCache: Record<string, unknown> | undefined;
  private votingTasksCache: VoteTask[] | undefined;
  private auditScoresCache: IAuditScoreHistory[] | undefined;
  private weeklyTargetProgressCache:
    | Record<string, Record<string, IWeeklyTargetProgress>>
    | undefined;
  private requestAttendanceCache: IAttendanceRequest | undefined | null = null;
  private allAttendanceRequestsCache:
    | Record<string, IAttendanceRequest>
    | undefined
    | null = null;
  private auditBankCache: IAuditBankDateCollection | undefined;
  private memberMessagesCache: IMemberMessage[] | undefined;
  private sentMessagesCache: IMemberSentReceipt[] | undefined;
  private lovenseToysCache: LovenseToy[] | undefined;
  private ownerProfilesCache: IOwnerProfileSession[] = [];
  private ownerControlRecordCache: ControlRecord | undefined;
  private ownedItemsCache: OwnedItemsCollection = {};
  private cachedProfileSettings: Record<string, ISessionSettings> = {};

  private get systemAuditLogs() {
    if (!this.systemAuditLogsCache) {
      this.systemAuditLogsCache = {};
      // /audit/logs root is every user's logs for the retention window.
      if (this.isSystemLoggedOn())
        this.unsubscribeSystemAuditLogs = this.database.snapAndSubAuditLogs(
          "",
          this.systemAuditLogsChangedCallback
        );
    }
    return this.systemAuditLogsCache;
  }

  private get auditLogs() {
    if (!this.auditLogsCache) {
      this.auditLogsCache = {};
      this.unsubscribeAuditLogs = this.database.snapAndSubAuditLogs(
        this.controlRecord.userid,
        this.auditLogsChangedCallback
      );
    }
    return this.auditLogsCache;
  }

  private get auditBank() {
    if (!this.auditBankCache) {
      this.auditBankCache = {};
      this.unsubscribeAuditBank = this.database.snapAndSubAuditBank(
        this.controlRecord.userid,
        this.auditBankChangedCallback
      );
    }
    return this.auditBankCache;
  }

  private get auditScores() {
    if (!this.auditScoresCache) {
      this.auditScoresCache = [];
      this.unsubscribeAuditScores = this.database.snapAndSubAuditScores(
        this.controlRecord.userid,
        this.auditScoresChangedCallback
      );
    }
    return this.auditScoresCache;
  }

  private get attendanceRequests() {
    if (this.requestAttendanceCache === null) {
      this.requestAttendanceCache = undefined;
      this.unsubscribeRequestAttendance =
        this.database.snapAndSubAttendanceRequests(
          this.controlRecord.userid,
          this.attendanceRequestCallback
        );
    }
    return this.requestAttendanceCache;
  }

  private get weeklyTargetProgress() {
    if (!this.weeklyTargetProgressCache) {
      this.weeklyTargetProgressCache = {};
      this.unsubscribeWeeklyTargetProgress =
        this.database.snapAndSubWeeklyTargetProgress(
          this.controlRecord.userid,
          this.weeklyTargetProgressChangedCallback
        );
    }
    return this.weeklyTargetProgressCache;
  }

  private get allAttendanceRequests() {
    if (this.allAttendanceRequestsCache === null) {
      this.allAttendanceRequestsCache = undefined;
      this.unsubscribeAllAttendanceRequests =
        this.database.snapAndSubAllAttendanceRequests(
          this.allAttendanceRequestCallback
        );
    }
    return this.allAttendanceRequestsCache;
  }

  private get sentMessages() {
    if (!this.sentMessagesCache) {
      this.sentMessagesCache = [];
      this.unsubscribeSentMessages = this.database.snapAndSubSentMessages(
        this.controlRecord.userid,
        this.onSentMessagesChanged
      );
    }
    return this.sentMessagesCache;
  }

  private get lovenseToys() {
    if (!this.lovenseToysCache) {
      this.lovenseToysCache = [];
      this.unsubscribeLovenseToys = this.database.snapAndSubLovenseToys(
        this.controlRecord.userid,
        this.onLovenseToysChanged
      );
    }
    return this.lovenseToysCache;
  }

  private get ownerProfiles() {
    if (!this.allOwnerProfilesFetched) {
      this.allOwnerProfilesFetched = true;
      void this.backgroundFetchAllOwnerProfiles();
    }
    return this.ownerProfilesCache;
  }

  private allOwnerProfilesFetched = false;

  private backgroundFetchAllOwnerProfiles = async (): Promise<void> => {
    const profiles = await this.database.fetchAllOwnerProfiles();
    profiles.forEach(p => {
      if (!this.ownerProfilesCache.some(e => e.userid === p.userid)) {
        this.ownerProfilesCache.push(p);
      }
    });
    this.subscriptionManager.fireListenersSync(ListenerType.OwnerProfiles, this.ownerProfilesCache);
  };

  /**
   * required to show task locks button
   */
  private get taskLocks() {
    if (!this.taskLocksCache) {
      this.taskLocksCache = {};
      this.unsubscribeTaskLocks = this.database.snapAndSubTaskLocks(
        this.controlRecord.userid,
        this.onTaskLocksChanged
      );
    }
    return this.taskLocksCache;
  }

  /**
   * required for the pillories button on startup
   */
  private get pillories() {
    if (!this.pilloriesCache) {
      this.pilloriesCache = [];
      this.unsubscribePillory = this.database.snapAndSubPillory(
        this.onPilloryChanged
      );
    }
    return this.pilloriesCache;
  }

  /**
   * required to show proof requests on start up
   */
  private get proofRequests() {
    if (!this.proofRequestsCache) {
      this.proofRequestsCache = {};
      this.unsubscribeProofRequests = this.database.snapAndSubProofRequests(
        this.controlRecord.userid,
        this.onProofRequestsChanged
      );
    }
    return this.proofRequestsCache;
  }

  /**
   * required for the votes button on startup
   */
  private get votingTasks() {
    if (!this.votingTasksCache) {
      this.votingTasksCache = [];
      this.unsubscribeVotingTasks = this.database.snapAndSubVoteTasks(
        this.onVotingTasks,
        v1()
      );
    }
    return this.votingTasksCache;
  }

  /**
   * required to show owner online
   */
  private get ownerControlRecord() {
    return this.ownerControlRecordCache;
  }

  /**
   * required for much processing when starting up
   */
  private get ownedItems() {
    return this.ownedItemsCache;
  }

  /**
   * required for the messages button on startup
   */
  private get memberMessages() {
    if (!this.memberMessagesCache) {
      this.memberMessagesCache = [];
      void this.database
        .snapAndSubMemberMessages(
          this.controlRecord.userid,
          this.onMemberMessagesChanged,
          this.onMemberMessageNotifications
        )
        .then((unsub) => {
          this.unsubscribeMemberMessages = unsub;
        });
    }
    return this.memberMessagesCache;
  }

  private cachedProfileSettingInstance: ProfileSettings | undefined = undefined;

  private notifiedMemberOfChanges = false;
  private debouncePushState = debounce(this.pushState, 100, {
    leading: false,
    trailing: true,
  });
  private sessionId = uuid();
  // guards against getCheckoutPermission (a read path invoked from render/tick hot paths)
  // repeatedly re-creating and re-writing a "Request Orgasm" permission for users who
  // don't have one yet; without this a missed cache/merge of the newly created item can
  // cause a runaway write loop (hundreds of Settings Writes per minute).
  private pendingCheckoutPermission?: IPermissionItem;
  private checkoutPermissionCreatedAt = 0;
  private static readonly CHECKOUT_PERMISSION_CREATION_COOLDOWN_MS = 60_000;

  private unsubscribeRequestAttendance: Unsubscribe | undefined;
  private unsubscribeState: Unsubscribe | undefined;
  private unsubscribeIntegrity: Unsubscribe | undefined;
  private unsubscribePendingState: Unsubscribe | undefined;
  private unsubscribeMemberMessages: (() => void) | undefined;
  private unsubscribeOwnerProfiles: Unsubscribe | undefined;
  private unsubscribeOwnedItems: Unsubscribe | undefined;
  private unsubscribeAuditLogs: Unsubscribe | undefined;
  private unsubscribeAuditBank: Unsubscribe | undefined;
  private unsubscribeAuditScores: Unsubscribe | undefined;
  private unsubscribeSentMessages: Unsubscribe | undefined;
  private unsubscribeSystemAuditLogs: Unsubscribe | undefined;
  private unsubscribePillory: (() => void) | undefined;
  private unsubscribeVotingTasks: Unsubscribe | undefined;
  private unsubscribeTaskLocks: Unsubscribe | undefined;
  private unsubscribeProofRequests: Unsubscribe | undefined;
  private unsubscribeWeeklyTargetProgress: Unsubscribe | undefined;
  private unsubscribeLovenseToys: Unsubscribe | undefined;
  private unsubscribeAllAttendanceRequests: Unsubscribe | undefined;

  protected updateControlListeners(): void {
    if (this.controlRecord.isOwned()) {
      this.ownerControlRecordCache = this.getAllControlRecords().find(
        (p) => p.userid === this.controlRecord.getOwner().userid
      );
      this.subscriptionManager.fireListenersSync(ListenerType.OwnerControlRecord, this.ownerControlRecord as ControlRecord);
    }
    super.updateControlListeners();
  }

  constructor(
    settingsUserId: string,
    database: Database,
    cookies: Cookies,
    showMessage: (args: IShowNotificationState) => void,
    cache: ConfigCache
  ) {
    super(database, showMessage, cookies, settingsUserId, cache);
    this.onSettingsChanged = this.onSettingsChanged.bind(this);
    void this.initialise(settingsUserId);
  }

  /**
   *
   * @returns is the settings fully loaded and ready for all interactions from the site
   */
  public isLoaded(): boolean {
    return (
      super.isLoaded() &&
      this.isOwnerProfilesLoaded() &&
      this.isStateLoaded() &&
      (this.isAdmin() || this.isOwnedItemsLoaded())
    );
  }

  public isStateLoaded = () => !this.awaitingState;
  public isOwnerProfilesLoaded = () => !this.awaitingOwnerProfiles;
  public isOwnedItemsLoaded = () => !this.awaitingOwnedItems;

  public dispose = (): void => {
    super.dispose();
    this.unsubscribeAll();
    if (this.unsubscribeState) this.unsubscribeState();
    if (this.unsubscribePendingState) this.unsubscribePendingState();
    if (this.unsubscribeMemberMessages) this.unsubscribeMemberMessages();
    if (this.unsubscribeOwnerProfiles) this.unsubscribeOwnerProfiles();
    if (this.unsubscribeOwnedItems) this.unsubscribeOwnedItems();
    if (this.unsubscribeAuditLogs) this.unsubscribeAuditLogs();
    if (this.unsubscribeAuditBank) this.unsubscribeAuditBank();
    if (this.unsubscribeAuditScores) this.unsubscribeAuditScores();
    if (this.unsubscribeSentMessages) this.unsubscribeSentMessages();
    if (this.unsubscribeSystemAuditLogs) this.unsubscribeSystemAuditLogs();
    if (this.unsubscribePillory) this.unsubscribePillory();
    if (this.unsubscribeVotingTasks) this.unsubscribeVotingTasks();
    if (this.unsubscribeTaskLocks) this.unsubscribeTaskLocks();
    if (this.unsubscribeProofRequests) this.unsubscribeProofRequests();
    if (this.unsubscribeWeeklyTargetProgress)
      this.unsubscribeWeeklyTargetProgress();
    if (this.unsubscribeLovenseToys) this.unsubscribeLovenseToys();
    if (this.unsubscribeIntegrity) this.unsubscribeIntegrity();
    if (this.unsubscribeRequestAttendance) this.unsubscribeRequestAttendance();
    if (this.unsubscribeAllAttendanceRequests)
      this.unsubscribeAllAttendanceRequests();
    this.caches.forEach((cache) => cache.dispose());
  };

  public subscribeToOwnerControlRecord = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.OwnerControlRecord, listener);
  };

  public unsubscribeToOwnerControlRecord = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.OwnerControlRecord, listener);
  };

  public getTaskAreItems = (): ITaskItem[] => {
    const items: ITaskItem[] = [];
    this.getTasks(
      (i) => i.isItem === true && isAvailableItem(i)
    ).forEach((c) => {
      items.push(c);
    });
    return items;
  };

  public getTasksWithItems = (): ITaskItem[] => {
    const items: ITaskItem[] = [];
    this.getTasks(
      (c) =>
        c.isItem === true ||
        (c.itemsUsed?.length ?? 0) > 0
    ).forEach((c) => {
      items.push(c);
    });
    return items;
  };

  public getTasks = (
    filter?: (i: ITaskItem) => boolean,
    includeNew?: boolean,
    adminMode?: boolean
  ): TaskItem[] => {
    const tasks = super.getTasks(filter, includeNew, adminMode);
    this.overrideTasksWithOwnedOptions(tasks);
    return tasks;
  };

  public overrideTasksWithOwnedOptions(credits: ITaskItem[]): void {
    if (this.isOwnedItemsLoaded() && this.ownedItems) {
      Object.keys(this.ownedItems).forEach((k) => {
        const match = credits.find((c) => c && c.key === k);
        if (match && TaskItem.IsItemToOwn(match)) {
          match.options = this.ownedItems[k];
        }
      });
    }
  }

  public getTaskSchedule() {
    return { ...this.taskSchedule };
  }
  public getPositionInfo = (): IPositionBasketInfo =>
    this.getState<IPositionBasketInfo>(
      STATE_KEY_POSITIONS,
      {} as IPositionBasketInfo
    );
  public getMetronomeInfo = (): IPositionBasketInfo =>
    this.getState<IPositionBasketInfo>(
      STATE_KEY_METRONOME,
      {} as IPositionBasketInfo
    );
  public getSiteEffectsInfo = (): IPositionBasketInfo =>
    this.getState<IPositionBasketInfo>(
      STATE_KEY_SITE_EFFECTS,
      {} as IPositionBasketInfo
    );
  public getPermissionInfo = (): IPermissionBasketInfo => ({
    ...this.permissionSchedule,
  });
  public getScheduleInfo = (): ISchedulerTimerInfo[] => [...this.eventSchedule];
  public getSessionSettings = (): ISessionSettings => this.settings;

  public unsubscribeAll = (): void => {
    super.unsubscribeAll();
    this.subscriptionManager.unsubscribe(ListenerType.SystemAuditLog);
    this.subscriptionManager.unsubscribe(ListenerType.AuditChanged);
    this.subscriptionManager.unsubscribe(ListenerType.VotingTasks);
    this.subscriptionManager.unsubscribe(ListenerType.Pillories);
    this.subscriptionManager.unsubscribe(ListenerType.MemberMessages);
    this.subscriptionManager.unsubscribe(ListenerType.SentMessages);
    this.subscriptionManager.unsubscribe(ListenerType.OwnedItems);
    this.subscriptionManager.unsubscribe(ListenerType.OwnerProfiles);
    this.subscriptionManager.unsubscribe(ListenerType.RequestAttendance);
    this.subscriptionManager.unsubscribe(ListenerType.ProofUploads);
    this.subscriptionManager.unsubscribe(ListenerType.WeeklyTargetProgress);
    this.subscriptionManager.unsubscribe(ListenerType.TaskLocks);
    this.subscriptionManager.unsubscribe(ListenerType.OwnerControlRecord);
  };

  // called by subscribers to listen to changes to votingTasks
  public subscribeToVotingTasks = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.VotingTasks, listener);
  };
  // called by subscribers to listen to changes to votingTasks
  public unsubscribeToVotingTasks = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.VotingTasks, listener);
  };

  // called by subscribers to listen to changes to pillories
  public subscribeToPillory = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.Pillories, listener);
  };
  // called by subscribers to listen to changes to pillories
  public unsubscribeToPillory = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.Pillories, listener);
  };

  // called by subscribers to listen to changes to proof uploads
  public subscribeToProofUploads = (listener: () => void): void => {
    void this.proofRequests; // ensure DB subscription is active before listening
    this.subscriptionManager.subscribe(ListenerType.ProofUploads, listener);
  };
  // called by subscribers to listen to changes to proof uploads
  public unsubscribeToProofUploads = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.ProofUploads, listener);
  };

  public subscribeToWeeklyTargetProgress = (listener: () => void): void => {
    void this.weeklyTargetProgress;
    this.subscriptionManager.subscribe(ListenerType.WeeklyTargetProgress, listener);
  };

  public unsubscribeToWeeklyTargetProgress = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.WeeklyTargetProgress, listener);
  };

  // called by subscribers to listen to changes to task locks
  public subscribeToTaskLocks = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.TaskLocks, listener);
  };
  // called by subscribers to listen to changes to task locks
  public unsubscribeToTaskLocks = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.TaskLocks, listener);
  };

  // called by subscribers to listen to changes to audit
  public subscribeToAudit = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.AuditChanged, listener);
  };
  // called by subscribers to listen to changes to audit
  public unsubscribeToAudit = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.AuditChanged, listener);
  };

  public subscribeToRequestAttendance = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.RequestAttendance, listener);
  };

  public unsubscribeToRequestAttendance = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.RequestAttendance, listener);
  };

  public subscribeToMemberMessages = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.MemberMessages, listener);
  };

  public unsubscribeToMemberMessages = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.MemberMessages, listener);
  };

  public subscribeToSentMessages = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.SentMessages, listener);
  };

  public unsubscribeToSentMessages = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.SentMessages, listener);
  };

  public subscribeToOwnedItems = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.OwnedItems, listener);
  };

  public unsubscribeToOwnedItems = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.OwnedItems, listener);
  };

  public subscribeToOwnerProfiles = (listener: () => void): void => {
    this.subscriptionManager.subscribe(ListenerType.OwnerProfiles, listener);
  };

  public unsubscribeToOwnerProfiles = (listener: () => void): void => {
    this.subscriptionManager.unsubscribe(ListenerType.OwnerProfiles, listener);
  };

  private systemAuditLogsChangedCallback = (snapshot: unknown): void => {
    const auditPackage = snapshot;
    if (auditPackage) {
      this.systemAuditLogsCache = auditPackage as Record<
        string,
        IAuditLogsDateCollection
      >;
    }
    this.subscriptionManager.fireListenersSync(ListenerType.SystemAuditLog, this.systemAuditLogsCache || {});
  };

  private auditLogsChangedCallback = (snapshot: unknown): void => {
    const auditPackage = snapshot;
    if (auditPackage) {
      this.auditLogsCache = auditPackage as IAuditLogsDateCollection;
      Object.keys(this.auditLogsCache).forEach((date) => {
        if (!this.auditLogsCache) return;
        const recordSet = this.auditLogsCache[date];
        Object.keys(recordSet).forEach((itemKey) => {
          const item = recordSet[itemKey];
          recordSet[itemKey] = decrypt(
            item as unknown as string,
            {}
          ) as AuditMessage;
        });
      });

      this.subscriptionManager.fireListenersSync(ListenerType.AuditChanged);
    }
  };

  private auditBankChangedCallback = (snapshot: unknown): void => {
    const auditPackage = snapshot;

    if (auditPackage) {
      this.auditBankCache = auditPackage as IAuditBankDateCollection;

      const bank = this.auditBank.bank as unknown as IAuditBankDateCollection;
      if (bank) {
        Object.keys(bank).forEach((date) =>
          Object.keys(bank[date]).forEach((itemKey) => {
            const item = bank[date][itemKey];
            item.reason = decrypt(item.reason, "") as string;
          })
        );
      }

      this.subscriptionManager.fireListenersSync(ListenerType.AuditChanged);
    }
  };
  private auditScoresChangedCallback = (snapshot: unknown): void => {
    const auditPackage = snapshot;

    if (auditPackage) {
      this.auditScoresCache = auditPackage as IAuditScoreHistory[];
      this.subscriptionManager.fireListenersSync(ListenerType.AuditChanged);
    }
  };

  private weeklyTargetProgressChangedCallback = (
    snapshot: Record<string, Record<string, IWeeklyTargetProgress>>
  ): void => {
    this.weeklyTargetProgressCache = snapshot || {};
    this.subscriptionManager.fireListenersSync(
      ListenerType.WeeklyTargetProgress
    );
  };

  private attendanceRequestCallback = (snapshot: IAttendanceRequest) => {
    this.requestAttendanceCache = snapshot;
    this.subscriptionManager.fireListenersSync(ListenerType.RequestAttendance);
  };

  private allAttendanceRequestCallback = (
    snapshot: Record<string, IAttendanceRequest>
  ) => {
    this.allAttendanceRequestsCache = snapshot;
    this.subscriptionManager.fireListenersSync(ListenerType.RequestAttendance);
  };

  private onPilloryChanged = (vals: Record<string, string>): void => {
    if (!vals) return;
    const existingPilloriesForUser = this.pilloriesCache?.filter(
      (p) => p.userid === this.getControl().userid
    );
    this.pilloriesCache = Object.values(vals as Record<string, string>).map(
      (c) => new PilloryTopic(decrypt<IPilloryTopic>(c, {} as IPilloryTopic))
    );

    if (existingPilloriesForUser && existingPilloriesForUser.length > 0) {
      const newPilloriesForUser = this.pilloriesCache?.filter(
        (p) => p.userid === this.getControl().userid
      );
      const delta = newPilloriesForUser.filter((n) => {
        const match = existingPilloriesForUser?.find((e) => e.id === n.id);
        if (!match) return true;
        return match.currentTotal !== n.currentTotal;
      });
      if (delta.length > 0) {
        delta.forEach((d) => {
          this.showMessage({
            social: true,
            message: `Your pillory ${d.name} has been updated by ${d.votedMembers.length > 0
              ? this.getAllControlRecords()?.find(
                (u) =>
                  u.userid == d.votedMembers[d.votedMembers.length - 1].user
              )?.username
              : d.ownername
              }`,
            notificationType: NotificationType.PilloryInfo,
          });
        });
        //todo: this needs to add to the preview and not regenerate stuff already resolved
        this.handlePilloryPreviewItems();
      }
    }
    this.subscriptionManager.fireListenersSync(ListenerType.Pillories);
  };

  private onProofRequestsChanged = (snapshot: unknown): void => {
    if (!snapshot) return;
    this.proofRequestsCache = snapshot as Record<string, unknown>;
    this.subscriptionManager.fireListenersSync(ListenerType.ProofUploads);
  };

  private onTaskLocksChanged = (snapshot: unknown, userid: string): void => {
    if (!this.taskLocksCache) this.taskLocksCache = {};
    if (!snapshot) {
      // All locks for this user were deleted — clear the cache entry so the UI
      // removes stale receipts rather than leaving them stranded.
      this.taskLocksCache[userid] = {};
      this.subscriptionManager.fireListenersSync(ListenerType.TaskLocks);
      return;
    }
    const userLocks = snapshot as Record<string, ILockReceipt>;
    this.taskLocksCache[userid] = userLocks;
    this.subscriptionManager.fireListenersSync(ListenerType.TaskLocks);
  };

  /**
   * called when we initially snapshot all voting tasks on start up
   * @param newTasks all votes avaiable in the system
   */
  private onVotingTasks = (task: string): void => {
    if (!task) return;
    if (!this.votingTasksCache) this.votingTasksCache = [];
    const existingVotesForUser = this.votingTasksCache.filter(
      (p) => p.userid === this.getControl().userid
    );
    const voteTask = new VoteTask(decrypt<IVoteTask>(task, {} as IVoteTask));
    const idx = this.votingTasksCache.findIndex((t) => t.id === voteTask.id);
    if (idx > -1) this.votingTasksCache[idx] = voteTask;
    else this.votingTasksCache.push(voteTask);

    if (existingVotesForUser && existingVotesForUser.length > 0) {
      const newVotesForUser = this.votingTasksCache.filter(
        (p) => p.userid === this.getControl().userid
      );
      const delta = newVotesForUser.filter((n) => {
        const match = existingVotesForUser?.find((e) => e.id === n.id);
        if (!match) return true;
        return match.durationMinutes !== n.durationMinutes;
      });
      if (delta.length > 0) {
        delta.forEach((d) => {
          if (
            Object.keys(d.votedMembers).length === 0 ||
            d.votedMembers[d.today] === undefined
          )
            return;
          const user =
            d.votedMembers[d.today][d.votedMembers[d.today].length - 1].user;
          const voteUser = this.getAllControlRecords()?.find(
            (u) => u.userid === user
          );
          if (!voteUser) return;
          this.showMessage({
            social: true,
            message: `Your running Task ${d.name} has been updated by ${voteUser.username}`,
            notificationType: NotificationType.VotedItem,
          });
        });
      }
    }

    this.subscriptionManager.fireListenersSync(ListenerType.VotingTasks);
  };

  private onPendingStateChanged = (snapshot: unknown): void => {
    this.pendingState = decrypt(snapshot as string, []);
    this.fireListeners(); // means view get updated with the changes too
    // Allow the scheduler to react immediately without waiting for the next minute tick
    this.onPendingStateReadyCallback?.();
  };

  private onStateChanged = (snapshot: unknown): void => {
    if (this.isStateTransaction()) {
      return;
    }
    const state = snapshot as Record<string, string>;
    this.state = state || {};
    this.taskSchedule = this.getState<ITaskBasketInfo>(STATE_KEY_CREDITS, {});
    this.eventSchedule = this.getState<ISchedulerTimerInfo[]>(
      STATE_KEY_SCHEDULER,
      []
    );
    this.permissionSchedule = this.getState<IPermissionBasketInfo>(
      STATE_KEY_PERMISSIONS,
      {}
    );
    this.awaitingState = false;
    loadPerf(`StatefulSettings.onStateChanged (awaitingState cleared)`, { stateKeys: Object.keys(this.state ?? {}).length });
    void this.setLoaded(false);
    this.fireListeners(); // means view get updated with the changes too
  };

  private onMemberMessagesChanged = (snapshot: unknown): void => {
    this.memberMessagesCache = [];
    const messages = snapshot as Record<string, IMemberMessage>;
    if (messages) {
      this.memberMessagesCache = Object.keys(messages).map((key) => {
        const encryptedMessage = { ...messages[key] };
        encryptedMessage.message = decrypt(
          encryptedMessage.message as unknown as string,
          {}
        ) as MessageContents;
        if (typeof encryptedMessage.message === "string")
          encryptedMessage.message = JSON.parse(encryptedMessage.message);
        encryptedMessage.key = key;
        encryptedMessage.sent = new Date(encryptedMessage.sent);
        return encryptedMessage;
      });
    }
    this.subscriptionManager.fireListenersSync(ListenerType.MemberMessages, this.memberMessages);
    this.fireListeners(); // means view get updated with the changes too
  };

  private onMemberMessageNotifications = (
    newMessages: Record<string, IMemberMessage>
  ): void => {
    if (newMessages) {
      let added = false;
      Object.keys(newMessages).forEach((key) => {
        const encryptedMessage = newMessages[key];
        encryptedMessage.message = decrypt(
          encryptedMessage.message as unknown as string,
          {}
        ) as MessageContents;
        if (typeof encryptedMessage.message === "string")
          encryptedMessage.message = JSON.parse(encryptedMessage.message);
        // there is a chance that this message doesn't exist so we can just ignore it if the message and subject are both empty
        if (
          !encryptedMessage.message ||
          (!encryptedMessage.message.subject &&
            !encryptedMessage.message.message)
        ) {
          return; // we will ignore this one as it's an empty shell and probably an update to 'read' 'keep' for something that doesn't exist
        }
        encryptedMessage.key = key;
        encryptedMessage.sent = new Date(encryptedMessage.sent);
        if (this.memberMessagesCache?.findIndex((m) => m.key === key) === -1) {
          added = true;
          this.memberMessagesCache.push(encryptedMessage);
        }
      });
      if (added) {
        this.subscriptionManager.fireListenersSync(ListenerType.MemberMessages, this.memberMessages);
        this.fireListeners(); // means view get updated with the changes too
      }
    }
  };

  private onLovenseToysChanged = (snapshot: unknown): void => {
    const toys = snapshot as Record<string, LovenseToy>;
    if (!toys) return;
    const previousCount = this.lovenseToysCache?.length ?? 0;
    this.lovenseToysCache = Object.values(toys);
    if (previousCount > 0 && previousCount !== this.lovenseToysCache.length) {
      Emitter.emitNotification({
        message: "New Lovense Toys are available",
        notificationType: NotificationType.SettingsInfo,
      });
    }
    this.fireListeners(); // means view get updated with the changes too
  };

  private onSentMessagesChanged = (snapshot: unknown): void => {
    this.sentMessagesCache = [];
    const messages = snapshot as Record<string, IMemberSentReceipt>;
    if (messages) {
      this.sentMessagesCache = Object.keys(messages).map((key) => {
        const encryptedMessage = { ...messages[key] };
        encryptedMessage.sent = new Date(encryptedMessage.sent);
        encryptedMessage.key = key;
        encryptedMessage.subject = decrypt(
          encryptedMessage.subject,
          encryptedMessage.subject
        );
        encryptedMessage.message = decrypt(
          encryptedMessage.message,
          encryptedMessage.message
        );
        return encryptedMessage;
      });
    }
    this.subscriptionManager.fireListenersSync(ListenerType.SentMessages, this.sentMessages);
    this.fireListeners(); // means view get updated with the changes too
  };

  private ownedItemsChangedCallback = (snapshot: unknown): void => {
    const encryptedOwnedItems = snapshot as EncryptedOwnedItemsCollection;
    this.ownedItemsCache = {};
    if (encryptedOwnedItems) {
      Object.keys(encryptedOwnedItems).forEach((k) => {
        this.ownedItems[k] = [];
        if (encryptedOwnedItems[k])
          this.ownedItems[k] = decrypt(encryptedOwnedItems[k], []);
      });
    }
    this.awaitingOwnedItems = false;
    loadPerf(`StatefulSettings.ownedItemsChanged (awaitingOwnedItems cleared)`, { itemKeys: Object.keys(this.ownedItemsCache ?? {}).length });
    this.setLoaded(false);
    this.subscriptionManager.fireListenersSync(ListenerType.OwnedItems, this.ownedItems);
  };

  private ownerProfilesChangedCallback = (snapshot: unknown): void => {
    const ownerProfiles = snapshot as Record<string, IOwnerProfileSession>;
    if (ownerProfiles) {
      Object.keys(ownerProfiles).forEach((tk) => {
        const profile = decrypt(
          ownerProfiles[tk] as unknown as string,
          {}
        ) as IOwnerProfileSession;
        const idx = this.ownerProfilesCache.findIndex(p => p.userid === profile.userid);
        if (idx > -1) this.ownerProfilesCache[idx] = profile;
        else this.ownerProfilesCache.push(profile);
      });
    }
    this.subscriptionManager.fireListenersSync(ListenerType.OwnerProfiles, this.ownerProfilesCache);
    this.awaitingOwnerProfiles = false;
    loadPerf(`StatefulSettings.ownerProfilesChanged (awaitingOwnerProfiles cleared)`, { profileCount: this.ownerProfilesCache.length });
    void this.setLoaded(false);
  };

  // when a user is set up then we listen to all saved collections to keep things current. this means if shares owned or settings are changed then we can update accordingly.
  protected initialise = async (userid: string): Promise<void> => {
    loadPerf(`StatefulSettings.initialise start`);
    super.initialise(userid);

    if (this.isLoggedOnUserSession()) {
      const state = await this.database.snapState(userid);
      this.onStateChanged(state);
    } else {
      this.unsubscribeState = this.database.snapAndSubState(
        userid,
        this.onStateChanged
      );
    }
    this.unsubscribeIntegrity = this.database.snapAndSubSessionIntegrity(
      userid,
      this.onSessionIntegrityIdUpdated
    );
    this.unsubscribePendingState = this.database.snapAndSubPendingState(
      userid,
      this.onPendingStateChanged
    );
    this.unsubscribeOwnedItems = this.database.snapAndSubOwnedItems(
      userid,
      this.ownedItemsChangedCallback
    );
    this.unsubscribeOwnerProfiles = this.database.snapAndSubOwnerProfiles(
      userid,
      this.ownerProfilesChangedCallback
    );
    this.caches.forEach((cache) => cache.get());

    // Run heavy custom schedule migration/summaries in the background so startup
    // readiness is not blocked by large legacy detail trees.
    void this.initialiseCustomScheduleSummaries(userid);

    // Pre-fetch detail for any custom schedules already referenced in settings
    // (fires again from onSettingsChanged once settings subscription delivers data)
    this.prefetchScheduledCustomScheduleDetails();
  };

  private initialiseCustomScheduleSummaries = async (userid: string): Promise<void> => {
    // Migration: generate lightweight summaries if this is the first run after the split.
    const migrationResults = await Promise.allSettled([
      this.database.migrateGameSummaries(),
      // Runtime startup should not block or inflate payloads with detail backfill.
      // Metadata backfill can be handled by dedicated migration runs.
      this.database.migrateCustomScheduleSummaries(userid, {
        includeMetadataBackfill: false,
      }),
    ]);
    migrationResults.forEach((result, index) => {
      if (result.status === "rejected") {
        console.warn(
          `StatefulSettings initialise migration ${index} failed for ${userid}`,
          result.reason
        );
      }
    });

    try {
      const summaries = await this.database.snapshotCustomScheduleSummaries(
        userid
      );
      this.customScheduleCache.onUpdate(summaries);
    } catch (error) {
      console.warn(
        `StatefulSettings could not load custom schedule summaries for ${userid}`,
        error
      );
      this.customScheduleCache.onUpdate({});
    }
  };

  private lastRemoteSessionId: string | undefined;
  private onSessionIntegrityIdUpdated = (id: string) => {
    if (
      this.lastRemoteSessionId &&
      this.lastRemoteSessionId !== id &&
      this.isLoggedOnUserSession()
    ) {
      // we have logged an id before and it's on the one we know
      // error saving state - this means the session id passed in does not match the session id on the state, it means the state was updated by something else and
      // our local state is not the latest any more, so we need to fetch the latest again from the database and lock the state to our session to then be able to update it further
      this.auditService.auditUserAction(
        "User state is updated by other session, refreshing state from db. Session: " +
        this.sessionId,
        AuditActionTypes.Session
      );
      // end this scheduler
      this.showMessage({
        message:
          "Another session has started on a device. Refresh the browser to restart session",
        persist: true,
        error: true,
        fatal: true,
        notificationType: NotificationType.ErrorInfo,
      });
      this.dispose();
      window.location.replace("forceSignIn");
      return;
    }
    this.lastRemoteSessionId = id; // now we have logged the last id,
  };

  public getPendingState = (): IPendingItem[] => {
    if (!this.pendingState) return [];
    const vals = this.pendingState;
    if (Array.isArray(vals)) return vals;
    return [];
  };

  /** Registers a callback that fires immediately whenever the pending state changes in Firebase. */
  public setPendingStateReadyCallback(cb: (() => void) | undefined): void {
    this.onPendingStateReadyCallback = cb;
  }

  // returns the state for tasks, credits, positions and events
  public getState<T>(key: string, defaultVal: T): T {
    if (!this.state) return defaultVal;
    return decrypt(this.state[key], defaultVal) as T;
  }

  // from member, resets ownership and everything. user becomes unowned and settings go back to standard.
  public resetSession = (): void => {
    if (this.isSystemLoggedOn()) return;
    this.auditService.auditUserAction(
      "User reset session requested",
      AuditActionTypes.Session
    );
    const sysSettings = this.getSystemSettings();
    if (this.getControl().isOwned())
      void this.database.removeOwnership(this.controlRecord);
    void this.database.updateSettings(
      this.controlRecord.userid,
      stripSettings(
        sysSettings,
        undefined,
        this.systemSettings,
        this.isSystemLoggedOn(),
        this.controlRecord.userid,
        this.database
      ),
      true
    );
    this.settings = cloneObject(sysSettings) as ISessionSettings;
    this.onSettingsChanged(this.settings);
    Object.keys(this.state).forEach((key) => {
      if (key !== STATE_KEY_SESSION_ID) this.updateState(key, undefined);
    });
    this.showMessage({
      message: "Your session has been reset, log on again it complete",
      persist: true,
      error: true,
      fatal: true,
      notificationType: NotificationType.ErrorInfo,
    });
  };

  public getProofRequest = (): IProofRequest | undefined => {
    return this.getState<IProofRequest | undefined>(
      STATE_KEY_PROOF_REQUEST_CAPTURE,
      undefined
    );
  };

  public getLinesRequest = (): ILinesRequest | undefined => {
    return this.getState<ILinesRequest | undefined>(STATE_KEY_LINES, undefined);
  };

  public getRouletteRequest = (): IRouletteRequest | undefined => {
    return this.getState<IRouletteRequest | undefined>(
      STATE_KEY_ROULETTE,
      undefined
    );
  };

  public getRequestLockState = (): IRequestLockState | undefined => {
    const state = this.getState<IRequestLockState | undefined>(
      STATE_KEY_REQUEST_LOCK,
      undefined
    );
    // Guard: only return lock state for the logged-in user's own receipt.
    // Stale data written before the per-user guard was in place can leave a
    // member's receipt in the owner's persisted state — clear it now.
    if (state?.result && state.result.userid !== this.controlRecord.userid) {
      this.updateRequestLockDialog(false);
      return undefined;
    }
    return state;
  };

  public getDailyReportRequest = (): IReportRequest | undefined => {
    return this.getState<IReportRequest | undefined>(
      STATE_KEY_DAILYREPORT,
      undefined
    );
  };

  public getMorningReportRequest = (): IReportRequest | undefined => {
    return this.getState<IReportRequest | undefined>(
      STATE_KEY_MORNINGREPORT,
      undefined
    );
  };

  public isCheckoutAllowed = (): boolean => {
    if (!this.isLoaded()) return false;
    const bank = this.getBankBalance();
    const checkoutCost = this.getCheckoutCost();
    const checkoutPermission = this.getCheckoutPermission();
    const basket = this.getBasketItems(true);
    return (
      bank >= checkoutCost &&
      !basket.permissions.find((p) => p.key === checkoutPermission?.key) &&
      !basket.pendingItems.find((p) => p.key === checkoutPermission?.key)
    );
  };

  // returns how close to the check out amount the user is by looking at their settings and state
  public getCheckoutPercent = (): number => {
    const bank = this.getBankBalance();
    let percent = bank / this.getCheckoutCost();
    if (percent > 1) percent = 1;
    else if (percent < 0) percent = 0;
    return isNaN(percent) ? 0 : percent * 100;
  };

  public getCheckoutCost = (): number => {
    const event = this.getCheckoutPermission();
    return event && event.requiredCredit ? event.requiredCredit : 0;
  };

  public requestOwnerPermission = (permission: IPermissionItem): void => {
    if (permission.requiredCredit) {
      void this.decrementBank(
        permission.requiredCredit,
        `${permission.name} Reward Request`
      );
    }
    sendMessageToOwner(
      MessageType.PermissionRequest,
      this,
      `${this.controlRecord.username} has requested that they spend ${permission.requiredCredit} on the reward ${permission.name}. You have the ability to approve or deny this request. If approved then you will get to choose the options how this reward will be applied. They have already been debited by the amount for asking the question.`,
      `${this.controlRecord.username} ${permission.name} Request`
    );
  };

  // removes the amount specified regardless for the user
  public decrementBank = (amount: number, reason: string): void => {
    const decrementAmount = Math.abs(amount) * -1;
    this.auditBankChange(reason, decrementAmount);
    void this.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "Bank Change",
        `Bank Decrement by ${decrementAmount}. ${reason}`,
        TraceLevel.info,
        false,
        decrementAmount
      )
    );
  };

  public getReward = (amount: number): number => {
    const creditSettings = this.getTaskSettings();
    const lowerReward = (1 - creditSettings.creditVariance) * amount;
    const reward = getDecimalRandom(lowerReward, amount, 0.25);
    return reward;
  };

  // updates the bank and sets the value up to the amount passed in, with random nature determined by the variance setting
  public incrementBank = (reward: number, reason: string): number => {
    const incrementAmount = Math.abs(reward);
    const newBank = this.auditBankChange(reason, incrementAmount);
    void this.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "Bank Change",
        `Bank Increment by ${incrementAmount} to ${newBank}. ${reason}`,
        TraceLevel.info,
        true,
        reward
      )
    );
    return reward;
  };

  public getCheckoutPermission = (): IPermissionItem | undefined => {
    if (!this.isLoaded() || !this.settings || !this.mergedSettings)
      return undefined;

    const permissionSettings = this.getPermissionSettings();
    if (!permissionSettings) return undefined;
    const allPermissions = this.getPermissions(
      (permission) => permission.eventType === EventType.checkout
    );
    let permissions: IPermissionItem[] = [];
    if (allPermissions.length > 1)
      permissions = allPermissions.filter((e) => isAvailableItem(e));
    if (permissions.length === 0) permissions = allPermissions;
    let checkoutPermission = permissions[0];
    if (permissions.length > 1)
      permissions = permissions.filter((e) => isAvailableItem(e));
    if (!checkoutPermission) {
      const now = Date.now();
      // if we just created one recently, reuse it instead of creating/writing again on
      // every call - protects against a tight create+write loop if the new item hasn't
      // yet been observed by getPermissions on a subsequent call (e.g. merge/reload race).
      if (
        this.pendingCheckoutPermission &&
        now - this.checkoutPermissionCreatedAt <
        StatefulSettings.CHECKOUT_PERMISSION_CREATION_COOLDOWN_MS
      ) {
        return this.pendingCheckoutPermission;
      }
      checkoutPermission = new PermissionItem({
        img: "",
        imgId: "",
        description: "",
        eventType: EventType.checkout,
        name: "Request Orgasm",
        approvalChance: 0.5,
        requiredCredit: 10,
        count: 10,
        countType: CountType.minutes,
        controlTypes: [ControlType.maximum],
        availability: AvailabilityType.daily,
        options: [],
        hidden: false,
        key: uuid(),
        itemType: ItemTypes.permission,
        isLimited: true,
        disableAdd: false,
        optionAppliedChance: 1,
        hasOptions: false,
        availabilityDayType: AvailabilityDayType.AllDays,
      });
      permissions.push(checkoutPermission);
      if (!this.settings.permissionSettings) {
        const effectivePermissionSettings = this.getPermissionSettings();
        this.settings.permissionSettings = {
          disablePermissions:
            effectivePermissionSettings?.disablePermissions ?? false,
          permissionDurationVariance:
            effectivePermissionSettings?.permissionDurationVariance ?? 0,
          permissions: [],
        } as IPermissionSettings;
      }
      this.settings.permissionSettings.permissions.push(checkoutPermission);
      this.pendingCheckoutPermission = checkoutPermission;
      this.checkoutPermissionCreatedAt = now;
      void this.updateSettings(this.settings, true).then(() =>
        this.auditService.auditSettingsItem(
          checkoutPermission,
          undefined,
          "Checkout Permission Item Created"
        )
      );
    }
    return checkoutPermission;
  };

  public getProgressStatus = () => {
    let status = "";
    const permission = this.getCheckoutPermission();
    const auditScores = this.getAuditScores();
    if (auditScores && auditScores.length > 0) {
      const latestScore = auditScores[auditScores.length - 1].bank;
      const oldestScore = auditScores[0].bank;
      if (this.isCheckoutAllowed()) {
        status = `${permission?.name} available`;
      } else if (oldestScore >= latestScore)
        status = `Not making progress to ${permission?.name}`;
      else {
        const cost = this.getCheckoutCost();
        const weeklyIncrement = latestScore - oldestScore;
        const requiredGain = cost - latestScore;
        const weeksRequired = requiredGain / weeklyIncrement;
        const daysRequired = weeksRequired * 7;
        status = `Expected ${permission?.name} Date: ${format(
          addDays(new Date(), Math.round(daysRequired)),
          "dd-MM-yyyy"
        )}`;
      }
    }
    return status;
  };

  public scheduleItemAvailable = (item: IScheduledItem): boolean => {
    const isWeekendDay = this.getIsWeekend();
    if (!this.canPerformSomeTasksForToday()) return false;

    switch (item.availability) {
      case AvailabilityType.daily:
        return true;
      case AvailabilityType.weekends:
        return isWeekendDay;
      case AvailabilityType.weekdays:
        return !isWeekendDay;
      case AvailabilityType.random:
        return flipCoin();
      case AvailabilityType.specific: {
        const day = convertToEventDayFromDate(new Date().getDay());
        if (item.specificDays) {
          return item.specificDays.findIndex((d) => d === day) > -1;
        }
        return false;
      }
      default:
        return true;
    }
  };

  public getBasketItems = (includePending: boolean): IBasket => {
    // returns a populated task list based on the settings and state of the user
    const basket: IBasket = {
      tasks: [],
      positions: [],
      penalties: [],
      permissions: [],
      pendingItems: [],
    };

    const applyLatestItemSnapshot = <T extends BaseBasketItem>(
      basketItem: T,
      latestItem?: ISettingsItem
    ): T => {
      if (!latestItem) return basketItem;
      basketItem.name = latestItem.name ?? basketItem.name;
      basketItem.description = latestItem.description ?? basketItem.description;
      basketItem.hidden = latestItem.hidden;
      basketItem.img = latestItem.img;
      basketItem.imgId = latestItem.imgId;

      if ('categories' in basketItem) {
        (basketItem as any).categories = cloneObject((latestItem as any).categories ?? []);
      }
      if ('options' in basketItem) {
        (basketItem as any).options = cloneObject((latestItem as any).options);
      }
      if ('optionImages' in basketItem) {
        (basketItem as any).optionImages = cloneObject((latestItem as any).optionImages);
      }
      return basketItem;
    };

    const pendingItems: BaseBasketItem[] = [];
    const positionInfo = this.getState<IPositionBasketInfo>(
      STATE_KEY_POSITIONS,
      {} as IPositionBasketInfo
    );
    const metronomeInfo = this.getState<IPositionBasketInfo>(
      STATE_KEY_METRONOME,
      {} as IPositionBasketInfo
    );
    const siteEffectsInfo = this.getState<IPositionBasketInfo>(
      STATE_KEY_SITE_EFFECTS,
      {} as IPositionBasketInfo
    );

    Object.keys(this.taskSchedule).forEach((creditKey) => {
      const timer = this.taskSchedule[creditKey];
      const item: ISettingsItem = timer.isPenalty
        ? (this.getPenalty(timer.key) as ISettingsItem)
        : (this.getTask(timer.key) as ISettingsItem);
      if (timer.isPenalty && (!item || isAvailableItem(item))) {
        const basketItem = applyLatestItemSnapshot(
          new PenaltyBasketItem(timer as IPenaltyTimerInfo),
          item
        );
        if (timer.isPaused) pendingItems.push(basketItem);
        else basket.penalties.push(basketItem);
      } else if (!item || isAvailableItem(item)) {
        const basketItem = applyLatestItemSnapshot(
          new TaskBasketItem(timer as ITaskTimerInfo),
          item
        );
        if (timer.isPaused) pendingItems.push(basketItem);
        else basket.tasks.push(basketItem);
      }
    });

    const timedBasketItemSorter = (
      a: BaseTimedBasketItem,
      b: BaseTimedBasketItem
    ) => {
      if (a.itemType === ItemTypes.task && b.itemType === ItemTypes.task) {
        const credA = a as TaskBasketItem;
        const credB = b as TaskBasketItem;
        if (credA.permanent && !credB.permanent) return -1;
        if (!credA.permanent && credB.permanent) return 1;
      }
      if (a.isComplete && !b.isComplete) return 1;
      if (!a.isComplete && b.isComplete) return -1;
      if (b.isComplete && a.isComplete)
        return a.name?.toLowerCase().localeCompare(b.name?.toLowerCase());
      if (a.timerTicks && b.timerTicks && a.timerTicks > b.timerTicks)
        return -1;
      if (a.timerTicks && b.timerTicks && a.timerTicks < b.timerTicks) return 1;
      return 0;
    };

    basket.tasks.sort(timedBasketItemSorter);
    basket.penalties.sort(timedBasketItemSorter);

    this.eventSchedule.forEach((schedule) => {
      if (
        schedule.itemType !== ItemTypes.event &&
        schedule.itemType !== ItemTypes.task &&
        schedule.itemType !== ItemTypes.permission
      )
        return;
      if (
        schedule.itemType === ItemTypes.event &&
        (!schedule.scheduleOptions ||
          !schedule.scheduleOptions.isCustomSchedule) &&
        !isAvailableItem(this.getEvent(schedule.key) as ISettingsItem)
      )
        return;
      if (
        schedule.itemType === ItemTypes.permission &&
        !isAvailableItem(this.getPermission(schedule.key) as ISettingsItem)
      )
        return;
      else if (schedule.itemType === ItemTypes.task) {
        const task = this.getTask(schedule.key);
        if (
          !task ||
          !isAvailableItem(task) ||
          task.creditTaskType === TaskType.acknowledgement
        )
          return;
      }
      const currentScheduleItem = schedule.itemType === ItemTypes.event
        ? this.getEvent(schedule.key)
        : schedule.itemType === ItemTypes.permission
          ? this.getPermission(schedule.key)
          : this.getTask(schedule.key);
      const basketItem = applyLatestItemSnapshot(
        new ScheduleBasketItem(schedule),
        currentScheduleItem as ISettingsItem | undefined
      );
      pendingItems.push(basketItem);
    });

    Object.keys(this.permissionSchedule).forEach((key) => {
      const permission = this.permissionSchedule[key];
      const currentPermission = this.getPermission(permission.key) as ISettingsItem;
      if (!isAvailableItem(currentPermission))
        return;
      const basketItem = applyLatestItemSnapshot(
        new PermissionBasketItem(permission),
        currentPermission
      );
      basket.permissions.push(basketItem);
    });
    basket.permissions.sort((a, b) =>
      a.decision === b.decision ? 0 : a.decision ? -1 : 1
    );

    const addPositionLikeItem = (info: IPositionBasketInfo) => {
      if (!info || (!info.hasPosition && !info.hasMetronome && !info.hasSiteEffects)) return;
      const isMetronome = info.hasMetronome === true;
      const isSiteEffects = info.hasSiteEffects === true;
      if (!isMetronome && !isSiteEffects && !isAvailableItem(this.getPosition(info.key) as ISettingsItem)) {
        return;
      }
      const timerTicks = info.endTime
        ? Math.max(0, differenceInSeconds(new Date(info.endTime), new Date()))
        : parseInt(this.cookies.getState(COOKIE_KEY_POSITION_TICKS));
      const safeTimerTicks = isNaN(timerTicks) ? 0 : timerTicks;
      const basketItem = new PositionBasketItem(info, safeTimerTicks);
      if (!basketItem.isComplete && basketItem.isPaused && !safeTimerTicks) {
        pendingItems.push(basketItem);
      } else {
        basket.positions.push(basketItem);
      }
    };

    addPositionLikeItem(positionInfo);
    addPositionLikeItem(metronomeInfo);
    addPositionLikeItem(siteEffectsInfo);
    basket.pendingItems = pendingItems;
    if (includePending) {
      // HANDLING FOR AN OWNER LOOKING AT THE PENDING ITEMS FOR A MEMBER SHOULD SHOW PENDING ITEMS READY TO APPLY
      this.peekPendingBasketItems(endOfToday(), basket);
      const pendingSorter = (a: ISettingsItem, b: ISettingsItem) => {
        if (
          (a.itemType === ItemTypes.event ||
            a.itemType === ItemTypes.permission) &&
          b.itemType !== ItemTypes.event &&
          b.itemType !== ItemTypes.permission
        )
          return -1;
        if (
          a.itemType !== ItemTypes.event &&
          a.itemType !== ItemTypes.permission &&
          (b.itemType === ItemTypes.event ||
            b.itemType === ItemTypes.permission)
        )
          return 1;
        return a.name?.toLowerCase().localeCompare(b.name?.toLowerCase());
      };
      basket.pendingItems.sort(pendingSorter);
    }
    return basket;
  };

  private peekPendingBasketItems = (basketDate: Date, basket: IBasket) => {
    if (this.canPerformSomeTasksForDate(basketDate)) {
      const ownerPendingItems = this.getPendingState();
      const toProcess = ownerPendingItems.filter(
        (item) => !item.date || isBefore(new Date(item.date), basketDate)
      );
      toProcess.forEach((pendingItem) => {
        if (pendingItem.item)
          pendingItem.item = this.getCurrentItem(pendingItem.item) ?? pendingItem.item;
        if (
          pendingItem.date &&
          differenceInDays(new Date(pendingItem.date), basketDate) !== 0
        )
          return;
        if (pendingItem.item && pendingItem.item.itemType === ItemTypes.task) {
          const idx = basket.pendingItems.findIndex((i) =>
            i.key?.startsWith(pendingItem.item?.key as string)
          );
          if (pendingItem.updateType === PendingUpdateType.add && idx === -1) {
            // Mirror scheduler add behavior in preview: replacing an existing
            // completed/paused/running task with the newly queued one.
            for (let i = basket.tasks.length - 1; i >= 0; i -= 1) {
              if (basket.tasks[i].key?.startsWith(pendingItem.item?.key as string)) {
                basket.tasks.splice(i, 1);
              }
            }
            this.createBasketItems(
              this.getCurrentItem(pendingItem.item) as ISettingsItem,
              () =>
                isWeekendDay(
                  convertToEventDayFromDate(basketDate.getDay()),
                  this.getWeekendDays()
                ),
              "This task has been added by the owner to the member schedule.",
              basketDate,
              { ...pendingItem.itemAdditionOptions, isPeek: false },
              undefined,
              false
            ).forEach((i) => {
              if (pendingItem.date) {
                if (isAfter(new Date(pendingItem.date), new Date())) {
                  i.startTime = new Date(pendingItem.date);
                }
              }
              basket.pendingItems.push(i);
            });
          } else if (
            pendingItem.updateType === PendingUpdateType.remove &&
            idx !== -1
          ) {
            basket.pendingItems.splice(idx, 1);
          }
        } else if (
          pendingItem.item &&
          pendingItem.item.itemType === ItemTypes.penalty
        ) {
          const idx = basket.pendingItems.findIndex((i) =>
            i.key?.startsWith(pendingItem.item?.key as string)
          );
          if (pendingItem.updateType === PendingUpdateType.add && idx === -1) {
            // Mirror scheduler add behavior in preview: replacing an existing
            // completed/paused/running penalty with the newly queued one.
            for (let i = basket.penalties.length - 1; i >= 0; i -= 1) {
              if (basket.penalties[i].key?.startsWith(pendingItem.item?.key as string)) {
                basket.penalties.splice(i, 1);
              }
            }
            this.createBasketItems(
              this.getCurrentItem(pendingItem.item) as IPenaltyItem,
              () =>
                isWeekendDay(
                  convertToEventDayFromDate(basketDate.getDay()),
                  this.getWeekendDays()
                ),
              "This penalty has been added by the owner to the member schedule.",
              basketDate,
              { ...pendingItem.itemAdditionOptions, isPeek: true },
              undefined,
              true
            ).forEach((i: TaskBasketItem | PenaltyBasketItem) => {
              if (
                pendingItem.date &&
                isAfter(new Date(pendingItem.date), new Date())
              )
                i.startTime = new Date(pendingItem.date);
              basket.pendingItems.push(i);
            });
          } else if (
            pendingItem.updateType === PendingUpdateType.remove &&
            idx !== -1
          ) {
            basket.pendingItems.splice(idx, 1);
          }
        } else if (
          pendingItem.updateType === PendingUpdateType.permissionChange
        ) {
          const idx = basket.pendingItems.findIndex((i) =>
            i.key?.startsWith(
              pendingItem.customDecisionResponse?.itemKey as string
            )
          );
          if (idx !== -1) {
            basket.pendingItems.splice(idx, 1);
          }
          const pIdx = basket.permissions.findIndex((i) =>
            i.key?.startsWith(
              pendingItem.customDecisionResponse?.itemKey as string
            )
          );
          if (pIdx !== -1) {
            basket.permissions.splice(pIdx, 1);
          }
        } else if (pendingItem.updateType === PendingUpdateType.permRelease) {
          const idx = basket.pendingItems.findIndex((i) =>
            i.key?.startsWith(pendingItem.item?.key as string)
          );
          if (
            pendingItem.updateType === PendingUpdateType.permRelease &&
            idx !== -1
          ) {
            basket.pendingItems.splice(idx, 1);
          }
        }
      });
    }
  };

  public getBankBalance = (): number => {
    return this.controlRecord.bank ?? 0;
  };

  /* settings editor */

  public saveDailyReport = (message: string, subject: string): void => {
    this.updateState(STATE_KEY_REPORTCOMPLETE, true);
    this.updateState(STATE_KEY_DAILYREPORT, false);
    sendMessageToOwner(MessageType.MemberMessage, this, message, subject);
  };

  public saveMorningReport = (message: string, subject: string): void => {
    this.updateState(STATE_KEY_MORNINGREPORT, false);
    sendMessageToOwner(MessageType.MemberMessage, this, message, subject);
  };

  public markMessageAsRead = ({ key, message }: IMemberMessage): void => {
    const m = this.memberMessages.find((m) => m.key === key);
    if (m) {
      m.read = true;
      void this.database.setMessageRead(
        key,
        this.controlRecord.userid,
        message.fromUserid
      );
      this.subscriptionManager.fireListenersSync(ListenerType.MemberMessages, this.memberMessages);
      this.auditService.auditUserAction("User Read Message", AuditActionTypes.Messages);
    }
  };

  public markMessageAsKeep = ({ key }: IMemberMessage, keep: boolean): void => {
    this.memberMessages.forEach((m) => {
      if (m.key === key) m.keep = keep;
    });
    this.subscriptionManager.fireListenersSync(ListenerType.MemberMessages, this.memberMessages);
    void this.database.setMessageKeepState(
      key,
      this.controlRecord.userid,
      keep
    );
  };

  public deleteMessage = ({ key }: IMemberMessage): void => {
    const messageidx = this.memberMessages.findIndex((m) => m.key === key);
    if (messageidx > -1) this.memberMessages.splice(messageidx, 1);
    this.subscriptionManager.fireListenersSync(ListenerType.MemberMessages, this.memberMessages);
    void this.database.deleteMessage(key, this.controlRecord.userid);
  };

  public deleteSentReceipt = ({ key }: IMemberSentReceipt): void => {
    const messageidx = this.sentMessages.findIndex((m) => m.key === key);
    if (messageidx > -1) this.sentMessages.splice(messageidx, 1);
    this.subscriptionManager.fireListenersSync(ListenerType.SentMessages, this.sentMessages);
    void this.database.deleteSentReceipt(key, this.controlRecord.userid);
  };

  public getIsSoundEnabled = (): boolean => {
    return this.getState(STATE_KEY_ENABLE_SOUNDS, false);
  };

  public getNotificationCount = (): number => {
    const messageCount = this.memberMessages
      ? this.memberMessages.filter((i) => !i.read).length
      : 0;
    return messageCount;
  };

  public get lastHygieneDate(): Date | undefined {
    return this.getState<Date | undefined>(STATE_KEY_LAST_HYGIENE, undefined);
  }

  public set lastHygieneDate(date: Date | undefined) {
    this.updateState(STATE_KEY_LAST_HYGIENE, date);
  }

  public getNextHygieneDate = (): string | undefined => {
    if (this.settings.hygieneBreaks === HygieneBreak.none) return undefined;
    const date = this.lastHygieneDate;
    if (!date) return "Hygiene Available";
    const diff = differenceInCalendarDays(new Date(), date);
    switch (this.settings.hygieneBreaks) {
      case HygieneBreak.daily: {
        if (diff > 0) return "Hygiene Available";
        else return "Available Tomorrow";
      }
      case HygieneBreak.twiceWeekly: {
        if (diff > 3) return "Hygiene Available";
        else {
          const toAdd = 4 - diff;
          if (diff === 1) return "Available Tomorrow";
          const newDate = addDays(new Date(), toAdd);
          return "Available " + convertDateToDay(newDate.getDay());
        }
      }
      case HygieneBreak.weekly: {
        if (diff > 7) return "Hygiene Available";
        else {
          const toAdd = 8 - diff;
          if (diff === 1) return "Available Tomorrow";
          const newDate = addDays(new Date(), toAdd);
          return "Available " + convertDateToDay(newDate.getDay());
        }
      }
      case HygieneBreak.twiceMonthly: {
        if (diff > 14) return "Hygiene Available";
        else {
          const toAdd = 15 - diff;
          if (diff === 1) return "Available Tomorrow";
          const newDate = addDays(new Date(), toAdd);
          return "Available " + formatValidDate(newDate);
        }
      }
      case HygieneBreak.monthly: {
        if (diff > 31) return "Hygiene Available";
        else {
          const toAdd = 32 - diff;
          if (diff === 1) return "Available Tomorrow";
          const newDate = addDays(new Date(), toAdd);
          return "Available " + formatValidDate(newDate);
        }
      }
    }
    return undefined;
  };

  public isHygieneAllowed = (): boolean => {
    const locks = this.getMyChastityTaskLocks();
    if (locks.length === 0) return false;

    const date = this.lastHygieneDate;
    if (!date) return this.settings.hygieneBreaks !== HygieneBreak.none;

    switch (this.settings.hygieneBreaks) {
      case HygieneBreak.daily: {
        return differenceInCalendarDays(new Date(), date) > 0;
      }
      case HygieneBreak.twiceWeekly: {
        return differenceInCalendarDays(new Date(), date) > 3;
      }
      case HygieneBreak.weekly: {
        return differenceInWeeks(new Date(), date) > 0;
      }
      case HygieneBreak.twiceMonthly: {
        return differenceInWeeks(new Date(), date) > 2;
      }
      case HygieneBreak.monthly: {
        return differenceInMonths(new Date(), date) > 0;
      }
      case HygieneBreak.none:
      default: {
        return false;
      }
    }
  };

  public isLocked = (): ILockState | undefined => {
    return this.getState(STATE_KEY_LOCK_STATE, undefined);
  };

  // get / unsub methods

  public async updateSettings(
    settings: ISessionSettings,
    systemUpdate?: boolean
  ): Promise<boolean> {
    if (!(await super.updateSettings(settings, systemUpdate))) return false;
    if (
      !this.controlRecord.isOwnedBySelf() &&
      !this.notifiedMemberOfChanges &&
      !systemUpdate
    ) {
    }
    return true;
  }

  public saveGeneralSettings(
    settings: IGeneralSettings,
    requiredCredit?: number,
    systemUpdate?: boolean
  ): Promise<void> {
    const { dailyReporting } = settings;
    const generalSettings = this.settings;
    if (generalSettings.dailyReporting !== dailyReporting) {
      generalSettings.dailyReporting = dailyReporting;
      // if turned off, set reports complete for the day, and if turned on also for today say we are done as we will otherwise be punished.
      const stateUpdates: Record<string, any> = {};
      stateUpdates[STATE_KEY_REPORTCOMPLETE] = true;
      void this.database
        .replaceUserState(
          this.controlRecord.userid,
          stateUpdates,
          this.sessionId,
          systemUpdate === true
        )
        .catch((e) => {
          console.debug(e);
        });
    }
    return super.saveGeneralSettings(settings, requiredCredit, systemUpdate);
  }

  public getDailyRulesText = (): string =>
    (this.getSessionSettings().dailyRulesText ?? "").toString();

  public saveDailyRulesText = async (rulesText: string): Promise<boolean> => {
    if (!this.canEditSettings()) {
      this.showMessage({
        message: "Unable to update daily rules for this session",
        persist: true,
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return false;
    }

    const normalisedRules = (rulesText ?? "").replace(/\r\n/g, "\n").trim();
    const previousRules = (this.settings.dailyRulesText ?? "").trim();
    const changed = previousRules !== normalisedRules;

    this.settings.dailyRulesText = normalisedRules;
    await this.saveGeneralSettings(this.settings);

    if (changed && !this.getControl().isOwnedBySelf()) {
      const message = normalisedRules.length > 0
        ? `Your owner has updated your daily rules.\n\n${normalisedRules}`
        : "Your owner has cleared your daily rules.";
      sendMessageToMember(
        MessageType.MemberMessage,
        this,
        message,
        "Daily Rules Updated",
        [this.getControl()],
        this.getControl().getOwner(),
        true
      );
    }

    this.showMessage({
      message: "Daily rules saved",
      notificationType: NotificationType.SettingsInfo,
    });
    return true;
  };

  public checkCanUnlockSession = (override?: boolean): void => {
    const lockState = this.isLocked();
    if (!lockState) return;
    if (!override) {
      if (lockState.lockType === LockTypes.duration) {
        if (
          lockState.unlockDate &&
          !isBefore(new Date(lockState.unlockDate), new Date())
        ) {
          return; // not yet
        }
      } else {
        const bank = this.getBankBalance();
        if (lockState.count && bank < lockState.count) {
          return; // not yet
        }
      }
    }
    if (override) {
      const state: Record<string, any> = {};
      state[STATE_KEY_LOCK_STATE] = "";
      void this.database
        .replaceUserState(
          this.controlRecord.userid,
          state,
          this.sessionId,
          override
        )
        .catch((e) => {
          console.debug(e);
        });
      return;
    }
    this.updateState(STATE_KEY_LOCK_STATE, undefined);
    this.showMessage({
      message: "Target Met - Session Unlocked",
      persist: true,
      notificationType: NotificationType.General,
    });
    this.auditService.auditUserAction(
      `Session unlocked - ${lockState.lockType}: ${lockState.count} ${LockCountType[lockState.countType]
      } target met`,
      AuditActionTypes.Session
    );
  };

  public lockSession = ({
    lockType,
    lock,
    count,
    countType,
  }: ILockState): void => {
    if (!lock) return;
    if (!this.controlRecord.isOwnedBySelf() || this.isLocked()) return;
    const lockState: ILockState = {
      lockType,
      count,
      countType,
    };
    if (!count) return;
    if (lockType === LockTypes.duration) {
      if (countType === LockCountType.weeks) {
        lockState.unlockDate = addWeeks(new Date(), count);
      } else {
        lockState.unlockDate = addDays(new Date(), count);
      }
    }
    this.updateState(STATE_KEY_LOCK_STATE, lockState);
    this.showMessage({
      message: "Session Locked",
      persist: true,
      notificationType: NotificationType.General,
    });
    this.auditService.auditUserAction(
      `Session locked - ${LockTypes[lockType]}: ${count} ${countType ? LockCountType[countType] : ""
      }`,
      AuditActionTypes.Settings
    );
  };

  public getPendingBasketItems = (isOwnerView?: boolean): BaseBasketItem[] => {
    const basketItems = this.getBasketItems(isOwnerView ?? false);

    const basketPendingItems: BaseBasketItem[] = [];
    basketItems.pendingItems
      .filter(p => !(p as ScheduleBasketItem | TaskBasketItem | PenaltyBasketItem).startTime)
      .forEach((pItem) => basketPendingItems.push(pItem));
    return basketPendingItems;
  }

  public getInProgressBasketItems = (isOwnerView?: boolean): BaseBasketItem[] => {
    const basketItems = this.getBasketItems(isOwnerView ?? false);
    const basketProgressItems: BaseBasketItem[] = [];
    basketItems.tasks.filter((c) => !c.isComplete).forEach((pItem) => basketProgressItems.push(pItem));
    basketItems.penalties.filter((c) => !c.isComplete).forEach((pItem) => basketProgressItems.push(pItem));
    basketItems.positions.filter((c) => !c.isComplete).forEach((pItem) => basketProgressItems.push(pItem));
    return basketProgressItems;
  }
  public offerSession = (userid: string, username: string): void => {
    const p = this.getProfile(userid);
    if (!p) return;
    const isAlreadyOwned = !!(p.ownerid && p.ownerid !== userid);
    sendOfferMessageToMember(this, { userid, username }, isAlreadyOwned);
    this.auditService.auditUserAction(
      `${this.controlRecord.username} Offered Session to ${username}`,
      AuditActionTypes.Session
    );
    this.showMessage({
      message: "Offer Sent",
      persist: true,
      notificationType: NotificationType.General,
    });
  };

  public loaneeEndLoan = async (): Promise<void> => {
    const control = this.getControl();
    if (!control || !control.ownerid) return;
    await this.unownSessionForUser(control, true);
    this.acceptSessionRequest(
      control.ownerid,
      control.owner as string,
      false,
      false
    );
  };
  public shareeEndShare = (): void => {
    const control = this.getControl();
    if (!control || !control.otherOwnerIds) return;
    const idx = control.otherOwnerIds.indexOf(this.controlRecord.userid);
    if (idx < 0) return;
    control.otherOwnerIds.splice(idx, 1);
    void this.database.updateOtherOwners(
      control.userid,
      control.otherOwnerIds,
      undefined,
      [this.controlRecord.username]
    );
  };

  public acceptSessionRequest = (
    ownerid: string,
    ownername: string,
    isLoan: boolean,
    isShare: boolean
  ): void => {
    if (!isLoan && !isShare)
      this.auditService.auditUserAction(
        `${this.controlRecord.username} accepted offer of ${ownername}`,
        AuditActionTypes.Session
      );
    else if (isLoan)
      this.auditService.auditUserAction(
        `${this.controlRecord.username} has been loaned to ${ownername}`,
        AuditActionTypes.Session
      );
    else if (isShare)
      this.auditService.auditUserAction(
        `${this.controlRecord.username} has been shared with ${ownername}`,
        AuditActionTypes.Session
      );

    if (!isShare) {
      void this.database.takeOwnership(
        this.controlRecord.userid,
        this.controlRecord.username,
        ownerid,
        ownername,
        isLoan
      );
      this.auditService.auditUserAction(
        `${ownername} owned Settings of ${this.controlRecord.username}`,
        AuditActionTypes.Session
      );
      if (!isLoan) {
        this.updateControlAttributes({ ownerid, owner: ownername });
        sendHouseRules(
          this,
          this.getOwnerProfile(ownerid),
          this.settings,
          this.getProfile(ownerid)
        );
        sendOwnedMessage(this, this.controlRecord, ownername);
        this.showMessage({
          message: `${this.controlRecord.username} is now owned by ${ownername}`,
          persist: true,
          notificationType: NotificationType.General,
        });
      } else {
        this.updateControlAttributes({
          loanedOwnerid: ownerid,
          loanedOwner: ownername,
        });
        this.showMessage({
          message: `Session Loaned to ${ownername}`,
          persist: true,
          notificationType: NotificationType.General,
        });
        sendLoanedMessage(this, this.controlRecord, ownername);
        sendLoaneeMessage(
          this,
          { userid: ownerid, username: ownername },
          this.controlRecord.username
        );
      }
    } else {
      this.showMessage({
        message: `Session shared to ${ownername}`,
        persist: true,
        notificationType: NotificationType.General,
      });
      if (!this.controlRecord.otherOwnerIds)
        this.controlRecord.otherOwnerIds = [];
      this.controlRecord.otherOwnerIds.push(ownerid);
      void this.database.updateOtherOwners(
        this.controlRecord.userid,
        this.controlRecord.otherOwnerIds,
        [ownername]
      );
      sendSharedMessage(this, this.controlRecord, ownername);
      sendShareeMessage(
        this,
        { userid: ownerid, username: ownername },
        this.controlRecord.username
      );
    }
  };

  public getSystemAuditLogs = (): Record<string, IAuditLogsDateCollection> => {
    Object.keys(this.systemAuditLogs).forEach((user) => {
      const userSet = this.systemAuditLogs[user];
      Object.keys(userSet).forEach((date) => {
        const recordSet = userSet[date];
        Object.keys(recordSet).forEach((itemKey) => {
          const item = recordSet[itemKey];
          if (!item.id) {
            recordSet[itemKey] = decrypt(
              item as unknown as string,
              {}
            ) as AuditMessage;
          }
        });
      });
    });
    return this.systemAuditLogs;
  };
  public getAuditLogs = (): IAuditLogsDateCollection => this.auditLogs;
  public getAuditBank = (): IAuditBankDateCollection => this.auditBank;
  public getAuditScores = (): IAuditScoreHistory[] => this.auditScores;
  public getWeeklyTargetProgress = (
    taskKey?: string
  ): IWeeklyTargetProgress[] | Record<string, Record<string, IWeeklyTargetProgress>> => {
    if (!taskKey) return this.weeklyTargetProgress;
    const byTask = this.weeklyTargetProgress[taskKey] || {};
    return Object.values(byTask).sort((a, b) =>
      (a.weekKey || "").localeCompare(b.weekKey || "")
    );
  };

  public saveWeeklyTargetProgress = async (
    progress: IWeeklyTargetProgress
  ): Promise<void> => {
    if (!progress?.taskKey || !progress.weekKey) return;
    await this.database.saveWeeklyTargetProgress(this.controlRecord.userid, progress);
    if (!this.weeklyTargetProgressCache) this.weeklyTargetProgressCache = {};
    if (!this.weeklyTargetProgressCache[progress.taskKey]) {
      this.weeklyTargetProgressCache[progress.taskKey] = {};
    }
    this.weeklyTargetProgressCache[progress.taskKey][progress.weekKey] = progress;
    this.subscriptionManager.fireListenersSync(ListenerType.WeeklyTargetProgress);
  };

  public setWeeklyTargetNextValue = async (
    taskKey: string,
    weekKey: string,
    nextWeekTargetValue: number
  ): Promise<void> => {
    if (!taskKey || !weekKey) return;
    await this.database.setWeeklyTargetNextValue(
      this.controlRecord.userid,
      taskKey,
      weekKey,
      nextWeekTargetValue
    );
    if (
      this.weeklyTargetProgressCache &&
      this.weeklyTargetProgressCache[taskKey] &&
      this.weeklyTargetProgressCache[taskKey][weekKey]
    ) {
      this.weeklyTargetProgressCache[taskKey][weekKey].nextWeekTargetValue =
        nextWeekTargetValue;
      this.subscriptionManager.fireListenersSync(ListenerType.WeeklyTargetProgress);
    }
  };

  public getOwnedItems = (): OwnedItemsCollection => this.ownedItems;
  public get ownedLovenseToys() {
    return this.lovenseToys;
  }

  public isLovenseBadlyConfigured = (
    item: TaskBasketItem | PenaltyBasketItem
  ) => {
    const toys = this.ownedLovenseToys;
    return (
      !toys ||
      toys.length === 0 ||
      !item.lovenseToyId ||
      !toys.find((t) => t.id === item.lovenseToyId)
    );
  };

  public getProfileSettings = async (
    profile: IOwnerProfileSession
  ): Promise<ISessionSettings | undefined> => {
    if (!profile) return undefined;
    const cachedItem = this.cachedProfileSettings[profile.userid];
    if (cachedItem) return cachedItem;
    this.cachedProfileSettings[profile.userid] =
      await this.getProfileSettingsForId(profile.userid);
    return this.cachedProfileSettings[profile.userid];
  };
  public updateOwnerProfileCache = (
    profile: IOwnerProfileSession,
    settings: ISessionSettings
  ): void => {
    if (!profile) return;
    this.cachedProfileSettings[profile.userid] = settings;
  };

  private getProfileSettingsForId = async (
    userid: string
  ): Promise<ISessionSettings> => {
    const cachedItem = this.cachedProfileSettings[userid];
    if (cachedItem) return cachedItem;
    this.cachedProfileSettings[userid] = await this.database.getProfileSettings(
      userid
    );
    return this.cachedProfileSettings[userid];
  };

  public updateNewJoinerSettings = (ownerTypeProfile: IOwnerProfileSession) => {
    void this.getProfileSettings(ownerTypeProfile).then((templateSettings) => {
      if (templateSettings) this.settings = templateSettings;
    });
  };

  private applyProfile = async (
    profile: IOwnerProfileSession,
    profileSettings: ISessionSettings,
    isWizard: boolean
  ): Promise<void> => {
    if (!profile || !profileSettings || this.isSystemLoggedOn()) return;

    this.settings = profileSettings;

    await this.database.updateSettings(
      this.controlRecord.userid,
      profileSettings,
      this.isLoggedOnUserSession()
    );

    // lock session as per requirements
    if (profile.lockOnSelection && !isWizard) {
      this.lockSession({
        lockType: profile.lockType as LockTypes,
        count: profile.lockCount,
        countType: profile.lockCountType as LockCountType,
        lock: true,
      });
    }

    this.updateControlAttributes({
      virtualProfileId: profile.userid,
      virtualProfileName: profile.name,
      virtualProfileImage: profile.ownerImg,
    });
    void this.database.takeVirtualOwnership(
      this.controlRecord.userid,
      profile.userid,
      profile.name,
      profile.ownerImg
    );
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        `New Profile ${profile.name} Selected for Session`,
        "Owner Selected"
      )
    );
  };

  public updateSessionLock = async () => {
    this.lastRemoteSessionId = this.sessionId;
    if (import.meta.env.VITE_LOCAL_MODE === 'true') return true;
    // lock the state updates to allow only this scheduler to update them. any other current schedulers will disconnect on this connection
    await this.database.updateSessionKey(
      this.controlRecord.userid,
      this.sessionId
    );
    this.auditService.auditUserAction(
      `Updated Session Lock Id - ${this.sessionId}`,
      AuditActionTypes.Session
    );
  };

  // updates the persisted state for a user
  public heartbeat = (): void => {
    if (!this.isControlLoaded()) return;
    localStorage.setItem("sessionTime", new Date().getTime().toString());
    void this.database.heartbeatControl(
      this.getControl().userid,
      import.meta.env.VITE_VERSION || "0.0.0"
    );
  };

  // updates the persisted state for a user
  public updateState<T>(key: string, value: T): void {
    if (!value) {
      delete this.state[key];
      this.pendingStateUpdates[key] = "";
    } else {
      const encryptedValue = encrypt(value);
      this.state[key] = encryptedValue;
      this.pendingStateUpdates[key] = encryptedValue;
    }
    void this.debouncePushState();
    this.fireListeners(); // means view get updated with the changes too
  }

  public isStateTransaction = () => this.inStateTransaction;

  public beginStateTransaction = () => {
    this.inStateTransaction = true;
  };

  public endStateTransaction = () => {
    this.inStateTransaction = false;
    void this.debouncePushState();
  };

  // Force any queued state updates to be pushed immediately.
  public flushPendingStateUpdates = async (): Promise<void> => {
    this.debouncePushState.flush();
    await this.pushState();
  };

  // updates the persisted state for a user
  private async pushState(): Promise<void> {
    if (
      this.isStateTransaction() ||
      !this.pendingStateUpdates ||
      Object.keys(this.pendingStateUpdates).length === 0
    )
      return;
    const updatesToSend = { ...this.pendingStateUpdates };
    this.pendingStateUpdates = {};
    await this.database.replaceUserState(
      this.controlRecord.userid,
      updatesToSend,
      this.sessionId,
      false
    );
  }

  public auditBankChange(reason: string, change: number): number {
    const newBank = super.auditBankChange(reason, change);
    this.checkCanUnlockSession();
    return newBank;
  }

  public getRollDateDiff = (): number => {
    const currentDay = startOfDay(new Date());
    const stateDayStr = this.getState(STATE_KEY_CURRENT_DAY, "");
    const stateDay = startOfDay(new Date(stateDayStr));
    const dayDiff = differenceInDays(currentDay, stateDay);
    if (dayDiff > 100) return -1; //treat as 1 day when so large
    return Math.abs(dayDiff);
  };

  public rollControlDay = async (): Promise<void> => {
    const currentDay = startOfDay(new Date());
    this.updateState(STATE_KEY_CURRENT_DAY, currentDay.toISOString());
    await this.pushState(); // force that change to be saved down to the db before we do anything else
    const stateDayStr = this.getState(STATE_KEY_CURRENT_DAY, "");
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Rolling Control Day",
        `Control day now set to ${stateDayStr}`
      )
    );
  };

  public rollDay = async (): Promise<RollState> => {
    const dayDiff = this.getRollDateDiff();
    if (dayDiff === 0) return { didRoll: false, days: 0, audit: [] };
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Rolling Session Detected",
        `${dayDiff} days found since last checked to roll. Updating session lock and current day`
      )
    );
    const rollState = { didRoll: true, days: dayDiff, audit: [] }; // checks if the date is current and updates it if it isn't current
    this.scheduleStartOfDay(rollState);
    await this.rollControlDay();
    await this.rollAudit(); // tidies up the audit logs for the new day
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Rolling Session",
        "Complete"
      )
    );
    return rollState;
  };

  private scheduleStartOfDay = (rollState: RollState): void => {
    const startOfDayEvent = this.getStartOfDayEvent() as IEventItem;

    const scheduleInfo = this.getScheduleInfo();
    // delete the existing start of day we have planned as we could have different and more accurate roll state
    if (this.hasEventScheduled(startOfDayEvent.key)) {
      const delIdx = scheduleInfo.findIndex(
        (s) => s.key === startOfDayEvent.key
      );
      if (delIdx > -1) {
        scheduleInfo.splice(delIdx, 1);
      }
    }

    const scheduledItem: ISchedulerTimerInfo = {
      ...startOfDayEvent,
      startTime: getScheduleTime(
        this,
        convertToEventDayFromDate(new Date().getDay()),
        startOfDayEvent
      ),
      duration: 0,
      scheduleOptions: { forceSchedule: true, state: rollState },
      isPaused: true,
      isComplete: false,
      scheduleReason:
        "This is the start of day event which begins each day and is scheduled to take place around 4am most days",
    };

    this.updateSchedulerState(scheduledItem);

    void this.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "Start Of Day",
        "Schedule updated",
        TraceLevel.info
      )
    );
  };

  private rollAudit = async (): Promise<void> => {
    await this.database.rollAudit(this.controlRecord.userid);
  };

  public getOwnerLastUpdateTime = (): ControlRecord => {
    return this.ownerControlRecord as ControlRecord;
  };

  public getUserLastUpdateTime = (): ControlRecord => {
    return this.controlRecord;
  };

  public getOwnerHistory = async (
    profile: IControl
  ): Promise<IOwnerHistory[]> => {
    if (!profile) return [];
    if (profile.ownerHistory) return profile.ownerHistory;
    profile.ownerHistory = await this.database.getOwnerHistory(profile.userid);
    return profile.ownerHistory;
  };

  public getMemberMessages = (): IMemberMessage[] => {
    return this.memberMessages;
  };

  public getSentMessages = (): IMemberSentReceipt[] => {
    return this.sentMessages;
  };

  public deleteReadMessages = (): void => {
    void this.database.deleteOldMessages(
      this.controlRecord.userid,
      DeleteMessageMode.Read
    );
    this.memberMessagesCache = this.memberMessages.filter(
      (m) => m.keep || !m.read
    );
    this.subscriptionManager.fireListenersSync(ListenerType.MemberMessages, this.memberMessages);
  };

  public deleteReadSentMessages = (): void => {
    void this.database.deleteOldSentMessages(
      this.controlRecord.userid,
      DeleteMessageMode.Read
    );
    this.sentMessagesCache = this.sentMessages.filter((m) => !m.read);
    this.subscriptionManager.fireListenersSync(ListenerType.SentMessages, this.sentMessages);
  };

  public deleteAllMessages = (): void => {
    void this.database.deleteOldMessages(
      this.controlRecord.userid,
      DeleteMessageMode.All
    );
    this.memberMessagesCache = this.memberMessages.filter((m) => m.keep);
    this.subscriptionManager.fireListenersSync(ListenerType.MemberMessages, this.memberMessages);
  };

  public deleteAllSentMessages = (): void => {
    void this.database.deleteOldSentMessages(
      this.controlRecord.userid,
      DeleteMessageMode.All
    );
    this.sentMessagesCache = [];
    this.subscriptionManager.fireListenersSync(ListenerType.SentMessages, this.sentMessages);
  };

  public deleteOldMessages = (onstartup?: boolean): void => {
    void this.database.deleteOldMessages(
      this.controlRecord.userid,
      onstartup ? DeleteMessageMode.Housekeep : DeleteMessageMode.All
    );
  };

  public deleteOldSentMessages = (onstartup?: boolean): void => {
    void this.database.deleteOldSentMessages(
      this.controlRecord.userid,
      onstartup ? DeleteMessageMode.Housekeep : DeleteMessageMode.All
    );
  };

  public shareItem = async (
    userid: string,
    username: string,
    item: ISettingsItem
  ): Promise<void> => {
    const settings = await this.database.getUserSettings(userid);
    if (!settings) return;
    this.updateSettingsWithItem(item, settings);
    void this.database.updateSettings(userid, settings, false);
    void this.auditUser(
      new AuditMessage(
        AuditActionTypes.Settings,
        item.name,
        `Shared ${getItemTypeDisplayName(item.itemType)} Item`,
        TraceLevel.info
      )
    );
    sendMessageToMember(
      MessageType.NewItemCreated,
      this,
      `A new ${getItemTypeDisplayName(
        item.itemType
      )} item has been shared to you. It will be available in your settings and by default will be hidden. You can toggle this to be included in your session at any time. ${item.description
      }`,
      `New Item Available - ${getItemTypeDisplayName(item.itemType)} - ${item.name
      }`,
      [{ userid, username }]
    );
    this.showMessage({
      message: `Shared ${getItemTypeDisplayName(item.itemType)} Item`,
      notificationType: NotificationType.General,
    });
  };

  private getTaskItemsInner = (
    task: ITaskItem,
    lookup: Record<string, ITaskItem[][]>
  ): ITaskItem[][] => {
    if (lookup[task.key]) return lookup[task.key];
    let items: ITaskItem[][] = [];
    if (task.isItem || !task.itemsUsed || task.itemsUsed.length === 0)
      return items;
    task.itemsUsed.forEach((tl) => {
      const itemSet: ITaskItem[] = [];
      items.push(itemSet);
      tl.forEach((ti) => {
        if (task.key === ti) return;
        const innerTask = this.getTask(ti);
        if (innerTask && innerTask.isItem) itemSet.push(innerTask);
        else if (innerTask && innerTask.itemsUsed)
          items = items.concat(this.getTaskItemsInner(innerTask, lookup));
      });
    });
    lookup[task.key] = items;
    return items;
  };

  private isCustomScheduleToday = (): boolean => {
    return this.getState(STATE_KEY_CUSTOM_DAY, false);
  };

  /*
        checks two things, firstly the event is valid and it is not hidden and then is it scheduled for the day.
        if it isn't scheduled then we will fall back to timing the event
    */
  public checkSettingItemEventTriggerValid = (
    item: IEndEventTriggered
  ): boolean => {
    if (!item.endTriggerEvent) return false;
    if (this.getEventSettings().disableEvents) return false;
    if (this.isCustomScheduleToday()) return false;
    const event = this.getEvent(item.endTriggerEvent);
    if (!event) {
      // this.auditAction({
      //     type: AuditActionTypes.Alert,
      //     name: this.getDisplayName(item),
      //     action: `Unable to find event for trigger ${item.endTriggerEvent}.`,
      //     success: false
      // });
      return false;
    }

    const eventVisible =
      isAvailableItem(event) &&
      (item.rollOverEventDays ||
        this.itemAvailableForDay(
          event,
          convertToEventDayFromDate(new Date().getDay()),
          false,
          false
        ));

    if (!eventVisible) {
      // this.auditAction({
      //     type: AuditActionTypes.Alert,
      //     name: this.getDisplayName(item),
      //     action: `Event ${this.getDisplayName(event)} is hidden so will never fire to end task.`,
      //     success: false
      // });
      return false;
    }

    return eventVisible;
    // return this.hasEventScheduled(eventKey); // this stops us creating a specific day event in future to trigger something if the event always has to be scheduled.
    // also if already happened today, then should be able to set it up for future day
  };

  /*
        checks of an event is valid at any point and then checks if it is valid for today
    */
  public checkSettingItemEventTriggerValidForToday = (
    item: IEndEventTriggered
  ): boolean => {
    if (!item.endTriggerEvent) return false;
    let valid = this.checkSettingItemEventTriggerValid(item);
    if (valid) {
      const event = this.getEvent(item.endTriggerEvent);
      if (!event) return false;
      valid = this.itemAvailableForDay(
        event,
        convertToEventDayFromDate(new Date().getDay()),
        false,
        false
      );
    }
    return valid;
  };

  /// ensures only one item of the categories listed are included in any daily schedule
  public canAddCategory(
    item: ICategoryItem,
    taskSchedule: (ITaskTimerInfo | IPenaltyTimerInfo)[]
  ): boolean {
    if (!item.categories || item.categories.length === 0) return true;
    const categoriesToAvoid: string[] | undefined =
      this.getTaskSettings().singleItemCategories;
    if (!categoriesToAvoid || categoriesToAvoid.length === 0) return true;

    const currentCategories: string[] = [];
    for (let i = 0; i < taskSchedule.length; i += 1) {
      const info = taskSchedule[i];
      if (!info) continue;
      if (!info.categories) continue;
      info.categories.forEach((c) => {
        if (c && !currentCategories.includes(c)) currentCategories.push(c);
      });
    }

    const pendingItems = this.getPendingState();
    if (pendingItems) {
      const itemsToAdd = pendingItems.filter(
        (p) => p.updateType === PendingUpdateType.add && p.item
      );
      for (let i = 0; i < itemsToAdd.length; i += 1) {
        const matchItem = itemsToAdd[i].item;
        if (matchItem && matchItem.key === (item as any).key) continue;
        const info = itemsToAdd[i].item as ICategoryItem;
        if (!info) continue;
        if (!info.categories) continue;
        info.categories.forEach((c) => {
          if (c && !currentCategories.includes(c)) currentCategories.push(c);
        });
      }
    }

    if (this.eventSchedule) {
      const itemsToAdd = this.eventSchedule.filter(
        (p) => p.itemType === ItemTypes.task
      );
      for (let i = 0; i < itemsToAdd.length; i += 1) {
        const info = itemsToAdd[i] as ICategoryItem;
        if (!info) continue;
        if (!info.categories) continue;
        info.categories.forEach((c) => {
          if (c && !currentCategories.includes(c)) currentCategories.push(c);
        });
      }
    }

    const uniqueSet = new Set(
      currentCategories.filter((c) => categoriesToAvoid.includes(c))
    );
    return item.categories.filter((c) => uniqueSet.has(c)).length === 0;
  }

  public canPerformAllTasksForEventDay(day: EventDay): boolean {
    return this.canPerformAllTasksForDay(convertToDateDay(day));
  }

  public itemAvailableForDay(
    item: ISettingsItem,
    eventDay: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean
  ): boolean {
    if (!item || (!includeHidden && item.hidden) || item.isDeleted)
      return false;
    if (
      item.itemType === ItemTypes.event &&
      (item as IEventItem).eventType === EventType.startOfDay
    )
      return true;
    if (
      (item.itemType === ItemTypes.event ||
        item.itemType === ItemTypes.permission) &&
      !this.isScheduleItemAvailableDay(item as IScheduledItem, eventDay)
    )
      return false;
    if (ignoreAvailability) return true;
    return this.itemAvailableForDayDiary(item, eventDay);
  }

  public itemAvailableForDayDiary(item: ISettingsItem, eventDay: EventDay) {
    if (this.canPerformSomeTasksForEventDay(eventDay)) {
      // means the day is not unavailable
      const memberNotBusyToday = this.canPerformAllTasksForEventDay(eventDay);
      switch (item.availabilityDayType) {
        case undefined:
        case AvailabilityDayType.AllDays: {
          return true;
        }
        case AvailabilityDayType.FreeDays: {
          return memberNotBusyToday;
        }
        case AvailabilityDayType.BusyDays: {
          return !memberNotBusyToday;
        }
        default:
          return false;
      }
    } else {
      // the day is unavailable for use
      return false;
    }
  }

  public getAllowedPenalties = (): IPenaltyItem[] => {
    return this.getPenalties((p) => this.canAddTask(p).isOk);
  };

  public getEventTriggeredBasketTasks = (): IEndEventTriggered[] => {
    const tasks: IEndEventTriggered[] = [];
    const basket = this.getBasketItems(false);
    basket.tasks
      .filter(
        (c) =>
          !c.isComplete &&
          !c.isPaused &&
          !c.permanent &&
          this.checkSettingItemEventTriggerValid(c)
      )
      .forEach((t) => tasks.push(t));
    basket.penalties
      .filter(
        (c) =>
          !c.isComplete &&
          !c.isPaused &&
          this.checkSettingItemEventTriggerValid(c)
      )
      .forEach((t) => tasks.push(t));
    return tasks;
  };

  public hasTaskFinished = (
    item: ITaskTimerInfo | IPenaltyTimerInfo,
    intraday: boolean,
    logReason?: boolean
  ): boolean => {
    const sodEvent = this.getStartOfDayEvent();
    const scheduleInfo = this.getState<ISchedulerTimerInfo[]>(
      STATE_KEY_SCHEDULER,
      []
    );
    const nextSODDate = getNextEventDateTime(sodEvent, scheduleInfo, this);
    const now = new Date();
    if (
      (item as ITaskTimerInfo).permanent ||
      item.isComplete ||
      !isAvailableItem(this.getTask(item.key) as ISettingsItem)
    ) {
      return true;
    } else if (
      item.endTime &&
      isBefore(item.endTime, intraday ? nextSODDate : now) &&
      item.isPaused !== true
    ) {
      return true;
    } else if (!item.isPaused && this.checkSettingItemEventTriggerValid(item)) {
      const scheduleMatch = scheduleInfo.find(
        (s) => s.key === item.endTriggerEvent
      );
      // scheduled events include an item with the trigger and the event as a start time and it is in the past
      if (
        scheduleMatch &&
        scheduleMatch.startTime &&
        isBefore(scheduleMatch.startTime, intraday ? nextSODDate : now)
      ) {
        // an event hasn't yet fired but the schedule in the past so would have fired
        return true;
      } else if (this.taskSchedule[item.endTriggerEvent as string]) {
        // an event has been handled but the corresponding task hasn't yet been cleared
        return true;
      } else {
        // an event was not scheduled for the time from last log on until now, but the event is valid and we roll it on until the event triggers the task to be cleared
        if (logReason) {
          this.auditUser(
            new AuditMessage(
              AuditActionTypes.DailyChecks,
              item.name,
              `${scheduleMatch?.name} is set to being awaited to trigger the completion of ${item.name}. This task has not finished.`,
              TraceLevel.warning
            )
          );
        }
        return false;
      }
    }
    return false;
  };

  public determineTasksForDay(
    day: EventDay,
    events: IEventItem[] | null,
    perms: IPermissionItem[] | null,
    includeHidden: boolean,
    ignoreAvailability: boolean,
    hidePendingItems: boolean,
    ignoreScheduleTasks?: boolean
  ): ((ITaskItem | IPenaltyItem) & IScheduledInfo)[] {
    const returnTasks = super.determineTasksForDay(
      day,
      events,
      perms,
      includeHidden,
      ignoreAvailability,
      hidePendingItems,
      ignoreScheduleTasks
    );

    const date = getDateForDay(day);

    if (hidePendingItems) return returnTasks;

    const pendingItems = this.getPendingState();
    const itemsToAdd = pendingItems.filter(
      (p) =>
        p.date &&
        getDate(new Date(p.date)) === getDate(date) &&
        isBefore(new Date(p.date), endOfDay(date)) &&
        p.updateType === PendingUpdateType.add
    );
    itemsToAdd.forEach((i) => {
      if (i.item && i.item.itemType === ItemTypes.task) {
        if (
          returnTasks.findIndex((s) => i.item && s.key === i.item.key) === -1 &&
          this.itemAvailableForDay(
            i.item,
            day,
            includeHidden,
            ignoreAvailability
          )
        )
          returnTasks.push(
            this.describeTask(
              {
                ...(i.item as ITaskItem),
                name: `${i.item.name} (Owner Selected)`,
                unavailable: ignoreAvailability
                  ? !this.itemAvailableForDayDiary(i.item, day)
                  : false,
                scheduleReason:
                  "Task has been manually added by the owner for the given day and is now in the list of items to be applied.",
              },
              day
            )
          );
      }
    });
    return returnTasks;
  }

  public shouldTaskRoll = (
    item: ITaskTimerInfo | IPenaltyTimerInfo,
    intraday: boolean
  ): boolean => {
    if (this.hasTaskFinished(item, intraday)) return false;
    if (this.checkSettingItemEventTriggerValid(item)) {
      return true; // it is awaiting an event and should be left to run
    } else if (
      isTimedItem(item.countType) &&
      !item.isPaused &&
      !item.isComplete
    )
      return true; // it is in progress and should be left to run
    return false;
  };

  public pendingRoll = (): boolean => {
    const today = startOfDay(new Date());
    const currentDayStr = this.getState(
      STATE_KEY_CURRENT_DAY,
      startOfDay(new Date()).toISOString()
    );
    const currentDay = new Date(currentDayStr);
    const dayDiff = currentDay && differenceInDays(currentDay, today);
    if (dayDiff === 0) {
      // means the day in the control record match today's date
      const startOfDayEvent = this.getStartOfDayEvent();
      if (!startOfDayEvent) return false;
      // this means we have not yet triggered and dealt with the new day, so treat as the last day still
      return this.hasEventScheduled(startOfDayEvent.key);
    }
    return true;
  };

  public getIsWeekend = (): boolean => {
    let dateToCheck = new Date();
    if (this.pendingRoll()) {
      dateToCheck = addDays(new Date(), -1);
    }
    const day = dateToCheck.getDay();
    const weekendDays = this.peekWeekendDays();
    // in case where weekend isn't set, use real weekend
    if (!weekendDays || weekendDays.length === 0) return isWeekend(dateToCheck);
    const eventDay = convertToEventDayFromDate(day);
    return weekendDays.indexOf(eventDay) > -1;
  };

  public shouldSendReportForYesterday = (): boolean => {
    const dateToCheck = addDays(new Date(), -1);
    return this.canPerformSomeTasksForDate(dateToCheck);
  };

  public hasEventScheduled = (key: string): boolean => {
    return this.eventSchedule.some(
      (i) => i.key === key && i.itemType === ItemTypes.event
    );
  };

  public hasTaskScheduled = (key: string): boolean => {
    return this.eventSchedule.some(
      (i) => i.key === key && i.itemType === ItemTypes.task
    );
  };

  public hasPermissionScheduled = (key: string): boolean => {
    return this.eventSchedule.some(
      (i) => i.key === key && i.itemType === ItemTypes.permission
    );
  };

  public checkItemIsOwned = (item: IItemSupport): boolean => {
    if (!item) return true;
    if (!item.isItem && (!item.itemsUsed || item.itemsUsed.length === 0))
      return true;
    if (!this.ownedItems) return false;
    if (item.isItem) return item.key in this.ownedItems;
    if (!item.itemsUsed) return true;
    /// one of the sets of items this item uses is owned in it's entirity
    return (
      item.itemsUsed.filter((i) => {
        const itemArray = i.map((ii) => this.getTask(ii));
        return (
          itemArray
            .filter((k) => k && k.key !== item.key)
            .filter((ii) => ii && this.checkItemIsOwned(ii)).length ===
          itemArray.length
        );
      }).length > 0
    );
  };

  private cachedTasks: Record<string, TaskItem[]> | undefined;

  protected onSettingsChanged(snapshot: ISessionSettings): void {
    this.cachedTasks = undefined;
    super.onSettingsChanged(snapshot);
    this.pruneExpiredCustomScheduledDates();
    // Pre-fetch detail for today's and tomorrow's scheduled custom schedules
    // so that day initialisation can access events synchronously.
    this.prefetchScheduledCustomScheduleDetails();
  }

  private getDateScheduleKey = (date: Date): string => format(date, "yyyy-MM-dd");

  private parseDateScheduleKey = (dateKey: string): Date | undefined => {
    if (!dateKey) return undefined;
    const parsed = parseISO(dateKey);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  private pruneExpiredCustomScheduledDates = (): void => {
    const settings = this.getSessionSettings();
    const dateMap = settings.customScheduledDates;
    if (!dateMap) return;

    const todayStart = startOfDay(new Date());
    let changed = false;
    Object.entries({ ...dateMap }).forEach(([dateKey, scheduleKey]) => {
      const parsed = this.parseDateScheduleKey(dateKey);
      if (!parsed || isBefore(parsed, todayStart)) {
        delete dateMap[dateKey];
        changed = true;
        const schedule = this.getCustomSchedule(scheduleKey);
        if (schedule?.scheduledDate === dateKey) {
          this.deleteCustomSchedule(schedule);
        }
      }
    });

    if (changed) {
      void this.saveGeneralSettings(settings, undefined, true);
    }
  };

  private prefetchScheduledCustomScheduleDetails = (): void => {
    const todayDate = new Date();
    const tomorrowDate = addDays(new Date(), 1);
    const today = convertToEventDayFromDate(todayDate.getDay());
    const tomorrow = convertToEventDayFromDate(tomorrowDate.getDay());
    [
      { day: today, date: todayDate },
      { day: tomorrow, date: tomorrowDate },
    ].forEach(({ day, date }) => {
      const key = this.getScheduledCustomSchedule(day, date)?.key;
      if (key && !this.customScheduleDetailCache[key]) {
        void this.loadCustomScheduleDetail(key);
      }
    });
  };

  public getAvailableRandomTasks = (
    intraDay: boolean,
    date: Date
  ): TaskItem[] => {
    if (!this.cachedTasks || !this.cachedTasks[intraDay.toString()]) {
      if (!this.cachedTasks) this.cachedTasks = {};
      this.cachedTasks[intraDay.toString()] = this.getTasks((i) =>
        intraDay
          ? TaskItem.CanTaskBeSelectedIntraDay(i)
          : TaskItem.CanTaskBeSelectedStartOfDay(i)
      );
    }
    return this.cachedTasks[intraDay.toString()].filter(
      (i) => this.canAddTask(i, date).isOk
    );
  };

  public getOwnerProfile = (id: string): IOwnerProfileSession | undefined => {
    const key = getOwnerUserId(id);
    return this.ownerProfiles.find((t) => t.userid === key);
  };

  public getAllProfiles = (showAll: boolean): IOwnerProfileSession[] => {
    const owners = this.ownerProfiles.filter((t) => showAll || !t.hidden);
    return owners;
  };

  /* called from the system delete function in the owner editor - deletes settings */
  public removeOwnerProfile = (key: string, deleteSettings: boolean): void => {
    key = getOwnerUserId(key);
    void this.database.removeOwnerProfile(key, deleteSettings);
    this.showMessage({
      message: "Owner Profile deleted",
      persist: true,
      notificationType: NotificationType.ProfileInfo,
    });
    this.auditService.auditUserAction("Owner Profile deleted", AuditActionTypes.Template);
  };

  /* called from the delete member profile function in the owner editor does not delete settings too */
  public removeMemberProfile = async (
    profile: IOwnerProfileSession
  ): Promise<void> => {
    if (!profile) return;
    profile.userid = getOwnerUserId(profile.userid);
    await this.database.removeOwnerProfile(profile.userid, false);
    this.showMessage({
      message: `Owner Profile ${profile.name} deleted`,
      persist: true,
      notificationType: NotificationType.ProfileInfo,
    });
    this.auditService.auditUserAction(
      `Owner Profile ${profile.name} deleted`,
      AuditActionTypes.Template
    );
  };

  public copyMemberProfile = async (
    from: IOwnerProfileSession
  ): Promise<void> => {
    const profileSettings = await this.getProfileSettings(from);
    if (!profileSettings) return;
    const profile: IOwnerProfileSession = { ...from };
    profile.hidden = true;
    profile.name = `Copy of ${profile.name}`;
    profile.userid = uuid() + "-ownerProfile"; // set up as a new system profile
    profile.isSystem = true;
    this.saveOwnerProfileSettings(profile, profileSettings);
  };

  public getOwnerImage = (): string | undefined => {
    const profile = this.getOwnerProfile(
      getOwnerUserId(this.getControl().getOwner().userid)
    );
    return profile?.ownerImg;
  };

  public getOwnerImageForId = (ownerid: string): string | undefined => {
    const profile = this.getOwnerProfile(getOwnerUserId(ownerid));
    return profile?.ownerImg;
  };

  public getItemOptionSelectedToday = (
    item: ITaskItem | IPenaltyItem
  ): string | undefined => {
    let option;
    for (let i = 0; i < Object.keys(this.taskSchedule).length; i += 1) {
      const taskInfo = this.taskSchedule[Object.keys(this.taskSchedule)[i]];
      // if what we are requesting is itself already selected
      if (taskInfo.key === item.key && taskInfo.selectedCondition)
        return `${taskInfo.selectedCondition} ${taskInfo.name}`;
      // if what we are requesting is an item used by a task already selected
      if (taskInfo.selectedItems && taskInfo.selectedItems[item.key])
        return taskInfo.selectedItems[item.key];
    }
    return option;
  };

  /// todo: this should avoid anything which has the same conflict category as items in the task list already
  /// todo: need to check all the other ways tasks are generated
  /// 3) via task mandatory tasks daily
  /// 4) via event category task selections
  /// 5) permission task selections?
  /// 6) tasks which have category selections
  /// what not to cater for
  /// if something specifically defines that task, allow it so if events, permissions - don't restrict
  /// this is for randomly generated items in the task list which may conflict with what is there already
  public getRandomTask = (availableTasks: TaskItem[]): TaskItem | undefined => {
    return getRandomItemWithCache(
      availableTasks,
      STATE_KEY_HISTORY_CREDITS,
      (i) => i.key,
      this,
      true
    );
  };

  public getTaskDateError = (
    item: ITaskSafety,
    date: Date
  ): string | undefined => {
    if (!this.canPerformSomeTasksForDate(date)) {
      return "Unable to add with day set to unavailable on user session";
    }
    if (item.availabilityDayType !== AvailabilityDayType.FreeDays)
      return undefined;
    return this.canPerformAllTasksForDate(date)
      ? undefined
      : "Unable to add with a busy day set for date selected";
  };

  public subscribeToSystemAuditLogs = (
    listener: (logs: Record<string, IAuditLogsDateCollection>) => void
  ): void => {
    this.subscriptionManager.subscribe(ListenerType.SystemAuditLog, listener);
  };

  public unsubscribeToSystemAuditLogs = (
    listener: (logs: Record<string, IAuditLogsDateCollection>) => void
  ): void => {
    this.subscriptionManager.unsubscribe(ListenerType.SystemAuditLog, listener);
  };

  public saveProfileChanges = (newState: ControlRecord): void => {
    const newRecord = new ControlRecord({ ...this.controlRecord });
    newRecord.categories = newState.categories ?? [];
    newRecord.missingCategories = newState.missingCategories ?? [];
    newRecord.profileImage = newState.profileImage ?? "";
    newRecord.role = newState.role ?? ProfileRole.Member;
    newRecord.summary = newState.summary ?? "";
    newRecord.disablePublicProofs = newState.disablePublicProofs || false;
    newRecord.country = newState.country ?? "";
    newRecord.language = newState.language ?? [];
    newRecord.discordUserId = newState.discordUserId ?? "";
    this.controlRecord = newRecord;
    this.updateControlAttributes(this.controlRecord);
  };

  public canPerformAllTasksForDay = (day: JavascriptDay): boolean => {
    if (this.checkHolidayForDay(getDateForDay(convertToEventDayFromDate(day))))
      return false;
    const unavailableDays = this.getUnavailableDays();
    const eventDay = convertToEventDayFromDate(day);
    if (
      unavailableDays &&
      unavailableDays.findIndex((d) => d === eventDay) > -1
    )
      return false;
    const workDays = this.getState(STATE_KEY_WORK_DAYS, []);
    if (workDays) {
      return workDays.findIndex((d) => d === eventDay) === -1;
    }
    return true;
  };

  public canPerformSomeTasksForToday(): boolean {
    return this.canPerformSomeTasksForDate(new Date());
  }

  public canPerformSomeTasksForDate = (date: Date) => {
    const dateDay = convertToEventDayFromDate(date.getDay());
    return this.canPerformSomeTasksForEventDay(dateDay);
  };

  public checkHolidayToday(): boolean {
    return this.checkHolidayForDay(new Date());
  }
  public checkHolidayForDay(day: Date): boolean {
    if (this.settings.holidayMode && this.settings.holidayEndDate) {
      if (!isBefore(this.settings.holidayEndDate, day)) return true; // still on holiday
      sendHolidayModeMessageToMember(false, this);
      this.settings.holidayMode = false;
      this.settings.holidayEndDate = undefined;
      void this.saveGeneralSettings(this.settings, undefined, true);
      this.showMessage({
        message: "Holiday Mode Expired",
        persist: true,
        notificationType: NotificationType.General,
      });
      this.auditService.auditUserAction("Holiday Mode Ended", AuditActionTypes.Session);
      return false;
    }
    const holidays = this.getHolidays();
    const oldHols: number[] = [];
    if (holidays && holidays.length > 0) {
      for (let i = 0; i < holidays.length; i += 1) {
        const range = holidays[i];
        const beforeStart = isBefore(range.from, day);
        const beforeEnd = !isAfter(range.to, day);
        if (beforeStart && !beforeEnd) return true; // holiday has started and not yet ended
        else if (beforeStart && beforeEnd) {
          oldHols.push(i); // holiday has fully passed
        }
        // else it is yet to happen ignore it
      }
    }
    if (oldHols.length > 0) {
      this.updateHolidays(holidays.filter((range) => isAfter(range.to, day)));
    }
    return this.settings.holidayMode || false;
  }

  public canPerformSomeTasksForEventDay(dateDay: EventDay) {
    if (this.checkHolidayForDay(getDateForDay(dateDay))) return false;
    const unavailableDays = this.peekUnavailableDays();
    if (unavailableDays && unavailableDays.length > 0) {
      if (unavailableDays.indexOf(dateDay) > -1) return false;
    }
    return true;
  }

  public canPerformAllToday = () => {
    return (
      !this.checkHolidayToday() &&
      this.canPerformSomeTasksForToday() &&
      this.canPerformAllTasksForDay(new Date().getDay())
    );
  };

  public toggleFavourite = (item: ISettingsItem & IFavourite): void => {
    let favs = this.getState<string[]>(STATE_KEY_FAVOURITE_ITEMS, []);
    if (!item.isFavourite) item.isFavourite = true;
    else item.isFavourite = false;
    if (!favs) {
      favs = [item.key];
    } else {
      const matchIdx = favs.findIndex((i) => i === item.key);
      if (matchIdx > -1) {
        favs.splice(matchIdx, 1);
      } else {
        favs.push(item.key);
      }
    }
    this.updateState(STATE_KEY_FAVOURITE_ITEMS, favs);
  };

  public enrollWithProfile = (
    profile: IOwnerProfileSession,
    profileSettings: ISessionSettings,
    isWizard: boolean
  ): void => {
    if (!profileSettings) return;
    this.ownerProfileSummary = profile; // the new owner for this member
    if (!profile.chastityOnly) {
      sendMessageToSystem(
        MessageType.OwnerSelected,
        this,
        `${profile.name} selected by user ${this.controlRecord.username} for a session.`,
        "Owner Profile Selected"
      );
      sendMessageToMember(
        MessageType.Owned,
        this,
        `You are now under the control of ${profile.name
        } who now runs your session. You session will be updated and from tomorrow you will see their settings take affect. ${profile.lockOnSelection
          ? "Your session is locked until you have met the requirements of your owner. Details are shown in the menu bar. After your session is unlocked, you are free to change your session at any time."
          : "You are free to change your owner at any time."
        }`,
        "You are now owned",
        [this.controlRecord],
        { userid: this.controlRecord.userid, username: profile.name }
      );

      if (profile.welcomeMessage && profile.welcomeMessage.length > 0) {
        sendMessageToMember(
          MessageType.WelcomeMessage,
          this,
          profile.welcomeMessage,
          `Message from ${profile.name}`,
          [this.controlRecord],
          {
            userid: this.controlRecord.userid,
            username: profile?.name as string,
          }
        );
      }

      sendHouseRules(this, profile, profileSettings);
    }
    if (profile.userid && !profile.isSystem) {
      const userid = profile.userid.replace(memberOwnerKeySuffix, "");
      const ownerMatch = this.getAllControlRecords().find(
        (c) => c.userid === userid
      );
      if (ownerMatch) {
        sendMessageToMember(
          MessageType.ProfileChosen,
          this,
          `Hello! ${this.controlRecord.username} has selected your Owner Profile as their virtual owner. You can respond to this message to speak further with them and the Offer Session Button on this message can be used to physically own their session. They will then need to 'Accept Offer' on a message they receive.`,
          "Your Owner Profile has been selected",
          [ownerMatch],
          this.getControl()
        );
      }
    }

    void this.applyProfile(profile, profileSettings, isWizard);
  };

  public getCommunityRole = (
    user: ControlRecord,
    profiles: IOwnerProfileSession[]
  ): ProfileRole => {
    if (!user || !user.userid) return ProfileRole.None;
    const ownerProfile = profiles.find(
      (p) => p && p.userid && p.userid.includes(user.userid)
    );
    const allMembers = this.getAllControlRecords();
    const ownedProfiles = allMembers.filter(
      (p) =>
        p && p.userid && p.ownerid === user.userid && p.ownerid !== p.userid
    );
    let role = user.role;
    if (
      !role ||
      (role === ProfileRole.Owner &&
        !ownerProfile &&
        ownedProfiles.length === 0) ||
      (role === ProfileRole.Member &&
        (ownerProfile || ownedProfiles.length > 0))
    ) {
      if ((ownerProfile || ownedProfiles.length > 0) && user.isAdmin && !role) {
        role = ProfileRole.Owner;
      } else if (ownerProfile || ownedProfiles.length > 0) {
        role = ProfileRole.Switch;
      } else {
        role = ProfileRole.Member;
      }
    }
    return role;
  };

  public getMemberCommunityProfile(
    user: ControlRecord | undefined
  ): ControlRecord {
    if (!user) return user as unknown as ControlRecord;
    const role = this.getCommunityRole(user, this.ownerProfiles);
    const ownerProfile = this.ownerProfiles.find(
      (p) => p && p.userid && p.userid.includes(user.userid)
    );
    if (role === ProfileRole.Owner && ownerProfile) {
      return this.convertOwnerProfileToCommunityProfile(user, ownerProfile);
    } else if (role === ProfileRole.Switch && ownerProfile) {
      return this.convertSwitchProfileToCommunityProfile(user, ownerProfile);
    }
    return new ControlRecord({
      ...user,
      role: role,
      profileImage: getProfileImage(user),
    });
  }

  public getTopOwners = (): string[] => {
    const profiles = this.getAllControlRecords();
    const s: Record<string, ControlRecord[]> = {};
    const ownerMap = profiles
      .filter(
        (p) =>
          p.ownerid && p.role !== ProfileRole.Owner && p.ownerid !== p.userid
      )
      .reduce((c, p) => {
        if (!c) c = {};
        if (p) (c[p.ownerid as string] = c[p.ownerid as string] || []).push(p);
        return c;
      }, s);
    return Object.values(ownerMap)
      .sort((x, y) => y.length - x.length)
      .map((p) => p[0].ownerid as string)
      .slice(0, 2);
  };

  public getTopMembers = (count?: number): string[] => {
    const profiles = this.getAllControlRecords();
    return profiles
      .filter(
        (p) =>
          p &&
          p.role !== ProfileRole.Owner &&
          p.lastScore &&
          isToday(p.lastUpdateTime)
      )
      .sort((x, y) => (y.lastScore as number) - (x.lastScore as number))
      .map((p) => p.userid)
      .slice(0, count || 3);
  };

  public getBottomMembers = (count?: number): string[] => {
    const profiles = this.getAllControlRecords();
    return profiles
      .filter(
        (p) =>
          p &&
          p.role !== ProfileRole.Owner &&
          p.lastPenalties &&
          isToday(p.lastUpdateTime)
      )
      .sort((x, y) => (y.lastPenalties as number) - (x.lastPenalties as number))
      .map((p) => p.userid)
      .slice(0, count || 3);
  };

  public getAllTimeTopMembers = (
    count?: number
  ): { userid: string; score: number }[] => {
    const profiles = this.getAllControlRecords();
    return profiles
      .filter((p) => p.role !== ProfileRole.Owner)
      .map((p) => ({
        userid: p.userid,
        score: p.totalScore || p.lastScore || 0,
      }))
      .filter((p) => p.score)
      .sort((x, y) => y.score - x.score)
      .slice(0, count || 3);
  };

  public getAllTimeBottomMembers = (
    count?: number
  ): { userid: string; penalties: number }[] => {
    const profiles = this.getAllControlRecords();
    return profiles
      .filter((p) => p.role !== ProfileRole.Owner)
      .map((p) => ({
        userid: p.userid,
        penalties: p.totalPenalties || p.lastPenalties || 0,
      }))
      .filter((p) => p.penalties)
      .sort((x, y) => y.penalties - x.penalties)
      .slice(0, count || 3);
  };

  public hasCategoriesInBasket = (
    includePending: boolean,
    categoriesToMatch: string[] | undefined,
    sourceKey?: string
  ): string[] | undefined => {
    if (categoriesToMatch !== undefined && categoriesToMatch.length > 0) {
      const basketSchedule = this.getBasketItems(includePending);
      const categories: string[] = [];
      basketSchedule.tasks
        .filter(
          (c) =>
            c.key !== sourceKey &&
            c.categories &&
            c.categories.length > 0 &&
            !c.isComplete &&
            (includePending || !c.isPaused)
        )
        .forEach((c) =>
          c.categories.forEach((cat) => {
            if (categories.indexOf(cat) === -1) categories.push(cat);
          })
        );
      basketSchedule.penalties
        .filter(
          (c) =>
            c.key !== sourceKey &&
            c.categories &&
            c.categories.length > 0 &&
            !c.isComplete &&
            (includePending || !c.isPaused)
        )
        .forEach((c) =>
          c.categories.forEach((cat) => {
            if (categories.indexOf(cat) === -1) categories.push(cat);
          })
        );
      if (includePending) {
        basketSchedule.pendingItems
          .filter((c) => {
            const cats = (c as ICategoryItem)?.categories;
            return (
              c.key !== sourceKey && cats && cats.length > 0 && !c.isComplete
            );
          })
          .forEach((c) =>
            (c as ICategoryItem)?.categories?.forEach((cat) => {
              if (categories.indexOf(cat) === -1) categories.push(cat);
            })
          );
      }
      return isCategoryLimited(categories, categoriesToMatch);
    }
    return undefined;
  };

  public addPenaltyPoint = (
    reason: string,
    amount?: number,
    resetValue?: boolean
  ) => {
    const currentPenaltyPoints = this.controlRecord.penaltyPoints ?? 0;
    const newVal = resetValue
      ? amount ?? 1
      : currentPenaltyPoints + (amount ?? 1);
    this.updateControlAttributes({ penaltyPoints: newVal });
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Penalty Point",
        `${resetValue
          ? `Penalties reset to ${amount}`
          : `${amount && amount < 0 ? "reduced by" : "increase by"} ${amount || 1
          }`
        } for ${reason}`
      )
    );
  };

  public getFavourites = (): string[] => {
    return this.getState<string[]>(STATE_KEY_FAVOURITE_ITEMS, []);
  };

  public getBusyDays(): EventDay[] {
    const busyDays = this.getState<EventDay[]>(STATE_KEY_WORK_DAYS, []);
    const unavailableDays = this.getUnavailableDays();
    if (unavailableDays) {
      unavailableDays.forEach((d) => {
        const idx = busyDays.indexOf(d);
        if (idx > -1) busyDays.splice(idx, 1);
      });
    }
    return busyDays;
  }

  public getHolidays = (): Holiday[] => {
    return this.getState<Holiday[]>(STATE_KEY_HOLIDAYS, []);
  };

  public getUnavailableDays = (): EventDay[] => {
    return this.getState<EventDay[]>(STATE_KEY_UNAVAILABLE_DAYS, []);
  };

  public getWeekendDays = (): EventDay[] => {
    return this.getState<EventDay[]>(STATE_KEY_WEEKEND_DAYS, [
      EventDay.saturday,
      EventDay.sunday,
    ]);
  };

  public getPreviewDays(): EventDay[] {
    return this.getState<EventDay[]>(STATE_KEY_PREVIEW_DAYS, []);
  }

  public getOverrideTimes(): Record<EventDay, number[]> {
    return this.getState<Record<EventDay, number[]>>(
      STATE_KEY_OVERRIDE_TIMES,
      {} as Record<EventDay, number[]>
    );
  }

  protected peekWeekendDays = (): EventDay[] => {
    return this.getState<EventDay[]>(STATE_KEY_WEEKEND_DAYS, [
      EventDay.saturday,
      EventDay.sunday,
    ]);
  };

  private peekUnavailableDays = (): EventDay[] => {
    return this.getState<EventDay[]>(STATE_KEY_UNAVAILABLE_DAYS, []);
  };

  public updateWorkDays = (days: EventDay[]): void => {
    this.updateState(STATE_KEY_WORK_DAYS, days);
  };

  public updateWeekendDays = (days: EventDay[]): void => {
    this.updateState(STATE_KEY_WEEKEND_DAYS, days);
  };

  public updateHolidays = (days: Holiday[]): void => {
    this.updateState(STATE_KEY_HOLIDAYS, days);
  };

  public showPreviewIfConfigured = () => {
    if (this.isTomorrowAPreviewDay()) {
      this.createPreview(false);
    }
  };

  public updatePreviewAndResend = (
    newTasks: IPendingItem[],
    scheduleReason: string
  ) => {
    if (!this.isTomorrowAPreviewDay()) return;

    let previewState = this.getState<IBasket | undefined>(
      STATE_KEY_PREVIEW_BASKET,
      undefined
    );
    if (!previewState) previewState = this.createPreview(false, true);
    // this task or penalty might have specific addition options added to it by the additionOptions
    else
      newTasks.forEach((t) => {
        const result = this.updatePreviewWithTask(
          { ...(t.item as ITaskItem | IPenaltyItem), scheduleReason },
          t.itemAdditionOptions
        );
        if (!result.isOK)
          Emitter.emitNotification({
            message: result.reason,
            error: true,
            notificationType: NotificationType.ErrorInfo,
          });
      });
    // refetch it up dated with the new items
    previewState = this.getState<IBasket | undefined>(
      STATE_KEY_PREVIEW_BASKET,
      undefined
    );
    if (previewState)
      sendPreviewMessageToMember(this, this.getControl(), previewState, true);

    // reason we do this is that we are NOT in the user session and we want to force the state update on the user session
    // the key part is "omit integrity" check
    const tempState: Record<string, string> = {};
    tempState[STATE_KEY_PREVIEW_BASKET] = encrypt(previewState);
    void this.database.replaceUserState(
      this.controlRecord.userid,
      tempState,
      this.sessionId,
      true
    );
  };

  public clearPreviewData = () => {
    this.updateState(STATE_KEY_PREVIEW_BASKET, undefined);
  };

  public updatePreviewDays = (days: EventDay[]): void => {
    this.updateState(STATE_KEY_PREVIEW_DAYS, days);
  };

  public updateOverrideTimesForDay = (
    eventDay: EventDay,
    newTimes: number[]
  ) => {
    const overrideTimes = this.getOverrideTimes();
    const newOverrides = { ...overrideTimes };
    newOverrides[eventDay] = newTimes;
    this.updateOverrideTimes(newOverrides);
    const dateDay = convertToEventDayFromDate(addDays(new Date(), 1).getDay());
    if (this.isTomorrowAPreviewDay() && dateDay === eventDay) {
      // we changed settings that affect tomorrow so recreate the preview
      sendPreviewMessageToMember(
        this,
        this.getControl(),
        this.createPreview(false)
      ); // re-generate the preview only if preview not changed
    }
    return newOverrides;
  };

  public updateOverrideTimes = (
    overrides: Record<EventDay, number[]>
  ): void => {
    this.updateState(STATE_KEY_OVERRIDE_TIMES, overrides);
  };

  public getPreviewRandomTasks(date: Date): TaskItem[] {
    const tasks = this.getAvailableRandomTasks(false, date);
    return this.filterTasksForPreview(tasks);
  }

  public filterTasksForPreview(inputTasks: TaskItem[]): TaskItem[] {
    let tasks = inputTasks;
    // remove anything already previewed
    const previewState = this.getState<IBasket | undefined>(
      STATE_KEY_PREVIEW_BASKET,
      { pendingItems: [] } as any as IBasket
    );
    tasks = tasks.filter(
      (t) =>
        !previewState ||
        previewState.pendingItems.findIndex((task) => task.key === t.key) === -1
    );
    // remove anything already pending for tomorrow
    const pending = this.getPendingState();
    tasks = tasks.filter(
      (t) =>
        !this.getPreviewTaskScheduleReason(
          t,
          pending,
          startOfTomorrow(),
          previewState?.pendingItems as any as (
            | ITaskTimerInfo
            | IPenaltyTimerInfo
          )[]
        )
    );
    return tasks;
  }

  protected getPreviewTaskForCategory = (
    selectedCategory: string,
    triggerItem: ISettingsItem,
    date?: Date
  ): ITaskItem | undefined => {
    let matches = this.getAvailableCategoryTasks(
      selectedCategory,
      triggerItem,
      date
    );
    matches = this.filterTasksForPreview(matches as TaskItem[]);

    if (matches.length === 0) {
      return;
    }
    const selectedItem = getRandomItemWithCache(
      matches,
      STATE_KEY_HISTORY_CREDITS,
      (t) => t.key,
      this,
      false
    );
    return selectedItem;
  };

  public createPreview = (
    force: boolean,
    suppressMessage?: boolean
  ): IBasket => {
    if (!this.isTomorrowAPreviewDay()) return {} as IBasket;

    // check if we already have a preview
    const currentPreview = this.getState<IBasket | undefined>(
      STATE_KEY_PREVIEW_BASKET,
      undefined
    );
    if (!force && currentPreview) {
      // return generated preview
      return currentPreview;
    }
    if (!this.isLoggedOnUserSession()) {
      //meaning we are the owner session trying to generate a preview for a member
      this.updatePendingItems(
        {
          fireImmediate: true,
          updateType: PendingUpdateType.createPreview,
        },
        false
      );
      // carry on however so the state reflects locally what is happening
    }
    // If a custom schedule is assigned for tomorrow but its detail (events) hasn't been
    // loaded into the detail cache yet, defer the preview until the detail is available.
    // This prevents the user session from generating and sending a blank preview message
    // when the scheduler processes PendingUpdateType.createPreview before the Firebase
    // subscription has delivered the custom schedule detail.
    const previewDateDay = convertToEventDayFromDate(startOfTomorrow().getDay());
    const strTomorrow = formatValidDate(addDays(new Date(), 1), "dd MMM yyyy");
    const pendingScheduleChange = this.getPendingState().find(
      (p) =>
        p.updateType === PendingUpdateType.scheduleChange &&
        p.date === strTomorrow
    );
    const scheduledCustomScheduleKey =
      pendingScheduleChange?.customScheduleKey ??
      this.settings?.customScheduledDays?.[previewDateDay];
    if (
      scheduledCustomScheduleKey &&
      !this.customScheduleDetailCache[scheduledCustomScheduleKey]
    ) {
      if (!this.customScheduleDetailLoads.has(scheduledCustomScheduleKey)) {
        this.customScheduleDetailLoads.add(scheduledCustomScheduleKey);
        void this.loadCustomScheduleDetail(scheduledCustomScheduleKey)
          .then((detail) => {
            if (detail) this.createPreview(force, suppressMessage);
          })
          .finally(() => {
            this.customScheduleDetailLoads.delete(scheduledCustomScheduleKey);
          });
      }
      return currentPreview ?? ({} as IBasket);
    }
    // if not then work out what tasks are due tomorrow
    let previewState: IBasket = {
      tasks: [],
      penalties: [],
      pendingItems: [],
      permissions: [],
      positions: [],
      childTasks: {},
      possiblePenalties: [],
      chanceTasks: [],
    };

    const dateDay = convertToEventDayFromDate(startOfTomorrow().getDay());
    // already pending items for tomorrow - do this first so other items limited by anything queued
    this.peekPendingBasketItems(endOfTomorrow(), previewState); // add in anything pending for tomorrow
    this.updateState(STATE_KEY_PREVIEW_BASKET, previewState);

    // we don't care presently about events and permissions here, let's for now focus on just pure tasks
    const sessionSchedule = this.getDaySessionSchedule(
      dateDay,
      false,
      false,
      false
    );
    const tasks = sessionSchedule.tasks;

    //TODO: these need to pass in anything already in preview list to omit, i.e. single selection categories.
    tasks.forEach((currentTask) => this.updatePreviewWithTask(currentTask));

    this.handlePilloryPreviewItems();
    // send preview message again every time it's updated
    previewState = this.getState<IBasket>(
      STATE_KEY_PREVIEW_BASKET,
      previewState
    );
    //todo: stop sending this out if nothing has changed?
    // suppressMessage must always win, including forced preview rebuilds triggered
    // by the member scheduler's PendingUpdateType.createPreview handler.
    if (!suppressMessage)
      sendPreviewMessageToMember(this, this.getControl(), previewState, true);
    return previewState;
  };

  public handlePilloryPreviewItems = () => {
    if (!this.isTomorrowAPreviewDay()) return;

    //Add Pillories that are going to expire tomorrow
    const pillories = this.getPilloriesForUser(this.controlRecord.userid);
    const pilloriesEndingTomorrow = pillories.filter(
      (p) =>
        isAfter(new Date(p.pilloryEndTime), endOfToday()) &&
        isBefore(new Date(p.pilloryEndTime), endOfTomorrow())
    );
    const tasksForPillories = pilloriesEndingTomorrow
      .filter((p) => p.item !== undefined)
      .map(
        (p) =>
        ({
          ...this.getPenalty(p.item?.key ?? ""),
          possiblePenaltyItem: true,
        } as IPenaltyItem & IScheduledInfo)
      );
    // will add in to the might need category
    tasksForPillories.forEach((currentTask) =>
      this.updatePreviewWithTask(currentTask)
    );
  };

  /// we gather the items for the roulette so we can can simulate the picking of one of them
  /// so we then can tell the user if that item contained an item to own
  private getRouletteItemsForTask = (task: ITaskItem | IPenaltyItem, previewState: IBasket, inputOptions?: ItemAdditionOptions): BaseBasketItem[] => {
    const rouletteTasks: (TaskBasketItem | PenaltyBasketItem)[] = [];
    //roulette added as all 
    if (task.taskKeys)
      task.taskKeys
        .map((t) => this.getTask(t))
        .forEach((t) => {
          if (!t) return;
          const previewReason = this.getPreviewTaskScheduleReason(
            t,
            [],
            startOfTomorrow(),
            previewState.pendingItems as any as (
              | ITaskTimerInfo
              | IPenaltyTimerInfo
            )[]
          );
          if (previewReason) {
            return;
          }
          const scheduleReason =
            "This task is selected by random by a roulette wheel and there is a chance you will need the items";
          const items = this.createBasketItems(
            t,
            () =>
              isWeekendDay(
                convertToEventDayFromDate(startOfTomorrow().getDay()),
                this.getWeekendDays()
              ),
            `Created by the preview feature yesterday. ${scheduleReason}`,
            startOfTomorrow(),
            inputOptions
          );
          items.forEach((i) => rouletteTasks.push(i)); // add it in to penalties array here as it isn't a penalty but it's how to show optional possible items
        });
    if (task.penaltyKeys)
      task.penaltyKeys
        .map((t) => this.getPenalty(t))
        .forEach((t) => {
          if (!t) return;
          const previewReason = this.getPreviewTaskScheduleReason(
            t,
            [],
            startOfTomorrow(),
            previewState.pendingItems as any as (
              | ITaskTimerInfo
              | IPenaltyTimerInfo
            )[]
          );
          if (previewReason) {
            return;
          }
          const scheduleReason =
            "This penalty is selected by random by a roulette wheel and there is a chance you will need the items";
          const items = this.createBasketItems(
            t,
            () =>
              isWeekendDay(
                convertToEventDayFromDate(startOfTomorrow().getDay()),
                this.getWeekendDays()
              ),
            `Created by the preview feature yesterday. ${scheduleReason}`,
            startOfTomorrow(),
            inputOptions
          );
          items.forEach((i) => rouletteTasks.push(i));
        });
    return rouletteTasks;
  }

  private updatePreviewWithTask = (
    currentTask: (ITaskItem | IPenaltyItem) & IScheduledInfo,
    inputOptions?: ItemAdditionOptions
  ): { isOK: boolean; reason: string } => {
    const previewState = this.getState<IBasket>(
      STATE_KEY_PREVIEW_BASKET,
      {} as IBasket
    );
    let { scheduleReason } = currentTask;
    let task = currentTask as ITaskItem | IPenaltyItem;
    if (currentTask.randomPlaceholder) {
      let proposedMatch: ITaskItem | IPenaltyItem | undefined;
      if (currentTask.randomSelectedKey) {
        const match = this.getCurrentItem({
          ...task,
          key: currentTask.randomSelectedKey,
        });
        if (match) {
          proposedMatch = match as ITaskItem | IPenaltyItem;
        }
      }
      if (!proposedMatch) {
        const availableTasks = this.getPreviewRandomTasks(
          startOfTomorrow()
        ).filter((i) => !TaskItem.IsScheduled(i));
        // now filter out
        proposedMatch = this.getRandomTask(availableTasks);
      }
      if (!proposedMatch)
        return { isOK: false, reason: "Unable to find task to add to preview" };
      task = proposedMatch;
      scheduleReason +=
        " This task was selected by random from those available.";
    } else if (
      task.itemType === ItemTypes.task &&
      (task as ITaskItem).selectCategoryItem &&
      TaskItem.IsTaskPickerTask(task as ITaskItem)
    ) {
      const randomPick = this.getPreviewTaskForCategory(
        (task as ITaskItem).selectCategoryItem as string,
        task,
        startOfTomorrow()
      );
      if (!randomPick)
        return {
          isOK: false,
          reason: "Unable to find a random matching task to add to preview",
        };
      task = randomPick;
      scheduleReason += ` This task was selected by random using the category ${(task as ITaskItem).selectCategoryItem
        }`;
    } else if (this.shouldReplaceRoutineItemOnPreview(task)) {
      const rouletteTasks: BaseBasketItem[] = this.getRouletteItemsForTask(task, previewState, inputOptions);
      // the idea here is to set up the roulette to pre-select the item it will pick, then we can add it to the items required too 
      const selectedItem = sample(rouletteTasks);
      if (selectedItem && previewState) {
        // this replaces the roulette item with the selected item
        if (!previewState.childTasks) previewState.childTasks = {};
        if (!previewState.childTasks[task.key]) previewState.childTasks[task.key] = [];
        previewState.childTasks[task.key].push(selectedItem as TaskBasketItem | PenaltyBasketItem);

        this.auditUser(
          new UserActionAuditMessage(
            AuditActionTypes.Session,
            "Create Preview",
            `Added ${selectedItem.name} to child task for roulette item ${task.name} for user with possible available items`
          )
        );
        // this state is then used in the the iteration so needs to be updated
        this.updateState(STATE_KEY_PREVIEW_BASKET, previewState);
        return { isOK: true, reason: 'Added successfully' }; // still lets add the routlette item itself to the list to process
      }
    } else if (
      task.itemType === ItemTypes.task &&
      TaskItem.IsStandardTask(task as ITaskItem) &&
      task.isItem &&
      task.useRouletteForOptions
    ) {
      task = {
        ...(this.getCurrentItem(task) as ITaskItem | IPenaltyItem),
        useRouletteForOptions: false,
      }; // treat as normal task
    }
    task.name = this.getCurrentItem(task)?.name ?? task.name; // lets us lose the scheduling information from the name
    if (!inputOptions) inputOptions = {};
    if (task.selectedChoiceName && !inputOptions.selectedChoiceName) {
      if (task.selectedChoiceName) {
        inputOptions.selectedChoiceName = task.selectedChoiceName;
        const choiceTask = task as ITaskItem;
        if (choiceTask && choiceTask.choiceNames && choiceTask.itemsUsed) {
          // this sets the other parts
          const choicePairIdx = Object.entries(choiceTask.choiceNames).findIndex((c) => c[1] === task.selectedChoiceName);
          let idx = -1;
          if (choicePairIdx > -1) idx = Object.entries(choiceTask.choiceNames)[choicePairIdx][0] as unknown as number;
          else if (task.selectedChoiceName.startsWith("Option ")) {
            idx = parseInt(task.selectedChoiceName.replace("Option ", ""), 10) - 1;
            if (isNaN(idx)) idx = -1;
          }
          if (idx > -1) {
            inputOptions.selectedItemGroupKeys = choiceTask.itemsUsed[idx];
            if (choiceTask.itemOptions)
              inputOptions.selectedItemGroupOptions = choiceTask.itemOptions[idx];
          }
        }
      }
    }
    // always set this so that if an item has options and items then both are set  
    if (!inputOptions.selectedCondition) inputOptions.selectedCondition = task.selectedCondition;
    if (!inputOptions.selectedItemGroupOptions) inputOptions.selectedItemGroupOptions = task.selectedItems;
    if (!inputOptions.selectedItemGroupKeys) inputOptions.selectedItemGroupKeys = task.selectedItemKeys;


    const previewReason = this.getPreviewTaskScheduleReason(
      task,
      [], // dont check against pending items, as things tomorrow are pending, so we cancel out items we should include
      startOfTomorrow(),
      previewState.pendingItems as any as (ITaskTimerInfo | IPenaltyTimerInfo)[]
    );
    if (!previewReason) {
      const items = this.createBasketItems(
        task,
        () =>
          isWeekendDay(
            convertToEventDayFromDate(startOfTomorrow().getDay()),
            this.getWeekendDays()
          ),
        `Created by the preview feature yesterday. ${scheduleReason}`,
        startOfTomorrow(),
        inputOptions
      );
      if (currentTask.possiblePenaltyItem) {
        items.forEach((i) => {
          if (!previewState.possiblePenalties)
            previewState.possiblePenalties = [];
          if (previewState.possiblePenalties.find((p) => p.key === i.key && (p as TaskBasketItem | PenaltyBasketItem).selectedCondition === i.selectedCondition))
            return;
          previewState.possiblePenalties.push(i);
        });
      } else {
        if (currentTask.chanceItem) {
          items.forEach((i) => {
            if (!previewState.chanceTasks) previewState.chanceTasks = [];
            if (previewState.chanceTasks.find((p) => p.key === i.key && p.selectedCondition === i.selectedCondition)) return;
            previewState.chanceTasks.push(i);
          });
        }

        if (currentTask.childTask && currentTask.fromKey && previewState) {
          if (!previewState.childTasks) previewState.childTasks = {};
          if (!previewState.childTasks[currentTask.fromKey])
            previewState.childTasks[currentTask.fromKey] = [];
          // this stopped multiple tasks being set from a single event, only first would be scheduled.
          // if (previewState.childTasks[currentTask.fromKey].length === 0) { // only add one set of child tasks for a parent task, if we are updating then only include new ones
          items.forEach((i) => {
            if (
              currentTask.fromKey &&
              previewState.childTasks &&
              previewState.childTasks[currentTask.fromKey] &&
              !previewState.childTasks[currentTask.fromKey].find(
                (childItem) => childItem.key === i.key && childItem.selectedCondition === i.selectedCondition
              )
            )
              // stops us replacing things already set
              previewState.childTasks[currentTask.fromKey].push(i);
          });
          // }
        } else {
          items.forEach((i) => {
            if (previewState.pendingItems.find((p) => p.key === i.key && (p as TaskBasketItem | PenaltyBasketItem).selectedCondition === i.selectedCondition)) return;
            previewState.pendingItems.push(i);
          });
        }
      }
      this.auditUser(
        new UserActionAuditMessage(
          AuditActionTypes.Session,
          "Create Preview",
          `Added ${task.name} to preview for user for tomorrow`
        )
      );
      // this state is then used in the the iteration so needs to be updated
      this.updateState(STATE_KEY_PREVIEW_BASKET, previewState);
    } else {
      this.auditUser(
        new UserActionAuditMessage(
          AuditActionTypes.Session,
          "Create Preview",
          `Unable to add ${task.name} to preview for user for tomorrow - ${previewReason}`
        )
      );
      return { isOK: false, reason: previewReason };
    }
    return { isOK: true, reason: "Added successfully" };
  };

  public isTodayPreviewDay = (): boolean => {
    const dateDay = convertToEventDayFromDate(new Date().getDay());
    const previewDays = this.getPreviewDays();
    return previewDays.indexOf(dateDay) > -1;
  };

  public isTomorrowAPreviewDay = (): boolean => {
    const dateDay = convertToEventDayFromDate(addDays(new Date(), 1).getDay());
    const previewDays = this.getPreviewDays();
    return previewDays && previewDays.indexOf(dateDay) > -1;
  };
  public unpackPreviewToday = (): boolean => {
    const dateDay = convertToEventDayFromDate(new Date().getDay());
    const previewDays = this.getPreviewDays();
    const isToday = previewDays.indexOf(dateDay) > -1;

    const preCreatedItems = this.getPreviewBasket();
    if (!isToday || !preCreatedItems) return false;
    // clear pending items as they would already be in the preview
    const currentPendingState = this.pendingState;
    if (preCreatedItems.pendingItems) {
      this.auditUser(
        new UserActionAuditMessage(
          AuditActionTypes.Session,
          "Unpack Preview",
          `Applying ${preCreatedItems.pendingItems.length} preview items to member session`
        )
      );
      preCreatedItems.pendingItems.forEach((item) => {
        if (this.taskSchedule[item.key])
          this.auditUser(
            new UserActionAuditMessage(
              AuditActionTypes.Session,
              "Unpack Preview",
              `Unable to apply unpacked task ${item.name} as item is already in progress`
            )
          );
        else if (!this.canAddTask(item).isOk) {
          this.auditUser(
            new UserActionAuditMessage(
              AuditActionTypes.Session,
              "Unpack Preview",
              `Unable to add task ${item.name
              } from preview as task is not available for today when trying to apply - ${this.getCanAddTaskReason(
                item,
                []
              )}`
            )
          );
        } else {
          const match = currentPendingState.find(
            (p) => p.item && p.item.key === item.key
          );
          const isTimedRelease =
            match &&
            match.updateType === PendingUpdateType.add &&
            match.itemAdditionOptions &&
            match.date &&
            isAfter(new Date(match.date), Date.now()) &&
            isBefore(new Date(match.date), startOfTomorrow());
          // this item is queued for today at a set time, so let's leave this one and allow the pending item to trigger still at the right time
          if (match && match.date && isTimedRelease) {
            this.auditUser(
              new UserActionAuditMessage(
                AuditActionTypes.Session,
                "Unpack Preview",
                `Skipping task ${item.name
                } from preview package as pending for set time ${new Date(
                  match.date
                )}`
              )
            );
          } else {
            this.updateTaskState(
              item as unknown as ITaskTimerInfo | IPenaltyTimerInfo
            );
            this.auditUser(
              new UserActionAuditMessage(
                AuditActionTypes.Session,
                "Unpack Preview",
                `Applied task ${item.name} from preview package`
              )
            );
          }
        }
      });
    }
    this.clearPreviewDayPendingItems();
    this.updateState(PREVIEW_EVENT_TASKS, preCreatedItems.childTasks);
    return true;
  };

  public handleSendRequiredEventsOnline = () => {
    sendRequiredEventsMessageToMember(
      this,
      this.controlRecord,
      this.getBasketItems(true)
    );
  };

  public handlePreviewDayOnRoll = () => {
    if (this.isTomorrowAPreviewDay()) {
      this.clearPreviewData();
      this.showPreviewIfConfigured();
    } else {
      this.clearPreviewData();
    }
  };

  public getPreviewBasket = (): IBasket | undefined => {
    return this.getState<IBasket | undefined>(
      STATE_KEY_PREVIEW_BASKET,
      undefined
    );
  };

  public getPreviewBasketTasks = (): BaseBasketItem[] | undefined => {
    const basket = this.getPreviewBasket();
    if (!basket) return [];
    const tasks = basket.pendingItems ?? [];
    if (basket.childTasks) {
      Object.values(basket.childTasks).forEach((set) => {
        set.forEach((i) => tasks.push(i));
      });
    }
    if (basket.chanceTasks) {
      basket.chanceTasks.forEach((task) => {
        tasks.push(task);
      });
    }
    if (basket.possiblePenalties) {
      basket.possiblePenalties.forEach((pen) => {
        tasks.push(pen);
      });
    }
    return tasks;
  };
  public removePreviewBasketTask = () => {
    const basket = this.getPreviewBasket();
    if (!basket) return [];
    const tasks = basket.pendingItems;
    if (basket.childTasks) {
      Object.values(basket.childTasks).forEach((set) => {
        set.forEach((i) => tasks.push(i));
      });
    }
    return tasks;
  };

  public removePreviewTaskAndResend = (taskToRemove: BaseBasketItem) => {
    if (!this.isTomorrowAPreviewDay()) return;

    const previewState = this.getState<IBasket | undefined>(
      STATE_KEY_PREVIEW_BASKET,
      undefined
    );
    const alreadyPreviewed = previewState !== undefined;
    if (!alreadyPreviewed || !previewState.childTasks) return;
    let removed = false;
    let idx = previewState.pendingItems.findIndex(
      (t) => t.key === taskToRemove.key
    );
    if (idx > -1) {
      previewState.pendingItems.splice(idx, 1);
      removed = true;
    }
    idx = previewState.penalties.findIndex((t) => t.key === taskToRemove.key);
    if (idx > -1) {
      previewState.penalties.splice(idx, 1);
      removed = true;
    }

    if (previewState.childTasks) {
      Object.keys(previewState.childTasks).forEach((childTaskKey) => {
        const tasks =
          previewState.childTasks && previewState.childTasks[childTaskKey];
        if (!tasks) return;
        idx = tasks.findIndex((t) => t.key === taskToRemove.key);
        if (idx > -1) {
          tasks.splice(idx, 1);
          removed = true;
        }
      });
    }

    if (!removed) return;
    if (previewState && alreadyPreviewed)
      sendPreviewMessageToMember(this, this.getControl(), previewState, true);

    // reason we do this is that we are NOT in the user session and we want to force the state update on the user session
    // the key part is "omit integrity" check
    const tempState: Record<string, string> = {};
    tempState[STATE_KEY_PREVIEW_BASKET] = encrypt(previewState);
    void this.database.replaceUserState(
      this.controlRecord.userid,
      tempState,
      this.sessionId,
      true
    );
  };

  public replacePreviewTaskAndResend = (
    taskToUpdate: TaskBasketItem | PenaltyBasketItem
  ) => {
    if (!this.isTomorrowAPreviewDay()) return;

    const previewState = this.getState<IBasket | undefined>(
      STATE_KEY_PREVIEW_BASKET,
      undefined
    );
    const alreadyPreviewed = previewState !== undefined;
    if (!alreadyPreviewed || !previewState.childTasks) return;
    let updated = false;
    let idx = previewState.pendingItems.findIndex(
      (t) => t.key === taskToUpdate.key
    );
    if (idx > -1) {
      previewState.pendingItems[idx] = taskToUpdate;
      updated = true;
    }
    idx = previewState.penalties.findIndex((t) => t.key === taskToUpdate.key);
    if (idx > -1) {
      previewState.penalties[idx] = taskToUpdate as PenaltyBasketItem;
      updated = true;
    }
    if (!updated) return;
    if (previewState && alreadyPreviewed)
      sendPreviewMessageToMember(this, this.getControl(), previewState, true);

    // reason we do this is that we are NOT in the user session and we want to force the state update on the user session
    // the key part is "omit integrity" check
    const tempState: Record<string, string> = {};
    tempState[STATE_KEY_PREVIEW_BASKET] = encrypt(previewState);
    void this.database.replaceUserState(
      this.controlRecord.userid,
      tempState,
      this.sessionId,
      true
    );
  };

  public updateUnavailableDays = (days: EventDay[]): void => {
    this.updateState(STATE_KEY_UNAVAILABLE_DAYS, days);
  };

  public resetTaskState = (info: ITaskBasketInfo): void => {
    this.taskSchedule = info;
    this.updateState(STATE_KEY_CREDITS, info);
  };

  public updateTaskState = (info: ITaskTimerInfo | IPenaltyTimerInfo): void => {
    this.taskSchedule[info.key] = info;
    this.updateState(STATE_KEY_CREDITS, this.taskSchedule);
  };

  public deleteTaskState = (key: string): void => {
    delete this.taskSchedule[key];
    this.updateState(STATE_KEY_CREDITS, this.taskSchedule);
  };

  public resetPermissionState = (info: IPermissionBasketInfo): void => {
    this.permissionSchedule = info;
    this.updateState(STATE_KEY_PERMISSIONS, info);
  };

  public updatePermissionState = (info: IPermissionTimerInfo): void => {
    this.permissionSchedule[info.key] = info;
    this.updateState(STATE_KEY_PERMISSIONS, this.permissionSchedule);
  };

  public deletePermissionState = (key: string): void => {
    delete this.permissionSchedule[key];
    this.updateState(STATE_KEY_PERMISSIONS, this.permissionSchedule);
  };

  public resetSchedulerState = (info: ISchedulerTimerInfo[]): void => {
    this.eventSchedule = info;
    this.updateState(STATE_KEY_SCHEDULER, info);
  };

  public replaceSchedulerState = (info: ISchedulerTimerInfo[]): void => {
    this.eventSchedule = info;
    this.updateState(STATE_KEY_SCHEDULER, info);
  };

  public updateSchedulerState = (item: ISchedulerTimerInfo): void => {
    const idx = this.eventSchedule.findIndex((i) => i.key === item.key);
    if (idx === -1) this.eventSchedule.push(item);
    else this.eventSchedule[idx] = item;
    this.updateState(STATE_KEY_SCHEDULER, this.eventSchedule);
  };

  public deleteSchedulerState = (itemKey: string): void => {
    const idx = this.eventSchedule.findIndex((i) => i.key === itemKey);
    if (idx === -1) return;
    this.eventSchedule.splice(idx, 1);
    this.updateState(STATE_KEY_SCHEDULER, this.eventSchedule);
  };

  public updatePositionState = (info: IPositionBasketInfo): void => {
    this.updateState(STATE_KEY_POSITIONS, info);
  };

  public updateMetronomeState = (info: IPositionBasketInfo): void => {
    this.updateState(STATE_KEY_METRONOME, info);
  };

  public updateSiteEffectsState = (info: IPositionBasketInfo): void => {
    this.updateState(STATE_KEY_SITE_EFFECTS, info);
  };

  public updateCurrentDay = (day: string): void => {
    this.updateState(STATE_KEY_CURRENT_DAY, day);
  };
  public updateProofRequests = (count: number): void => {
    this.updateState(STATE_KEY_PROOF_REQUESTS, count);
  };

  public updateLastRunDate = (item: ISettingsItem): void => {
    let lookup = this.getState<Record<number, Record<string, Date>>>(
      STATE_KEY_LAST_RUN_DATES,
      {}
    );
    if (!lookup || !isRecord(lookup)) lookup = {};
    if (!lookup[item.itemType]) lookup[item.itemType] = {};
    lookup[item.itemType][item.key] = new Date();
    if (item instanceof TaskBasketItem || item instanceof PenaltyBasketItem) {
      if (item.selectedItems) {
        Object.keys(item.selectedItems).forEach((key) => {
          if (!lookup[ItemTypes.task]) lookup[ItemTypes.task] = {};
          lookup[ItemTypes.task][key] = new Date();
        });
      }
    }
    this.updateState(STATE_KEY_LAST_RUN_DATES, lookup);
  };

  public getPreviousItemSelectionState = (): Record<string, string[]> => {
    return this.getState(STATE_KEY_PREVIOUS_ITEM_SELECTIONS, {});
  };

  public updatePreviousItemSelections = (
    key: string,
    vals: string[],
    add: boolean
  ): void => {
    let lookup = this.getState<Record<string, any[]>>(
      STATE_KEY_PREVIOUS_ITEM_SELECTIONS,
      {}
    );
    let update = false;
    if (!lookup || !isRecord(lookup)) {
      lookup = {};
      update = true;
    }
    let list = lookup[key];
    if (add) {
      if (!list) {
        list = [...vals];
        update = true;
      } else {
        for (let i = 0; i < vals.length; i += 1) {
          const val = vals[i];
          if (list.indexOf(val) === -1) {
            list.push(val);
            update = true;
          }
        }
      }
    } else {
      if (list) {
        for (let i = 0; i < vals.length; i += 1) {
          const val = vals[i];
          const idx = list.indexOf(val);
          if (idx > -1) {
            list.splice(idx, 1);
            update = true;
          }
        }
      }
    }
    if (update) {
      lookup[key] = list;
      this.updateState(STATE_KEY_PREVIOUS_ITEM_SELECTIONS, lookup);
    }
  };

  public updateReportComplete = (complete: boolean): void => {
    this.updateState(STATE_KEY_REPORTCOMPLETE, complete);
  };

  public updateMorningReportDialog = (
    dialogState: IReportRequest | false
  ): void => {
    this.updateState(STATE_KEY_MORNINGREPORT, dialogState);
    this.fireListeners();
  };

  private proofItemsPending = () => {
    const state = this.getProofRequest();
    const schedule = this.getTaskSchedule();
    return (
      state &&
      state.items.filter((i) => schedule[i.key] && !schedule[i.key].isPaused)
    );
  };

  public getNextProofItem = () => {
    const pending = this.proofItemsPending();
    if (!pending) return undefined;
    return pending.length > 0 ? pending[0] : undefined;
  };

  public updateProofDialog = (
    item: ITaskProofRequestItem | IPenaltyProofRequestItem | undefined,
    add: boolean
  ): void => {
    // Weekly numeric targets require structured proof submission so owners can
    // review progress and set the next target; do not suppress this dialog.
    const isWeeklyTargetProof =
      !!item &&
      item.itemType === ItemTypes.task &&
      TaskItem.IsWeeklyNumericTargetTask(item as unknown as ITaskItem);
    const ignoreDialog = !isWeeklyTargetProof && !this.canHandleDiscordProofCalls();
    let state = this.getProofRequest();
    if (!state) state = { open: add, items: [] };
    if (item && !ignoreDialog) {
      if (add) {
        if (!state.items) state.items = [];
        state.items.push(item);
      } else {
        const idx = state.items.findIndex((i) => i.key === item.key);
        if (idx > -1) state.items.splice(idx, 1);
      }
      state.open = state.items.length > 0;
    } else {
      state = { open: false, items: [] };
    }
    this.updateState(STATE_KEY_PROOF_REQUEST_CAPTURE, state);
  };

  public updateLinesDialog = (dialogState: ILinesRequest | boolean): void => {
    this.updateState(STATE_KEY_LINES, dialogState);
  };

  public updateRouletteDialog = (
    dialogState: IRouletteRequest | boolean
  ): void => {
    this.updateState(STATE_KEY_ROULETTE, dialogState);
  };

  public updateReportDialog = (dialogState: IReportRequest | boolean): void => {
    this.updateState(STATE_KEY_DAILYREPORT, dialogState);
    this.fireListeners();
  };

  public updateRequestLockDialog = (
    dialogState: IRequestLockState | boolean
  ): void => {
    this.updateState(
      STATE_KEY_REQUEST_LOCK,
      canHandleDiscordCalls(this.getConfig(), this.getControl().username)
        ? dialogState
        : false
    );
  };

  public closeDialogs = () => {
    const tempState: Record<string, any> = {};
    tempState[STATE_KEY_DAILYREPORT] = false;
    tempState[STATE_KEY_ROULETTE] = false;
    tempState[STATE_KEY_LINES] = false;
    tempState[STATE_KEY_MORNINGREPORT] = false;
    tempState[STATE_KEY_REQUEST_LOCK] = false;
    tempState[STATE_KEY_PROOF_REQUEST_CAPTURE] = encrypt({
      open: false,
      items: [],
    });
    void this.database.replaceUserState(
      this.controlRecord.userid,
      tempState,
      this.sessionId,
      true
    );
  };

  public closeCaptureDialog = () => {
    this.closeDialogs();
    const tempState: Record<string, any> = {};
    tempState[STATE_KEY_PROOF_REQUEST_CAPTURE] = encrypt({
      open: false,
      items: [],
    });
    void this.database.replaceUserState(
      this.controlRecord.userid,
      tempState,
      this.sessionId,
      true
    );
    this.showMessage({
      message: "Dialogs Close Sent",
      notificationType: NotificationType.General,
    });
  };

  /**
   * updates the queue of items from the owner to update on the member session
   * called from controller traditionally on the owner session and then picked up
   * per minute on the member session when the pending items change, these can be task additions,
   * removals and event releases, permission changes and also reset session events from admin
   * @param items the new pending items for the user to pick up
   * @param fromOwner is this from system or the actual owner
   */
  public updatePendingItems = (
    item: IPendingItem,
    isRemove?: boolean
  ): void => {
    const idx = this.pendingState.indexOf(item);
    if (isRemove) {
      if (idx > -1) this.pendingState.splice(idx, 1);
      else
        this.auditUser({
          action: "Unable to find pending item to remove",
          id: v1(),
          level: TraceLevel.error,
          localeTime: new Date().toLocaleString(),
          name: "Pending Items",
          time: new Date(),
          type: AuditActionTypes.Session,
        });
    } else {
      if (idx > -1) this.pendingState[idx] = item;
      else this.pendingState.push(item);
    }
    void this.database.updatePendingState(
      this.controlRecord.userid,
      encrypt(this.pendingState)
    );
  };
  public clearPreviewDayPendingItems = (): void => {
    // we only want to keep pending adds for today which are after the start of day so that they can still happen at set times in the day.
    // they should pick up the unpacked state for their options
    this.pendingState = this.pendingState.filter(
      (p) =>
        p.updateType === PendingUpdateType.add &&
        p.date &&
        isAfter(new Date(p.date), startOfToday())
    );
    void this.database.updatePendingState(
      this.controlRecord.userid,
      encrypt(this.pendingState)
    );
  };

  public updateEnableSoundState = (enabled: boolean): void => {
    this.updateState(STATE_KEY_ENABLE_SOUNDS, enabled);
  };

  public updateActiveSessionState = (key: string) => {
    this.updateState(STATE_KEY_ACTIVE_SESSION_NAME, key);
  };

  public updateCommunityState = (filters: any) => {
    this.updateState(STATE_KEY_COMMUNITY_FILTERS, filters);
  };

  public updateSessionViewState = (args: {
    showCards: boolean;
    activeFirst: boolean;
  }) => {
    this.updateState(STATE_KEY_SESSION_VIEW, args);
  };

  public updateUserName = (username: string) => {
    if (!this.controlRecord) return;
    void this.database.updateUserName(this.controlRecord.userid, username);
  };

  public getPilloriesComplete = () => {
    return this.pillories.filter(
      (p) =>
        p.userid === this.controlRecord.userid &&
        p.hasEnded() &&
        (!p.item ||
          p.item.hidden ||
          p.item.isDeleted ||
          this.canAddTask(p.item).isOk)
    ); // should only include those pillories able to complete today
  };

  public getMyTaskLocks = (): ILockReceipt[] => {
    const vals = this.taskLocks[this.controlRecord.userid];
    if (!vals) return [];
    return Object.values(vals);
  };

  public getMyChastityTaskLocks = (): ILockReceipt[] => {
    const retTaskLocks: ILockReceipt[] = this.getMyTaskLocks();
    return retTaskLocks.filter(
      (l) => this.getTask(l.taskKey)?.creditTaskType === TaskType.chastity
    );
  };

  public getMyProofRequests = (): string[] => {
    return Object.keys(this.proofRequests);
  };

  public getOwnerChastityTaskLocksForUser = (
    userid: string
  ): ILockReceipt[] => {
    const retTaskLocks: ILockReceipt[] = this.getOwnerChastityTaskLocks();
    return retTaskLocks.filter((l) => l.userid === userid);
  };

  protected override handleAllControlsChanged(record: ControlRecord): void {
    super.handleAllControlsChanged(record);
    this.onAllControlsChangedForLocks(record);
  }

  protected override onAfterSystemUserIdLoaded(): void {
    // If the system user ID resolved after control records were already processed,
    // isSystemLoggedOn() was false during those callbacks and no member lock
    // subscriptions were set up. Re-process all known records now so the locks
    // page shows data for system admins.
    if (!this.isSystemLoggedOn()) return;
    Object.values(this.allControlRecords ?? {}).forEach((record) => {
      this.onAllControlsChangedForLocks(record);
    });
  }

  private onAllControlsChangedForLocks = (record: ControlRecord): void => {
    if (
      record &&
      (this.isSystemLoggedOn() || record.ownerHasAccess(this.controlRecord)) &&
      record.userid !== this.controlRecord.userid
    ) {
      if (!this.taskLocks[record.userid]) {
        this.taskLocks[record.userid] = {};
        // subscribe to this persons locks if owns them
        this.database.snapAndSubTaskLocks(
          record.userid,
          this.onTaskLocksChanged
        );
      }
    }
  };

  public getOwnerChastityTaskLocks = (): ILockReceipt[] => {
    let retTaskLocks: ILockReceipt[] = [];
    if (this.isSystemLoggedOn()) {
      Object.values(this.taskLocks).forEach(
        (user) => (retTaskLocks = retTaskLocks.concat(Object.values(user)))
      );
    } else {
      const ownedSessions = this.getOwnedSessions();
      Object.values(this.taskLocks).forEach(
        (user) =>
        (retTaskLocks = retTaskLocks.concat(
          Object.values(user).filter(
            (u) => ownedSessions.findIndex((s) => s.userid === u.userid) > -1
          )
        ))
      );
    }
    return retTaskLocks.filter(
      (l) => this.getTask(l.taskKey)?.creditTaskType === TaskType.chastity
    );
  };

  public getOwnerTaskLocks = (): ILockReceipt[] => {
    let retTaskLocks: ILockReceipt[] = [];
    if (this.isSystemLoggedOn()) {
      Object.values(this.taskLocks).forEach(
        (user) => (retTaskLocks = retTaskLocks.concat(Object.values(user)))
      );
    } else {
      const ownedSessions = this.getOwnedSessions();
      Object.values(this.taskLocks).forEach(
        (user) =>
        (retTaskLocks = retTaskLocks.concat(
          Object.values(user).filter(
            (u) => ownedSessions.findIndex((s) => s.userid === u.userid) > -1
          )
        ))
      );
    }
    return retTaskLocks;
  };

  public getOwnerVoteTasks = (): VoteTask[] => {
    if (this.isSystemLoggedOn()) {
      return this.getVotes();
    } else {
      const ownedSessions = this.getOwnedSessions();
      return this.getVotes().filter((vote) =>
        ownedSessions.find((s) => s.userid === vote.userid)
      );
    }
  };

  public getOwnerPillories = (): IPilloryTopic[] => {
    if (this.isSystemLoggedOn()) {
      return this.getPillories();
    } else {
      const allPills = this.getPillories();
      const ownedSessions = this.getOwnedSessions();
      return allPills.filter((p) =>
        ownedSessions.find((s) => s.userid === p.userid)
      );
    }
  };
  public getPilloriesForUser = (userid: string): PilloryTopic[] => {
    const allPills = this.getPillories();
    return allPills.filter((p) => p.userid === userid);
  };

  public getPillories = () =>
    this.pillories.filter(
      (p) =>
        this.getAllControlRecords().find((c) => c.userid === p.userid) !==
        undefined
    );

  public votePilloryUp = async (
    pillory: PilloryTopic,
    option?: string
  ): Promise<void> => {
    const hasError = pillory.voteUp(
      this.controlRecord.userid,
      this.cookies,
      option
    );
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Vote Task",
        `user voted task ${pillory.name} up for ${pillory.username}${option ? " using " + option : ""
        }`
      )
    );
    await this.database.updatePilloryTopic(pillory);
    this.subscriptionManager.fireListenersSync(ListenerType.Pillories);
    handleDiscordPilloryTickerUp(this, pillory);
  };

  public votePilloryDown = async (pillory: PilloryTopic, isOwner?: boolean): Promise<void> => {
    const hasError = pillory.voteDown(this.controlRecord.userid, this.cookies, isOwner);
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Vote Task",
        `user voted task ${pillory.name} down for ${pillory.username}`
      )
    );
    await this.database.updatePilloryTopic(pillory);
    this.subscriptionManager.fireListenersSync(ListenerType.Pillories);
    handleDiscordPilloryTickerDown(this, pillory);
  };

  public endPillory = async (pillory: PilloryTopic): Promise<void> => {
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Pillory",
        `user ended pillory for ${pillory.username}`
      )
    );
    pillory.pilloryEndTime = new Date().toISOString();
    await this.database.updatePilloryTopic(pillory);
  };

  /**
   * This ends the vote, as it is, and not ends the task itself
   * @param vote
   */
  public endVote = (vote: VoteTask): void => {
    this.clearCompleteVoteTasks([vote]);
  };

  public getVotesForUser = (userid: string): VoteTask[] => {
    const allVotes = this.getVotes();
    return allVotes.filter((p) => p.userid === userid);
  };
  public getVotes = () =>
    this.votingTasks.filter((v) =>
      this.getAllControlRecords().find((p) => p.userid === v.userid)
    );

  public voteUpdate = (config: ITaskVoteConfig): void => {
    const voteMatches = this.getVotesForUser(this.controlRecord.userid).filter(
      (v) => v.key === config.taskKey
    );
    if (!voteMatches || voteMatches.length === 0) return;
    voteMatches.forEach((vote) => {
      void this.auditUser(
        new UserActionAuditMessage(
          AuditActionTypes.Session,
          "Vote Update",
          `Task Vote for ${vote.name} updated ${config.isUp ? "up" : "down"
          } for ${vote.username} by ${convertToCountTypeString(
            config.voteIncrement,
            config.countType
          )}`
        )
      );
      vote.voteTaskUpdate(config);
      void this.database.updateVoteTask(vote);

      this.subscriptionManager.fireListenersSync(ListenerType.VotingTasks);
      handleDiscordVoteTickerRoulette(this, vote, config);
    });
  };

  public voteTaskUp = (vote: VoteTask): void => {
    let hasError;
    if (vote.lovenseToyId) {
      const { lovenseProfile, voteCount, lovenseToyId, getVoter } = vote;
      const voter = getVoter(this.controlRecord.userid);
      const multiplier = voter ? voter + 1 : 1;
      const cmd = new LovenseCommand(
        import.meta.env.VITE_LOVENSE_API_KEY as string,
        vote.userid,
        lovenseToyId,
        (voteCount || 1) * 60 * multiplier,
        "Preset",
        lovenseProfile
      );
      void pushLovense(cmd);
      hasError = vote.nudge(this.controlRecord.userid);
    } else {
      hasError = vote.voteUp(this.controlRecord.userid, this.cookies);
    }
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }

    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Vote Task",
        `user voted task ${vote.name} up for ${vote.username}`
      )
    );
    void this.database.updateVoteTask(vote);

    this.subscriptionManager.fireListenersSync(ListenerType.VotingTasks);
    handleDiscordVoteTickerUp(this, vote);
  };

  public testToy = (id: string) => {
    const cmd = new LovenseCommand(
      import.meta.env.VITE_LOVENSE_API_KEY as string,
      this.getControl().userid,
      id,
      10,
      "Preset",
      LovensePresetName.pulse
    );
    void pushLovense(cmd);
  };

  public voteTaskDown = (vote: VoteTask, isOwner?: boolean): void => {
    const hasError = vote.voteDown(
      this.controlRecord.userid,
      this.cookies,
      isOwner
    );
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    } else {
      void this.auditUser(
        new UserActionAuditMessage(
          AuditActionTypes.Session,
          "Vote Task",
          `user voted task ${vote.name} down for ${vote.username}`
        )
      );
      void this.database.updateVoteTask(vote);
    }
    this.subscriptionManager.fireListenersSync(ListenerType.VotingTasks);
    handleDiscordVoteTickerDown(this, vote);
  };

  public clearCompletePillories = (pilloriesToComplete: PilloryTopic[]) => {
    pilloriesToComplete.forEach((p) => {
      if (!this.pillories.includes(p)) return;
      this.pillories.splice(this.pillories.indexOf(p), 1);
    });
    this.database.clearCompletePillories(pilloriesToComplete);
    this.subscriptionManager.fireListenersSync(ListenerType.Pillories);
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Cleared completed pillories",
        `${pilloriesToComplete.length} deleted`
      )
    );
  };

  public clearCompleteVoteTasks = (votesToComplete: VoteTask[]) => {
    votesToComplete.forEach((v) => {
      if (!this.votingTasks.includes(v)) return; // only let the user delete their own voting tasks
      // need to do it this way as delete the entire node doesn't update onchange
      this.votingTasks.splice(this.votingTasks.indexOf(v), 1);
    });
    this.database.clearCompleteVoteTasks(votesToComplete);

    this.subscriptionManager.fireListenersSync(ListenerType.VotingTasks);

    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Cleared completed vote tasks",
        `${votesToComplete.length} deleted`
      )
    );
  };

  public clearCompleteTaskLocks = (taskLocks: ILockReceipt[]) => {
    taskLocks.forEach((t) => {
      const record = this.taskLocks[t.userid];
      if (record) delete record[t.imageKey];
    });
    this.database.clearPausedTaskLocks(taskLocks);
    this.subscriptionManager.fireListenersSync(ListenerType.TaskLocks);
    void this.auditUser(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        "Cleared completed task locks",
        `${taskLocks.length} deleted`
      )
    );
  };

  public sendUnlockRequest = (receipt: ILockReceipt, paused?: boolean) => {
    if (!this.taskLocks) return;
    if (!this.taskLocks[receipt.userid]?.[receipt.imageKey]) return;
    if (!paused) delete this.taskLocks[receipt.userid][receipt.imageKey];
    const message = paused
      ? `Task Pause sent for ${receipt.taskName} to release task lock image`
      : `Task End sent for ${receipt.taskName} to release task lock image`;
    const title = "Cleared completed task locks";
    void this.auditUser(
      new UserActionAuditMessage(AuditActionTypes.Session, title, message)
    );
    Emitter.emitNotification({
      message,
      notificationType: NotificationType.ScheduleInfo,
    });
    // Fire Discord notification fire-and-forget — do not wait for its callback.
    // The dialog is shown immediately below using the code already on the receipt.
    handleDiscordPausedLockTicker(this, paused ?? false, receipt);
    // Show the unlock/hygiene dialog immediately without waiting for the cloud
    // function to write isComplete back to Firebase.
    // Only show the dialog to the member whose lock it is — not to the owner/admin
    // who triggered the release on their behalf.
    if (this.controlRecord.userid === receipt.userid) {
      this.showLockReceiptDialog(receipt, paused ?? false);
    }
  };

  public sendPauseLockRequest = (receipt: ILockReceipt): void => {
    return this.sendUnlockRequest(receipt, true);
  };

  public editOwnedItems = async (
    item: ITaskItem,
    owned: boolean
  ): Promise<void> => {
    if (!this.ownedItems) this.ownedItemsCache = {};
    const didOwn = this.ownedItems[item.key] !== undefined;
    const ownedItems = cloneObject(this.ownedItems) as OwnedItemsCollection;
    if (!owned) delete ownedItems[item.key];
    else ownedItems[item.key] = item.options ?? [];
    this.auditService.auditOwnerItemAction(
      "Owned items Updated",
      diffObjects(this.ownedItems, ownedItems)
    );
    this.ownedItemsCache = ownedItems;
    await this.database.updateOwnedItem(this.controlRecord.userid, item, owned);
    handleDiscordOwnedItemsTicker(this, item, owned, didOwn);
  };

  /**
   * Applies a single settings item update received via pending state from an owner session.
   * Does not write back to the DB — the owner session already wrote it.
   */
  public applyOwnerSettingsItem(item: ISettingsItem): void {
    super.updateSettingsItem(item, false, true, true);
    this.fireListeners();
  }

  public updateBasketItemImage(editItem: ISettingsItem): boolean {
    const basket = this.taskSchedule ?? this.getState<ITaskBasketInfo>(STATE_KEY_CREDITS, {});
    if (!basket[editItem.key]) return false;
    basket[editItem.key] = {
      ...basket[editItem.key],
      img: editItem.img,
      imgId: undefined,
    };
    this.taskSchedule = basket;
    this.updateState(STATE_KEY_CREDITS, this.taskSchedule);
    return true;
  }

  public updateSettingsItem(
    editItem: ISettingsItem,
    addToOwner?: boolean,
    bypassSettingOwnerCheck?: boolean
  ): boolean {
    const isNew = !editItem.key;
    if (
      super.updateSettingsItem(editItem, addToOwner, bypassSettingOwnerCheck)
    ) {
      if (isNew && addToOwner) {
        // this is a new item
        if (
          this.getControl().isOwned() &&
          this.isSessionOwnedByLoggedOnUser()
        ) {
          if (this.ownerSettings) {
            // Also save to ownerSettings (the actual owner's profile settings) so the
            // owner's profile reflects the new item — distinct from what the base class
            // saves to memberOwnerProfileSettings.
            this.saveOwnerProfileSettings(
              this.getOwnerProfile(
                this.controlRecord.getOwner().userid
              ) as IOwnerProfileSession,
              this.ownerSettings
            );
          }
        }
      }
      this.auditSettingChange(editItem, isNew);
      return true;
    }
    return false;
  }

  private auditSettingChange = (item: ISettingsItem, isNew: boolean): void => {
    if (!item || this.isSystemLoggedOn()) return;

    if (isNew) {
      sendMessageToSystem(
        MessageType.NewItemCreated,
        this,
        `New ${getItemTypeDisplayName(
          item.itemType
        )} Settings Addition by owner ${this.cookies.username} - ${item.name
        }. ${item.description} `,
        `New ${getItemTypeDisplayName(item.itemType)} - ${item.name} `
      );
    }

    const current = this.getCurrentItem(item);
    if (this.controlRecord.isOwned()) {
      if (
        current &&
        current.availabilityDayType !== AvailabilityDayType.FreeDays &&
        item.availabilityDayType === AvailabilityDayType.FreeDays
      ) {
        sendMessageToSelf(
          MessageType.WorkItemChanged,
          this,
          `${getItemTypeDisplayName(item.itemType)} ${item.name
          } has been updated to be a task which can apply on any day by your owner ${this.controlRecord.getOwner().username
          }. You no longer need to have this item all days.`,
          `${getItemTypeDisplayName(item.itemType)} ${item.name
          } Settings have been updated`
        );
      } else if (
        current &&
        current.availabilityDayType === AvailabilityDayType.FreeDays &&
        item.availabilityDayType !== AvailabilityDayType.FreeDays
      ) {
        sendMessageToSelf(
          MessageType.WorkItemChanged,
          this,
          `${getItemTypeDisplayName(item.itemType)} ${item.name
          } has been updated to be a free day task by your owner ${this.controlRecord.getOwner().username
          }. Ensure you have the item available on all days.`,
          `${getItemTypeDisplayName(item.itemType)} ${item.name
          } Settings have been updated`
        );
      } else if (
        !current &&
        item.availabilityDayType !== AvailabilityDayType.FreeDays
      ) {
        // sendMessageToSelf(
        //   MessageType.NewItemCreated,
        //   this,
        //   `${getItemTypeDisplayName(item.itemType)} ${item.name
        //   } has been created by your owner ${this.controlRecord.getOwner().username
        //   }. It is an anyday task. The for the task are required on all days.`,
        //   `${getItemTypeDisplayName(item.itemType)} ${item.name
        //   } Settings have been updated`
        // );
      } else if (!current) {
        // sendMessageToSelf(
        //   MessageType.NewItemCreated,
        //   this,
        //   `${getItemTypeDisplayName(item.itemType)} ${item.name
        //   } has been created by your owner ${this.controlRecord.getOwner().username
        //   }.`,
        //   `${getItemTypeDisplayName(item.itemType)} ${item.name
        //   } Settings have been updated`
        // );
      } else {
        // sendMessageToSelf(
        //   MessageType.WorkItemChanged,
        //   this,
        //   `${getItemTypeDisplayName(item.itemType)} ${item.name
        //   } has been modified by owner ${this.cookies.username}. ${item.description
        //   } `,
        //   `${getItemTypeDisplayName(item.itemType)} ${item.name
        //   } Settings have been updated`
        // );
      }
    }
  };

  public saveDailyScore = (score: number, currentPenalties: number) => {
    void this.database.saveDailyScore(
      this.getControl().userid,
      score,
      this.getBankBalance(),
      currentPenalties
    );
    this.updateControlAttributes({
      totalScore: (this.controlRecord.totalScore ?? 0) + score,
      totalPenalties:
        (this.controlRecord.totalPenalties ?? 0) + currentPenalties,
      // penaltyPoints: currentPenalties, don't do this as it mucks up penalties in progress. we will update the penalties when we spend them
      lastPenalties: currentPenalties,
      lastScore: score,
    });
  };

  /**
   * Not all members can update their profile so we need to check if the member can edit there own items
   * or just take the items from their owner.
   * @returns can the new items list dialog appear
   */
  public canUpdateNewItems = (): boolean => {
    return (
      this.getCurrentCommunityProfile().role === ProfileRole.Owner ||
      (this.getCurrentCommunityProfile().role === ProfileRole.Switch &&
        this.isAdmin()) ||
      this.isSystemLoggedOn() ||
      (!this.isLocked() && this.getControl().isOwnedBySelf())
    );
  };

  public getProfileSettingInstance = () => {
    let userid = getOwnerUserId(this.controlRecord.userid);
    if (this.isSystemLoggedOn()) userid = this.systemUserId;
    if (!this.cachedProfileSettingInstance)
      this.cachedProfileSettingInstance = new ProfileSettings(
        this.database,
        userid,
        this.getOwnerProfileSettings(true) as ISessionSettings,
        this.getMemberOwnerProfileSummary() as IOwnerProfileSession,
        this.systemSettings,
        this.getConfig(),
        this.cookies,
        (settings) => this.updateOwnerProfileSettings(settings)
      );
    return this.cachedProfileSettingInstance;
  };

  public hasNewItems(): boolean {
    if (!this.canUpdateNewItems()) return false;
    return super.hasNewItems();
  }

  /**
   * this returns the right settings for the type of member logged on
   * either the owner profile settings for owners
   * the owner profile settings for a switch in admin mode
   * the member settings for a switch not in admin mode
   * or member settings for a member
   * system also returns member settings as system does not use owner profiles
   * @returns returns the correct type of settings that can then be edited accordingly
   */
  public getCurrentModeSettings = (): StatefulSettings | ProfileSettings => {
    if (this.isSystemLoggedOn()) {
      return this;
    }
    const role = this.getCurrentCommunityProfile().role;
    if (
      role === ProfileRole.Owner ||
      (role === ProfileRole.Switch && this.isAdmin())
    )
      return this.getProfileSettingInstance();
    return this;
  };

  public getBasketInProgress = (): (TaskBasketItem | PenaltyBasketItem)[] => {
    const basket = this.getBasketItems(false);
    const inProgress: (TaskBasketItem | PenaltyBasketItem)[] = [];
    basket.tasks.forEach((c) => {
      if (!c.isPaused && !c.isComplete) {
        inProgress.push(c);
      }
    });
    basket.penalties.forEach((p) => {
      if (!p.isPaused && !p.isComplete) {
        inProgress.push(p);
      }
    });
    return inProgress;
  };

  public getTasksCanPostpone = (): ISettingsItem[] => {
    const basket = this.getBasketItems(true);
    const inProgress: ISettingsItem[] = [];
    basket.pendingItems.forEach((p) => {
      if (p instanceof PenaltyBasketItem || p instanceof TaskBasketItem)
        inProgress.push(p);
    });
    return inProgress;
  };

  public updateOwnerProfileSettings = (settings: ISessionSettings) => {
    this.memberOwnerProfileSettings = settings;
    this.fireListeners();
  };

  public canShowSummary = (key: string): boolean => {
    const flags = this.getState<string[]>(STATE_KEY_SUMMARY_TEXT_FLAGS, []);
    return !flags.includes(key);
  };

  public toggleSummary = (key: string) => {
    const flags = this.getState<string[]>(STATE_KEY_SUMMARY_TEXT_FLAGS, []);
    if (flags.includes(key)) this.showSummary(key);
    else this.hideSummary(key);
  };

  public hideSummary = (key: string) => {
    const stateFlags = this.getState<string[]>(STATE_KEY_SUMMARY_TEXT_FLAGS, []);
    const flags = Array.isArray(stateFlags) ? [...new Set(stateFlags)] : [];
    if (!flags.includes(key)) flags.push(key);
    this.updateState(STATE_KEY_SUMMARY_TEXT_FLAGS, flags);
  };
  public showSummary = (key: string) => {
    const stateFlags = this.getState<string[]>(STATE_KEY_SUMMARY_TEXT_FLAGS, []);
    const flags = (Array.isArray(stateFlags) ? stateFlags : []).filter(
      (flag) => flag !== key
    );
    this.updateState(STATE_KEY_SUMMARY_TEXT_FLAGS, flags);
  };

  public sendAttendanceRequestToMember = (
    profile: ControlRecord,
    position: PositionItem,
    penalty: PenaltyItem,
    endTime: Date,
    holdDurationSec?: number
  ): Promise<void> => {
    return this.database.saveAttendanceRequest(
      profile,
      position.key,
      penalty.key,
      endTime,
      false,
      holdDurationSec
    );
  };

  public updateAttendanceRequest = (
    request: IAttendanceRequest
  ): Promise<void> => {
    return this.database.updateAttendanceRequest(
      this.getControl(),
      request.positionKey,
      request.penaltyKey,
      request.endTimeUtc,
      request.handled,
      request.fullScreen,
      request.lastMessage,
      request.holdDurationSec,
      request.releaseTimeUtc
    );
  };

  public acceptAttendanceRequest = (): void => {
    const request = this.getRequestAttendance();
    if (request) {
      const releaseTimeUtc = request.holdDurationSec && request.holdDurationSec > 0
        ? new Date(Date.now() + request.holdDurationSec * 1000).toUTCString()
        : request.releaseTimeUtc;
      void this.database.updateAttendanceRequest(
        this.getControl(),
        request.positionKey,
        request.penaltyKey,
        request.endTimeUtc,
        true,
        request.fullScreen,
        request.lastMessage,
        request.holdDurationSec,
        releaseTimeUtc
      );
    }
  };

  public cancelAttendanceRequest = (userid: string): void => {
    return this.database.cancelAttendanceRequest(userid);
  };

  public getRequestAttendance = () => this.attendanceRequests;

  public getAllRequestAttendance = (): (IAttendanceRequest & IControl)[] => {
    if (!this.allAttendanceRequests) return [];
    const ownedSessions = this.getOwnedSessions();
    return ownedSessions
      .map((s) => {
        if (!this.allAttendanceRequests) return;
        if (!this.allAttendanceRequests[s.userid]) return;
        return { ...this.allAttendanceRequests[s.userid], ...s };
      })
      .filter((s) => s) as (IAttendanceRequest & IControl)[];
  };

  public subscribeToUserImages = (
    cb: (imageSrc: string) => void,
    userid: string
  ): Unsubscribe => {
    return this.database.snapAndSubAttendanceRequestImages(userid, cb);
  };

  public addTaskToSchedule = (
    inputItem: ITaskItem,
    scheduleReason: string,
    sourceItem: EventItem | PermissionItem | undefined,
    inputOptions?: ItemAdditionOptions,
    bypassAdditionCheck?: boolean
  ): (TaskBasketItem | PenaltyBasketItem)[] | undefined => {
    const taskSchedule = this.getTaskSchedule();

    const existingItem = taskSchedule[inputItem.key];
    if (existingItem) {
      if ((existingItem as ITaskTimerInfo).permanent) {
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            inputItem.name,
            "Unable to add item as already permanently applied.",
            TraceLevel.warning
          )
        ); // already permanent
        return undefined;
      }
      if (!existingItem.isPaused && !existingItem.isComplete) {
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            inputItem.name,
            "Unable to add item as already scheduled for today.",
            TraceLevel.warning
          )
        ); // already underway
        return undefined;
      }
      this.handleRemoveItemFromBasket(inputItem); // let's remove the match we do have, as we now have a replacement
    }

    let result = this.canAddTask(inputItem);
    if (
      !result.isOk
      && result.reason === 'Unable to find the item to add'
      && sourceItem
    ) {
      // Event/permission completions can create synthetic acknowledgement items.
      // Validate against the real source item when the synthetic item key is not resolvable.
      result = this.canAddTask(sourceItem as unknown as ISettingsItem);
    }
    if (!bypassAdditionCheck && !result.isOk) {
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          inputItem.name,
          "Unable to add item requested. " + result.reason,
          TraceLevel.error
        )
      );
      return [];
    }

    const basketItems = this.createBasketItems(
      inputItem,
      this.getIsWeekend,
      scheduleReason,
      new Date(),
      inputOptions,
      sourceItem
    );

    basketItems.forEach((basketItem) => {
      this.updateTaskState(basketItem);
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "addTaskToSchedule",
          `${basketItem.name} added to task list`,
          TraceLevel.info
        )
      );
    });

    this.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        inputItem.name,
        "Task List Updated",
        TraceLevel.info
      )
    );
    return basketItems;
  };

  public addPenaltyToSchedule = (
    itemToAdd: IPenaltyItem,
    scheduleReason: string,
    sourceItem: EventItem | PermissionItem | undefined,
    additionOptions: ItemAdditionOptions | undefined,
    bypassAdditionCheck?: boolean
  ): (PenaltyBasketItem | TaskBasketItem)[] | null => {
    const taskSchedule = this.getTaskSchedule();
    const existingItem = taskSchedule[itemToAdd.key];
    if (existingItem) {
      if ((existingItem as ITaskTimerInfo).permanent) {
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            itemToAdd.name,
            "Unable to add item as already permanently applied.",
            TraceLevel.warning
          )
        ); // already permanent
        return null;
      }
      if (!existingItem.isPaused && !existingItem.isComplete) {
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            itemToAdd.name,
            "Unable to add item as already scheduled for today.",
            TraceLevel.warning
          )
        ); // already underway
        return null;
      }
      this.handleRemoveItemFromBasket(itemToAdd); // let's remove the match we do have, as we now have a replacement
    }

    const result = this.canAddTask(itemToAdd);
    if (!bypassAdditionCheck && !result.isOk) {
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          itemToAdd.name,
          "Unable to add item requested. " + result.reason,
          TraceLevel.error
        )
      );
      this.showMessage({
        message: `Unable to add ${itemToAdd.name} requested ${result.reason}`,
        notificationType: NotificationType.ErrorInfo,
      });
      return [];
    }

    const basketItems = this.createBasketItems(
      itemToAdd,
      this.getIsWeekend,
      scheduleReason,
      new Date(),
      additionOptions,
      sourceItem
    );

    if (basketItems && basketItems.length)
      basketItems.forEach((basketItem) => this.updateTaskState(basketItem));
    this.showMessage({
      message: "Penalty Added",
      notificationType: NotificationType.ScheduleInfo,
    });
    this.auditUser(
      new AuditMessage(
        AuditActionTypes.Penalty,
        itemToAdd.name,
        `Penalty Added to task list${additionOptions?.reason ? ` - ${additionOptions?.reason}` : ""
        }`,
        TraceLevel.info
      )
    );
    return basketItems;
  };

  // add an item to the task list and process it based on the type
  public handleRemoveItemFromBasket = (item: ISettingsItem): void => {
    if (item.itemType === ItemTypes.position) {
      const positionInfo = this.getPositionInfo();
      positionInfo.isComplete = true;
      positionInfo.isPaused = false;
      positionInfo.expectedCount = 0;
      positionInfo.completedTime = new Date();
      this.updatePositionState(positionInfo);
      this.showMessage({
        message: `Position ${item.name} removed by owner`,
        notificationType: NotificationType.ScheduleInfo,
      });
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          item.name,
          `Position ${item.name} removed by owner`,
          TraceLevel.info
        )
      );
    } else {
      const creditInfo = this.getTaskSchedule();
      if (creditInfo[item.key]) {
        this.deleteTaskState(item.key);
        this.showMessage({
          message: `Task ${item.name} removed by owner`,
          notificationType: NotificationType.ScheduleInfo,
        });
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            item.name,
            `Task ${item.name} removed by owner`,
            TraceLevel.info
          )
        );
      }
      const schedule = this.getScheduleInfo();
      const idx = schedule.findIndex((event) => event.key === item.key);
      if (idx > -1) {
        this.deleteSchedulerState(item.key);
        this.showMessage({
          message: `Event ${item.name} removed by owner`,
          notificationType: NotificationType.ScheduleInfo,
        });
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            item.name,
            `Event ${item.name} removed by owner`,
            TraceLevel.info
          )
        );
      }
      if ((item as EventBasketItem).isReport) this.updateReportComplete(true);

      const permissions = this.getPermissionInfo();
      if (permissions[item.key]) {
        this.deletePermissionState(item.key);
        this.showMessage({
          message: `Permission ${item.name} removed by owner`,
          notificationType: NotificationType.ScheduleInfo,
        });
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            item.name,
            `Permission ${item.name} removed by owner`,
            TraceLevel.info
          )
        );
      }
    }
  };

  // add an item to the task list and process it based on the type
  public handleUpdateBasket = (
    item: TaskBasketItem | PenaltyBasketItem,
    additionOptions: ItemAdditionOptions | undefined
  ): void => {
    const creditInfo = this.getTaskSchedule();
    if (creditInfo[item.key]) {
      this.updateTaskState({ ...item, ...(additionOptions ?? {}) });
      this.showMessage({
        message: `Task ${item.name} updated by owner`,
        notificationType: NotificationType.SettingsInfo,
      });
    }
    this.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        item.name,
        "Admin Update Item",
        TraceLevel.info
      )
    );
  };

  public hasFutureTasks = (): boolean => {
    const previewBasket = this.getPreviewBasket();
    return (this.isTomorrowAPreviewDay() &&
      previewBasket &&
      ((previewBasket.pendingItems && previewBasket.pendingItems?.length > 0)
        || (previewBasket.childTasks && Object.keys(previewBasket.childTasks).length > 0)
        || (previewBasket.possiblePenalties && previewBasket.possiblePenalties.length > 0)) ||
      this.getPendingState().filter(
        (t) => t.updateType === PendingUpdateType.add && t.item
      ).length > 0);
  };

  public saveCustomScheduledDay = (item: ICustomSchedule, day: EventDay) => {
    const settings = this.getSessionSettings();
    if (!settings.customScheduledDays) settings.customScheduledDays = {} as Record<EventDay, string>;
    if (settings.customScheduledDays[day] === item.key) {
      return;
    }
    settings.customScheduledDays[day] = item.key as string;
    void this.saveGeneralSettings(settings, undefined, true);
    const dateDay = convertToEventDayFromDate(addDays(new Date(), 1).getDay());
    if (this.isTomorrowAPreviewDay() && dateDay === day) {
      this.createPreview(true, true);
    }
  }

  public saveCustomScheduledDate = (item: ICustomSchedule, date: Date | string) => {
    item.scheduledDate = typeof date === "string" ? date : this.getDateScheduleKey(date);
    this.saveCustomSchedule(item);
  }

  public getScheduledCustomSchedule = (
    day: EventDay,
    date?: Date
  ): ICustomSchedule | undefined => {
    const pendingItems = this.getPendingState();
    const customDays = pendingItems.filter(
      (c) => c.updateType === PendingUpdateType.scheduleChange
    );
    if (date) {
      const dateKey = this.getDateScheduleKey(date);
      const scheduledDateKey = this.settings.customScheduledDates?.[dateKey];
      if (scheduledDateKey) {
        const dateSchedule = this.getCustomSchedule(scheduledDateKey);
        if (dateSchedule) return dateSchedule;
      }
      const strDate = formatValidDate(date, "dd MMM yyyy");
      if (customDays.length > 0) {
        const scheduled = customDays.find((c) => c.date === strDate);
        if (scheduled && scheduled.customScheduleKey)
          return this.getCustomSchedule(scheduled.customScheduleKey);
      }
    }

    if (
      this.settings.customScheduledDays
      && this.settings.customScheduledDays[day]
      && this.getCustomSchedule(this.settings.customScheduledDays[day])
    ) {
      return this.getCustomSchedule(this.settings.customScheduledDays[day]);
    }
    return undefined;
  };

  public generateScheduleForDay = (selectedDay: EventDay): ICustomSchedule => {
    const newSchedule: ICustomSchedule = {
      key: uuid(),
      name: `Settings Schedule for ${toTitleCase(EventDay[selectedDay])}`,
      events: [],
    };
    // determine a preview day for the day in question from the settings to obtain the events
    // include all the items that would normally apply on that day without availability constraints
    // as the availability will be applied when the schedule is applied on that day itself
    const daySchedule = this.getDaySessionSchedule(selectedDay, false, true, true);
    if (daySchedule) {
      newSchedule.events = daySchedule.events
        .filter(e => e.eventType === EventType.standard)
        .map(e => {
          const time = getScheduleTime(
            this,
            convertToEventDayFromDate(new Date().getDay()),
            e
          );
          const keys = e.taskKeys ? [...e.taskKeys] : [];
          if (keys.length === 0 && e.taskCategories && e.taskCategories.length > 0) {
            const categoryTasks = this.getTasks(
              (i) =>
                Array.isArray(i.categories) && Array.isArray(e.taskCategories)
                  ? i.categories.filter((element) => e.taskCategories!.includes(element)).length > 0
                  : false
            );
            const taskKey = this.getRandomTask(categoryTasks);
            if (taskKey) {
              keys.push(taskKey.key);
            }
          }
          return {
            ...e,
            key: `event-${e.name}-${uuid()}`,
            minTime: format(time, 'HH:mm'),
            maxTime: format(addMinutes(time, 1), 'HH:mm'),
            name: e.name.split('\n')[0], // strip any /n notes from name
            taskKeys: keys,
            itemOptions: {},
            isChoiceNamed: false,
          }
        });
      const convertedTasks = daySchedule.tasks
        .filter(t => !t.childTask)
        .map((task, idx) => {
          const specificTimeEvent = (!TaskItem.HasEndTriggerEvent(task as ITaskItem)
            && !TaskItem.IsTimed(task as ITaskItem))
            || TaskItem.IsSimpleRule(task as ITaskItem)
            || (task as ITaskItem).suppressScheduleTask;

          let minTime = getSODStartTime(this);

          if (!TaskItem.IsPermanentlyApplied(task as ITaskItem) && (task as ITaskItem).suppressScheduleTask) {
            minTime = format(addMinutes(addHours(asDate(getSODStartTime(this)), 4), idx * 15), 'HH:mm');
          }
          let maxTime = specificTimeEvent ? format(addMinutes(asDate(minTime), 1), 'HH:mm')
            : getLastScheduleOffsetTimeForDay(this, selectedDay);

          // if task key is in any proof required list for newSchedule.events then it needs to have maxTime set to be the event.minTime
          const proofEvent = newSchedule.events?.find(e => e.proofTaskKeys && e.proofTaskKeys.length > 0 && e.proofTaskKeys.includes(task.key));
          if (proofEvent && proofEvent.minTime) maxTime = proofEvent.minTime;

          const taskKeys = [task.key];
          return {
            ...task,
            taskKeys, // this event need to reference the task it's mimicking
            name: task.name.split('(')[0].split('\n')[0], // strip any /n notes from name
            minTime,
            // idea here is that if a simple rule with no count then fire it early for the day but if something
            // counted then give it a range
            maxTime,
            eventType: EventType.standard,
            availability: AvailabilityType.daily,
            disableAdd: false,
            hasOptions: false,
            optionAppliedChance: 1,
            itemType: ItemTypes.event,
            approvalChance: 1,
            key: `task-${task.name}-${uuid()}`,
            itemOptions: {},
            isChoiceName: false,
          } as ICustomScheduleEvent;
        });
      // then create events for each task that would normally apply daily during that day
      newSchedule.events = [...newSchedule.events, ...convertedTasks];
    }
    //strip out any with no tasks
    // newSchedule.events = newSchedule.events.filter(e => e.taskKeys && e.taskKeys.length > 0);
    return newSchedule;
  }

  public getTodos = (): ITodoItem[] => {
    const userTodos = this.todoCache.get();
    const systemTodos = this.getDefaultTodos();
    return systemTodos.concat(userTodos ?? []);
  };

  public getDefaultTodos = (): ITodoItem[] => {
    const todos: ITodoItem[] = [];
    const isAdmin = this.isAdmin();
    const ownerProfile = this.getMemberOwnerProfileSummary();
    const hasUnreadWelcomeMail = this.memberMessages.find(
      (m) => m.message.subject === "Welcome To Tarlings" && !m.read
    );
    const setAvailableDays =
      (!isAdmin &&
        this.getState<EventDay[] | undefined>(
          STATE_KEY_UNAVAILABLE_DAYS,
          undefined
        )) ||
      this.getState<EventDay[] | undefined>(
        STATE_KEY_WEEKEND_DAYS,
        undefined
      ) ||
      this.getState<EventDay[] | undefined>(STATE_KEY_WORK_DAYS, undefined) ||
      this.getState<EventDay[] | undefined>(STATE_KEY_PREVIEW_DAYS, undefined);
    const setOwnedItems =
      !isAdmin && Object.keys(this.getOwnedItems()).length > 0;
    const updatedProfile =
      !isAdmin &&
      this.controlRecord.summary !== undefined &&
      this.controlRecord.summary.length > 0;
    const updatedOwnerProfile =
      isAdmin &&
      ownerProfile &&
      ownerProfile.description !== undefined &&
      ownerProfile.description?.length > 0;

    if (hasUnreadWelcomeMail)
      todos.push({
        name: "Read your mails",
        targetPage: notifications_page,
        description: "Your mails contain instructions and tips",
        key: v1(),
        img: "https://previews.123rf.com/images/michaeljung/michaeljung1506/michaeljung150600181/40783649-happy-young-woman-reading-emails-on-laptop-computer-in-the-kitchen.jpg",
      } as ITodoItem);
    if (!isAdmin && !setAvailableDays)
      todos.push({
        name: "Update Your Diary",
        targetPage: editProfile_page,
        targetTab: "diary",
        description: "Your diary will determine what tasks on which day",
        key: v1(),
        img: "https://www.shutterstock.com/image-photo/enthusiastic-brunette-woman-diary-holding-260nw-2371202977.jpg",
      } as ITodoItem);
    if (!isAdmin && !setOwnedItems)
      todos.push({
        name: "Update Owned Items",
        targetPage: editProfile_page,
        targetTab: "items",
        description: "Your items will enable more tasks to be active",
        key: v1(),
        img: "https://i.imgur.com/Tyeuw5x.jpg",
      } as ITodoItem);
    if ((!isAdmin && !updatedProfile) || (isAdmin && !updatedOwnerProfile))
      todos.push({
        name: "Update Profile with a Summary",
        targetPage: editProfile_page,
        targetTab: "profile",
        description:
          "Your summary will tell the other members and owners more about you",
        key: v1(),
        img: "https://i.imgur.com/Tyeuw5x.jpg",
      } as ITodoItem);
    if (!isAdmin) {
      const shoppingRequests = this.getShoppingItems().filter((i) => !i.review);
      shoppingRequests.forEach((request) => {
        todos.push({
          name: `Purchase ${request.item.name}`,
          targetPage: home_page,
          targetTab: "shopping",
          description:
            "You have been requested to purchase this item. Visit the shopping tab to see more details.",
          key: v1(),
          imgId: request.item.imgId,
        } as ITodoItem);
      });
    }
    if (isAdmin) {
      const actionableMessages = this.getMemberMessages().filter(
        (m) => messageRequiresInteraction(m.message.messageType) && !m.read
      );
      actionableMessages.forEach((msg) => {
        todos.push({
          name: `Action Required for ${msg.message.fromUsername}`,
          description: `${msg.message.subject}`,
          targetPage: notifications_page,
          targetTab: msg.key,
          key: v1(),
        } as unknown as ITodoItem);
      });
    }
    return todos;
  };

  /** Lightweight summary cache (no subtype-specific data) — subscribed at startup. */
  private gamesCache = new CacheRecord<IGame>(
    "Games",
    this,
    this.database.updateGame,
    this.database.removeGame,
    this.database.snapAndSubGameSummaries,
    (item) => item.name
  );

  /** Full-detail cache (with subtype-specific data) — populated on demand. */
  private gameDetailCache: Record<string, IGame> = {};

  public getGames = (): IGame[] => {
    return this.gamesCache.get()
      .filter((g): g is IGame => g != null)
      .map(g => this.gameDetailCache[g.key] ?? g);
  };

  public getGame = (gameKey: string): IGame | undefined => {
    if (this.gameDetailCache[gameKey]) return this.gameDetailCache[gameKey];
    const match = this.gamesCache.get().find((g) => g?.key === gameKey);
    return match;
  };

  public hasGame = (game: IGame): boolean => {
    const match = this.gamesCache.get().filter((g) => g?.key === game.key);
    return match.length > 0;
  };

  public clearGame = (game: IGame): void => {
    delete this.gameDetailCache[game.key];
    this.gamesCache.remove(game);
  };

  public saveGame = (item: IGame): void => {
    // Cache full detail locally so it is immediately available
    this.gameDetailCache[item.key] = item;
    this.gamesCache.add(item);
  };

  /**
   * Fetch and cache the full game detail (with subtype-specific data) for a single key.
   * Returns cached detail on repeat calls without re-fetching.
   */
  public loadGameDetail = async (key: string): Promise<IGame | undefined> => {
    if (this.gameDetailCache[key]) return this.gameDetailCache[key];
    const detail = await this.database.fetchGameDetail(key);
    if (detail) {
      this.gameDetailCache[key] = detail;
      this.fireListeners();
      this.gamesCache.fireListeners();
    }
    return detail;
  };

  public subscribeGames = (cb: () => void): Unsubscribe => {
    return this.gamesCache.subscribeItems(cb);
  };

  private todoCache = new CacheRecord<ITodoItem>(
    "Todo",
    this,
    this.database.addTodo,
    this.database.removeTodo,
    this.database.snapAndSubTodo,
    (item) => item.name
  );

  public hasTodo = (todo: ITodoItem): boolean => {
    const match = this.todoCache.get().filter((t) => t.key === todo.key);
    return match.length > 0;
  };

  public clearTodo = (todo: ITodoItem): void => {
    this.todoCache.remove(todo);
  };

  public saveTodo = (item: ITodoItem): void => {
    this.todoCache.add(item);
  };

  public subscribeTodos = (cb: () => void): Unsubscribe => {
    return this.todoCache.subscribeItems(cb);
  };

  // ── Wizard Templates ──────────────────────────────────────────────────────

  private wizardTemplateCache = new CacheRecord<IWizardTemplate>(
    "Wizard Template",
    this,
    this.database.addWizardTemplate,
    this.database.removeWizardTemplate,
    this.database.snapAndSubWizardTemplates,
    (item) => item.name
  );

  public getWizardTemplates = (): IWizardTemplate[] => {
    return this.wizardTemplateCache.get();
  };

  public saveWizardTemplate = (item: IWizardTemplate): void => {
    this.wizardTemplateCache.add(item);
  };

  public deleteWizardTemplate = (item: IWizardTemplate): void => {
    this.wizardTemplateCache.remove(item);
  };

  public subscribeWizardTemplates = (cb: () => void): Unsubscribe => {
    return this.wizardTemplateCache.subscribeItems(cb);
  };

  /** Lightweight summary cache (no events) — subscribed at startup to keep downloads small. */
  private customScheduleCache = new CacheRecord<ICustomSchedule>(
    "Schedule",
    this,
    this.database.addCustomSchedule,
    this.database.removeCustomSchedule,
    this.database.snapAndSubCustomScheduleSummaries,
    (item) => item.name
  );

  /** Full-detail cache (with events) — populated on demand or for scheduled custom schedules. */
  private customScheduleDetailCache: Record<string, ICustomSchedule> = {};
  private customScheduleDetailLoads = new Set<string>();

  /** Published schedules from other users - fetched lazily, not at startup */
  private publishedSchedulesCache: Record<string, ICustomSchedule> = {};
  private publishedSchedulesFetched = false;

  /**
   * Load published schedules from other users on first call (e.g. when the schedule browser opens).
   * Subsequent calls are no-ops so the full tree is only pulled once per session.
   */
  public loadPublishedSchedules = async (): Promise<void> => {
    if (this.publishedSchedulesFetched) return;
    this.publishedSchedulesFetched = true;
    const published = await this.database.fetchPublishedSchedules(this.getControl().userid);
    this.publishedSchedulesCache = published;
    this.fireListeners();
  };

  public getCustomSchedule = (key: string): ICustomSchedule | undefined => {
    // Detail cache has events — check first
    if (this.customScheduleDetailCache[key]) return this.customScheduleDetailCache[key];
    const match = this.customScheduleCache.get().filter(s => s != null).find((s) => s.key === key);
    if (match) return match;
    // also check lazily-loaded published schedules (already have events)
    return this.publishedSchedulesCache[key];
  }

  /**
   * Fetch and cache the full detail (with events) for a single schedule key.
   * Returns the cached detail if already loaded, or fetches it from DB.
   */
  public loadCustomScheduleDetail = async (key: string): Promise<ICustomSchedule | undefined> => {
    // Already in detail cache
    if (this.customScheduleDetailCache[key]) return this.customScheduleDetailCache[key];
    // Find the summary to get the owner userid for the DB path
    const summary = this.customScheduleCache.get().find(s => s?.key === key)
      ?? this.publishedSchedulesCache[key];
    if (!summary?.userid) return undefined;
    const detail = await this.database.fetchCustomScheduleDetail(summary.userid, key);
    if (detail) {
      this.customScheduleDetailCache[key] = detail;
      this.fireListeners();
      this.customScheduleCache.fireListeners();
    }
    return detail;
  };

  public getCustomSchedules = (): ICustomSchedule[] => {
    const ownSummaries = this.customScheduleCache.get().filter((s): s is ICustomSchedule => s != null);
    const own = ownSummaries.map((schedule) => this.customScheduleDetailCache[schedule.key] ?? schedule);
    const published = Object.values(this.publishedSchedulesCache);
    // merge: published cache supplements own; dedup by key
    const merged = [
      ...own,
      ...published.filter(p => !own.some(o => o.key === p.key)),
    ];
    return merged.filter(s => s.published || s.userid === this.getControl().userid);
  };

  public saveCustomSchedule = (customSchedule: ICustomSchedule): void => {
    const existingSchedule = this.getCustomSchedule(customSchedule.key);
    const previousSchedule = existingSchedule ? cloneObject(existingSchedule) : undefined;
    customSchedule.events?.forEach((e) => {
      if (!e.approvalChance) e.approvalChance = 1;
      e.itemType = ItemTypes.event;
      if (!e.itemOptions) e.itemOptions = {};
      if (!e.taskKeys) e.taskKeys = [];
    });
    // Cache full detail locally when events are present
    if (customSchedule.events !== undefined) {
      this.customScheduleDetailCache[customSchedule.key] = customSchedule;
    }

    const scheduledDateKey = (customSchedule.scheduledDate ?? "").trim();
    if (scheduledDateKey) {
      const settings = this.getSessionSettings();
      if (!settings.customScheduledDates) settings.customScheduledDates = {};
      Object.entries({ ...settings.customScheduledDates }).forEach(([dateKey, scheduleKey]) => {
        if (scheduleKey === customSchedule.key && dateKey !== scheduledDateKey) {
          delete settings.customScheduledDates?.[dateKey];
        }
      });
      if (settings.customScheduledDates[scheduledDateKey] !== customSchedule.key) {
        settings.customScheduledDates[scheduledDateKey] = customSchedule.key;
        void this.saveGeneralSettings(settings, undefined, true);
      }
    }

    const scheduleDiff = previousSchedule
      ? diffObjects(previousSchedule, customSchedule)
      : undefined;
    if (scheduleDiff?.events && Object.keys(scheduleDiff.events).length > 0) {
      const linkedDayLabels = Object.entries(this.settings.customScheduledDays ?? {})
        .filter(([, scheduleKey]) => scheduleKey === customSchedule.key)
        .map(([day]) => toTitleCase(EventDay[Number(day) as EventDay] ?? day));
      const linkedDaysSuffix = linkedDayLabels.length > 0
        ? ` - ${linkedDayLabels.join(", ")}`
        : "";
      this.auditService.auditOwnerItemAction(
        `${this.getControl().username} - Schedule ${customSchedule.name}${linkedDaysSuffix} updated`,
        previousSchedule,
        customSchedule
      );
    }

    this.customScheduleCache.add(customSchedule, false, true);
    // determine if the schedule is applicable for tomorrow and if tomorrow is a preview day
    const dateDay = convertToEventDayFromDate(addDays(new Date(), 1).getDay());
    const selectedCustomSchedule = this.getScheduledCustomSchedule(dateDay, addDays(new Date(), 1));
    if (selectedCustomSchedule && selectedCustomSchedule.key === customSchedule.key) {
      this.createPreview(true, false); // force it to be resent from scratch as there could be many changes made to it by owner.
    }
  };

  /**
   * Evict a single entry from the detail cache, forcing a fresh fetch next time it is needed.
   * Used by the member scheduler when processing PendingUpdateType.createPreview so the preview
   * is regenerated from the latest Firebase data rather than a stale local snapshot.
   */
  public evictCustomScheduleDetailCache = (key: string): void => {
    delete this.customScheduleDetailCache[key];
  };

  public deleteCustomSchedule = (schedule: ICustomSchedule): void => {
    delete this.customScheduleDetailCache[schedule.key];
    this.customScheduleCache.remove(schedule);
    const settings = this.getSessionSettings();
    if (settings.customScheduledDates) {
      let changed = false;
      Object.entries({ ...settings.customScheduledDates }).forEach(([dateKey, scheduleKey]) => {
        if (scheduleKey === schedule.key) {
          delete settings.customScheduledDates?.[dateKey];
          changed = true;
        }
      });
      if (changed) {
        void this.saveGeneralSettings(settings, undefined, true);
      }
    }
    const dateDay = convertToEventDayFromDate(addDays(new Date(), 1).getDay());
    const selectedCustomSchedule = this.getScheduledCustomSchedule(dateDay, addDays(new Date(), 1));
    if (selectedCustomSchedule && selectedCustomSchedule.key === schedule.key) {
      this.createPreview(true, false); // force it to be resent from scratch as there could be many changes made to it by owner.
    }
  };

  public subscribeCustomSchedules = (cb: () => void): Unsubscribe => {
    return this.customScheduleCache.subscribeItems(cb);
  };

  private shoppingItemCache = new CacheRecord<IShoppingRequest>(
    "Messages",
    this,
    this.database.addShoppingItem,
    this.database.removeShoppingRequest,
    this.database.snapAndSubShoppingRequests,
    (item) => item.item.name
  );

  public addShoppingRequest = (
    request: IShoppingRequest,
    bypassMessage?: boolean
  ): void => {
    if (!request.key) request.key = uuid();
    if (!this.isSettingsOwner()) {
      request.review = true;
      sendUrlRequestToPurchase(request, this);
      this.showMessage({
        message: `Shopping Item ${request.item.name} requested`,
        notificationType: NotificationType.SettingsInfo,
      });
      void this.database.addShoppingItem(this.controlRecord.userid, request);
      this.auditService.auditUserAction(
        `Shopping Request ${request.item.name} created for review`,
        AuditActionTypes.Session
      );
      return;
    }
    request.review = false;
    this.shoppingItemCache.add(request);
    if (!bypassMessage) sendUrlRequestPurchase(request, this);
  };

  public removeShoppingItem = (request: IShoppingRequest): void => {
    this.shoppingItemCache.remove(request);
  };

  public subscribeShoppingRequests = (cb: () => void): Unsubscribe => {
    return this.shoppingItemCache.subscribeItems(cb);
  };

  public getShoppingItems = (): IShoppingRequest[] => {
    return this.shoppingItemCache.get();
  };

  private caches: IEntityCache[] = [
    this.todoCache,
    this.customScheduleCache,
    this.shoppingItemCache,
    this.gamesCache,
  ];

  public getSelectedSession = (): string => {
    return this.cookies.getState(COOKIE_KEY_SELECTED_SESSION_USER_ID);
  };
  public setSelectedSession = (userid: string) => {
    this.cookies.setState(COOKIE_KEY_SELECTED_SESSION_USER_ID, userid);
  };

  public isOwnerView = () => {
    return this.getControl().role === ProfileRole.Owner || this.isAdmin();
  };

  /**
   * Builds and shows the lock receipt dialog for a given receipt.
   * Called immediately on unlock/pause so the dialog opens without waiting
   * for the Firebase cloud function to write back isComplete/isPaused.
   * `paused` drives whether the member should capture a new lock image (hygiene
   * break start) or just view the code to open their lock (full unlock).
   */
  private showLockReceiptDialog = (receipt: ILockReceipt, paused: boolean): void => {
    // Only show the dialog to the member whose lock it is.
    if (receipt.userid !== this.controlRecord.userid) return;
    const generalSettings = this.getGeneralSettings();
    const timedLock =
      generalSettings.hygieneIntervalMins !== undefined &&
      generalSettings.hygieneIntervalMins > 0;
    const unlockStartTime = new Date();
    let unlockEndTime = new Date();
    let penalties: string[] = [];
    if (timedLock) {
      unlockEndTime = addMinutes(
        unlockStartTime,
        generalSettings.hygieneIntervalMins as number
      );
      if (generalSettings.hygienePenalties)
        penalties = generalSettings.hygienePenalties
          .map((key) => this.getPenalty(key)?.name)
          .filter((n) => n !== undefined) as any as string[];
      if (generalSettings.hygienePillories)
        penalties = penalties.concat(
          generalSettings.hygienePillories
            .map((key) => this.getPenalty(key)?.name)
            .filter((n) => n !== undefined) as any as string[]
        );
    }
    // taskSchedule may not contain the task if it has already completed and been cleaned up.
    // Construct a minimal fallback item from the receipt so the dialog can still open.
    const scheduleItem = this.taskSchedule[receipt.taskKey];
    const item = scheduleItem ?? {
      key: receipt.taskKey,
      name: receipt.taskName,
      img: receipt.taskImg,
      isComplete: !paused,
      isPaused: paused,
    } as any;
    // Synthesise the completion state so the dialog renders correctly even before
    // the cloud function writes back to the DB.
    const resultReceipt: ILockReceipt = { ...receipt, isComplete: !paused, isPaused: paused };
    this.updateRequestLockDialog({
      isPaused: paused,
      captureUpload: paused, // pause = member captures new image; unlock = member views the code
      item,
      result: resultReceipt,
      timedLock,
      unlockStartTime,
      unlockEndTime,
      penalties,
      hasCostPerMin: generalSettings.hygieneApplyCostPerMin,
      costPerMin: generalSettings.hygieneCostPerMin,
    });
  };

  public handleCompletedLockTasks = (): void => {
    const myTaskLocks = this.getMyTaskLocks();
    const task = myTaskLocks.find((t) => t.isPaused || t.isComplete);
    if (!task) return;
    // Delegate to the shared helper — use the receipt's own isPaused flag as
    // the authoritative "paused" signal when coming via the FB listener path.
    this.showLockReceiptDialog(task, task.isPaused ?? false);
  };

  public showLockRequest = (item: ITaskTimerInfo | IPenaltyTimerInfo): void => {
    // could be a pause or a complete
    this.updateRequestLockDialog({
      isPaused: false,
      captureUpload: true,
      item,
      result: undefined,
    }); // this will show the request for a new image
  };

  public endLock = (state: IRequestLockState) => {
    this.database.removeTaskLock(state, this.controlRecord.userid);
  };

  private checkingLockTimerOverrran = (
    unlockState: IRequestLockState
  ): void => {
    if (!unlockState.timedLock) {
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Hygiene Timer",
          "Not a timed lock",
          TraceLevel.warning
        )
      );
      return;
    }
    if (!unlockState.unlockEndTime) {
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Hygiene Timer",
          "No End Time for Lock",
          TraceLevel.warning
        )
      );
      return;
    }
    if (
      (!this.settings.hygienePenalties ||
        this.settings.hygienePenalties.length === 0) &&
      (!this.settings.hygienePillories ||
        this.settings.hygienePillories.length === 0)
    ) {
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Hygiene Timer",
          "No Penalties or Pillories for Lock",
          TraceLevel.warning
        )
      );
      return;
    }
    const ticks = differenceInSeconds(unlockState.unlockEndTime, Date.now());
    const isOver = ticks < 0;
    if (!isOver) {
      this.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Hygiene Timer",
          "Timer did not overrun for Hygiene",
          TraceLevel.info
        )
      );
      return;
    }

    const minutesOverdue =
      Math.abs(differenceInMinutes(unlockState.unlockEndTime, Date.now())) + 1;
    let additionalInfo: ItemAdditionOptions | undefined = undefined;
    if (unlockState.hasCostPerMin && unlockState.costPerMin)
      additionalInfo = {
        expectedCount: minutesOverdue * (unlockState.costPerMin ?? 0),
      };
    if (this.settings.hygienePenalties) {
      this.settings.hygienePenalties
        .map((p) => this.getPenalty(p))
        .filter((p) => p)
        .forEach((p) => {
          if (!p) return;
          this.addPenaltyToSchedule(
            p as IPenaltyItem,
            "Hygiene timer overran",
            undefined,
            additionalInfo,
            undefined
          );
          this.auditUser(
            new AuditMessage(
              AuditActionTypes.Session,
              "Hygiene Timer",
              `Adding penalty for late hygiene lock - ${p.name}`,
              TraceLevel.info
            )
          );
          sendPenaltyAddedMessageToOwner(this, p, "Hygiene timer overran");
        });
    }

    if (this.settings.hygienePillories) {
      this.settings.hygienePillories
        .map((p) => this.getPenalty(p))
        .filter((p) => p)
        .forEach((p) => {
          if (!p) return;
          this.auditUser(
            new AuditMessage(
              AuditActionTypes.Session,
              "Hygiene Timer",
              `Adding pillory for late hygiene lock - ${p.name}`,
              TraceLevel.info
            )
          );
          const { countType, count } = getPilloryTopicDefaults(p);
          const pendingItem = new PilloryTopic({
            updateType: PendingUpdateType.add,
            item: p,
            name: p.name,
            reason: "Late Hygiene Lock",
            username: this.getControl().username,
            userid: this.getControl().userid,
            ownername: this.getControl().getOwner().username,
            countType,
            incrementCount: count,
            pilloryEndTime: addDays(new Date(), 1).toISOString(),
            isRoulette: true,
            currentTotal: additionalInfo?.expectedCount ?? count,
            votedMembers: [],
            itemAdditionOptions: {
              isPillory: true,
              isRoulette: true,
              expectedCount: additionalInfo?.expectedCount,
            },
            voteOptions: {},
          });
          this.updatePilloryTopic(pendingItem);
          sendPilloryAddedMessageToOwner(this, pendingItem);
        });
    }
  };

  private restartTaskLock = (unlockState: IRequestLockState) => {
    void this.getConfig()
      .fetchConfig()
      .then((config) => {
        if (config.allowLocks && unlockState.result) {
          const taskInfo = this.getTaskSchedule();
          const basketInfo = taskInfo[unlockState.result.taskKey];
          // update the time remaining to match
          if (
            basketInfo.votable &&
            basketInfo.countType !== CountType.minutes &&
            basketInfo.voteCount !== undefined &&
            basketInfo.voteCount > 0
          ) {
            basketInfo.voteCount = getCountMinutes(
              basketInfo.voteCount,
              basketInfo.countType
            );
          }
          basketInfo.countType = CountType.minutes; // think this sets the the vote to then be in minutes
          basketInfo.count = differenceInMinutes(
            basketInfo.endTime as Date,
            new Date()
          ); // this works out how much to restart the task using
          basketInfo.expectedCount = basketInfo.count;
          this.auditUser(
            new AuditMessage(
              AuditActionTypes.Session,
              "Hygiene Complete",
              `Hygiene Complete. Chastity set to end on ${(
                basketInfo.endTime as Date
              ).toString()}. Set remaining time to ${basketInfo.expectedCount
              } minutes`,
              TraceLevel.info
            )
          );
          this.updateTaskState(basketInfo);
          this.fireListeners();
        }
      });
  };

  /* handling to relock - this should no longer call new dialog and instead this logic should setup state in the unlock image dialog */
  public handleRelockRequest = (unlockState: IRequestLockState): void => {
    this.checkingLockTimerOverrran(unlockState);
    this.restartTaskLock(unlockState);
  };

  public getOptionAndThen = (task: ITaskItem, callback: (option: string | undefined) => void) => {
    if (!task.options || task.options.length === 0) {
      callback(undefined);
      return;
    }
    if (task.options.length === 1) {
      callback(task.options[0]);
      return;
    }

    Emitter.emit(ShowOptionPickerDialog, task, this, callback);
  }

  public sendAttendanceImageToOwner = (imageSrc: string) => {
    void this.database.updateAttendanceRequestImage(
      imageSrc,
      this.getControl()
    );
  };

  public hasCustomScheduleToday = (): boolean => {
    const dateDay = convertToEventDayFromDate(new Date().getDay());
    return this.getScheduledCustomSchedule(dateDay, new Date()) !== undefined;
  }

  public getCustomScheduledDateEntries = (): { date: string; schedule: ICustomSchedule }[] => {
    const entries = this.settings.customScheduledDates ?? {};
    return Object.entries(entries)
      .map(([dateKey, scheduleKey]) => ({
        dateKey,
        scheduleKey,
        parsed: this.parseDateScheduleKey(dateKey),
      }))
      .filter(({ parsed }) => !!parsed)
      .sort((a, b) => a.parsed!.getTime() - b.parsed!.getTime())
      .map(({ dateKey, scheduleKey }) => {
        const schedule = this.getCustomSchedule(scheduleKey);
        if (!schedule) return undefined;
        return { date: dateKey, schedule };
      })
      .filter((entry): entry is { date: string; schedule: ICustomSchedule } => !!entry);
  };

  private STATE_KEYS_SCHEDULED_GAMES = "scheduledGames";
  public getCurrentGame = (): IScheduledGame & { game: IGame | undefined } | undefined => {
    const games = this.getState<IScheduledGame[]>(this.STATE_KEYS_SCHEDULED_GAMES, []);
    if (games.length === 0) return undefined;
    const gameKey = games[0].gameKey;
    const matchGame = this.getGame(gameKey);
    if (!matchGame) {
      if (this.gamesCache.itemsCache && this.gamesCache.itemsCache.length > 0) {
        // game no longer exists so remove it
        games.splice(0, 1);
        this.updateState(this.STATE_KEYS_SCHEDULED_GAMES, games);
        return this.getCurrentGame();
      }
      return undefined;
    }
    // Ensure full detail is available before returning; game components need subtype-specific data
    if (!this.gameDetailCache[gameKey]) {
      void this.loadGameDetail(gameKey); // triggers re-render via fireListeners when ready
      return undefined;
    }
    return { ...games[0], game: this.gameDetailCache[gameKey] };
  }

  public setGameResult = (result: IGameResult): void => {
    const games = this.getState<IScheduledGame[]>(this.STATE_KEYS_SCHEDULED_GAMES, []);
    const gameIdx = games.findIndex(g => g.gameKey === result.gameKey);
    if (gameIdx === -1) return;
    const game = games[gameIdx];
    const gameInstance = this.getGame(game.gameKey);
    if (!gameInstance) return;
    let reason = `Game Result: ${result.completed} correct, ${result.mistakes} mistakes`;
    if (gameInstance.gameType === GameTypes.quiz) {
      reason = `From Game ${gameInstance.name} - ${result.completed} correct out of ${result.total}`;
    } else if (gameInstance.gameType === GameTypes.wordsearch) {
      reason = `From Game ${gameInstance.name} - ${result.totalTime} minutes over timed limit for ${result.total} words. (${result.mistakes} mistakes)`;
    } else if (gameInstance.gameType === GameTypes.cardFlip) {
      reason = `From Game ${gameInstance.name} - ${result.penaltyCount} minutes over timed limit for ${result.total} pairs.`;
    } else if (gameInstance.gameType === GameTypes.higherlower) {
      reason = `From Game ${gameInstance.name} - ${result.mistakes} mistakes made when guessing ${result.total} cards.`;
    } else if (gameInstance.gameType === GameTypes.minefield) {
      reason = `From Game ${gameInstance.name} - ${result.mistakes} mistakes made when navigating ${result.total} steps.`;
    } else if (gameInstance.gameType === GameTypes.patternRecall) {
      reason = `From Game ${gameInstance.name} - ${result.mistakes} mistakes made when recalling ${result.total} patterns.`;
    } else if (gameInstance.gameType === GameTypes.memoryNumbers) {
      reason = `From Game ${gameInstance.name} - ${result.mistakes} mistakes made when recalling ${result.total} numbers.`;
    } else if (gameInstance.gameType === GameTypes.battleShips) {
      reason = `From Game ${gameInstance.name} - Failed to find ${result.penaltyCount} ships when sinking ${result.total} ships.`;
    } else if (gameInstance.gameType === GameTypes.blackJack) {
      reason = `From Game ${gameInstance.name} - Lost ${result.mistakes} when playing ${result.total} rounds of Blackjack.`;
    } else if (gameInstance.gameType === GameTypes.ticTacToe) {
      reason = `From Game ${gameInstance.name} - Lost ${result.mistakes} when playing ${result.total} rounds of Tic Tac Toe.`;
    } else if (gameInstance.gameType === GameTypes.rockPaperScissors) {
      reason = `From Game ${gameInstance.name} - Lost ${result.penaltyCount} when playing ${result.total} rounds of Rock Paper Scissors.`;
    } else if (gameInstance.gameType === GameTypes.snakesAndLadders) {
      reason = `From Game ${gameInstance.name} - Completed ${result.completed} action squares over ${result.totalTime} rolls.`;
    }
    games.splice(gameIdx, 1);
    this.updateState(this.STATE_KEYS_SCHEDULED_GAMES, games);

    const itemPlayed: TaskBasketItem = {
      key: `game-${gameInstance.key}-${v1()}`,
      itemType: ItemTypes.task,
      name: `Played Game: ${gameInstance.name}`,
      countType: CountType.none,
      expectedCount: 1,
      reason,
      expireOnPermission: false,
      isLimited: false,
      remainingMinutes: "0",
      percentComplete: 100,
      isPenalty: false,
      credit: 0,
      categories: [],
      preSelect: PreselectType.none,
      timesRequired: 0,
      getBasketText: () => `Played ${gameInstance.name}`,
      scheduleReason: "",
      isPaused: false,
      isComplete: true,
      completedTime: new Date(),
      description: "Member has played a game to completion",
      imgId: "",
      availabilityDayType: AvailabilityDayType.AllDays,
      decrementCountRequired: () => false,
      decrementTimesRemaining: () => false
    };
    this.updateTaskState(itemPlayed);

    let penaltyDescription = `Penalties for ${gameInstance.name}:`;
    if (game.penaltyKeys && game.penaltyKeys.length > 0) {
      //handle result
      if (result.penaltyCount > 0) {
        game.penaltyKeys.forEach(pk => {
          const penalty = this.getPenalty(pk);
          if (!penalty) return;
          penalty.key = pk + v1(); // ensure each penalty is counted separately
          penaltyDescription += `\n${penalty.name} x ${convertToCountTypeString(result.penaltyCount * (game.count ?? 1), penalty.countType)}`;
          this.createBasketItems(
            penalty,
            this.getIsWeekend,
            "Game Result",
            new Date(),
            {
              reason,
              expectedCount: result.penaltyCount * (game.count ?? 1),
            },
            undefined
          ).forEach(bi => this.updateTaskState(bi));
        });
      } else {
        penaltyDescription += " None incurred";
      }
    } else {
      penaltyDescription += " None Set (no penalties configured for game)";
    }
    if (this.getControl().isOwned())
      sendMessageToOwner(MessageType.GameResult, this,
        `Game Result for ${gameInstance.name}`,
        `The game was completed. ${reason}. ${penaltyDescription}`);
  }

  public queueScheduledGame = (game: IScheduledGame): void => {
    this.updatePendingItems({
      updateType: PendingUpdateType.add,
      gameRequest: game,
      date: formatValidDate(new Date(), "dd MMM yyyy"),
    });
  }

  public addScheduledGame = (game: IScheduledGame): void => {
    let actualGameKey = game.gameKey;

    // Handle random game of type selection
    if (game.gameKey.startsWith(RANDOM_GAME_OF_TYPE_PREFIX)) {
      const gameTypeLabel = game.gameKey.replace(RANDOM_GAME_OF_TYPE_PREFIX, "");

      // Map label back to GameTypes enum by comparing with getGameTypeName results
      const allGames = this.getGames();
      const matchingGames = allGames.filter(g => {
        return getGameTypeName(g.gameType).toLowerCase().replace(/\s+/g, "") ===
          gameTypeLabel.toLowerCase().replace(/\s+/g, "");
      });

      if (matchingGames.length > 0) {
        // Randomly select one game of this type
        const randomIndex = Math.floor(Math.random() * matchingGames.length);
        actualGameKey = matchingGames[randomIndex].key;

        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "addScheduledGame",
            `Randomly selected ${matchingGames[randomIndex].name} from ${matchingGames.length} ${gameTypeLabel} games`,
            TraceLevel.info
          )
        );
      } else {
        this.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "addScheduledGame",
            `No games found for type ${gameTypeLabel}`,
            TraceLevel.warning
          )
        );
        return; // Don't schedule if no games found
      }
    }

    this.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "addScheduledGame",
        "Adding Scheduled Game " + actualGameKey,
        TraceLevel.info
      )
    );
    const games = this.getState<IScheduledGame[]>(this.STATE_KEYS_SCHEDULED_GAMES, []);
    if (games.find(g => g.gameKey === actualGameKey)) return;
    games.push({ ...game, gameKey: actualGameKey });
    this.updateState(this.STATE_KEYS_SCHEDULED_GAMES, games);
  }

  public sendItemPreviewsToMember = (item: ITaskItem, subjectOverride?: string, initiatorUser?: IUserRecord) => {
    if (!item.itemsUsed) return;
    const basketItems: (TaskBasketItem | PenaltyBasketItem)[] = [];
    item.itemsUsed.forEach((_, optionIdx) => {
      basketItems.push(...this.createBasketItems(item, this.getIsWeekend, "Share All Outfit Options with Member", new Date(),
        optionIdx !== null && item.options ? {
          selectedChoiceName: item.choiceNames ? item.choiceNames[optionIdx] : undefined,
          selectedCondition: item.options[optionIdx],
          selectedItemGroupOptions: item.itemOptions && item.itemOptions[optionIdx] ? item.itemOptions[optionIdx] : undefined,
          selectedItemGroupKeys: item.itemsUsed && item.itemsUsed[optionIdx] ? item.itemsUsed[optionIdx] : undefined,
        } : undefined, undefined, true));
    });
    basketItems.sort((a, b) => a.name.localeCompare(b.name)); // sort alphabetically to make it harder to spot which is from which outfit
    sendPreviewMessageToMember(this, this.controlRecord, { pendingItems: basketItems } as any as IBasket, true,
      subjectOverride ?? ("Item Preview: " + item.name), initiatorUser);
    this.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "sendItemPreviewToMember",
        "Sending Item Preview to Member " + item.name,
        TraceLevel.info
      )
    );
  }

  public sendItemPreviewToMember = (item: ITaskItem, optionIdx: number | null, subjectOverride?: string,
    initiatorUser?: IUserRecord) => {
    const basketItems = this.createBasketItems(item, this.getIsWeekend, "Share Outfit with Member", new Date(),
      optionIdx !== null && item.options ? {
        selectedChoiceName: item.choiceNames ? item.choiceNames[optionIdx] : undefined,
        selectedCondition: item.options[optionIdx],
        selectedItemGroupOptions: item.itemOptions && item.itemOptions[optionIdx] ? item.itemOptions[optionIdx] : undefined,
        selectedItemGroupKeys: item.itemsUsed && item.itemsUsed[optionIdx] ? item.itemsUsed[optionIdx] : undefined,
      } : undefined, undefined, true);
    sendPreviewMessageToMember(this, this.controlRecord, { pendingItems: basketItems } as any as IBasket, true,
      subjectOverride ?? ("Item Preview: " + item.name), initiatorUser);
    this.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "sendItemPreviewToMember",
        "Sending Item Preview to Member " + item.name,
        TraceLevel.info
      )
    );
  }

  // ── Outfit ratings ────────────────────────────────────────────────────
  public saveOutfitRating = async (
    taskKey: string,
    optionKey: string,
    rating: ITaskRating
  ): Promise<void> => {
    const userid = this.getControl().userid;
    await this.database.saveOutfitRating(userid, taskKey, optionKey, rating);
  };

  public loadOutfitRatingsForTask = async (
    taskKey: string
  ): Promise<Record<string, ITaskRating> | null> => {
    const userid = this.getControl().userid;
    return this.database.getOutfitRatingsForTask(userid, taskKey);
  };

  // ── Schedule ratings ──────────────────────────────────────────────────
  public saveScheduleRating = async (
    scheduleKey: string,
    rating: ITaskRating
  ): Promise<void> => {
    const userid = this.getControl().userid;
    await this.database.saveScheduleRating(scheduleKey, userid, rating);
  };

  public loadScheduleRatings = async (
    scheduleKey: string
  ): Promise<Record<string, ITaskRating> | null> => {
    return this.database.getScheduleRatings(scheduleKey);
  };

  // ── Event ratings ─────────────────────────────────────────────────────────
  public saveEventRating = async (
    eventKey: string,
    rating: ITaskRating,
    scheduleKey?: string
  ): Promise<void> => {
    const userid = this.getControl().userid;
    await this.database.saveEventRating(eventKey, userid, rating, scheduleKey);
  };

  public loadEventRatings = async (
    eventKey: string,
    scheduleKey?: string
  ): Promise<Record<string, ITaskRating> | null> => {
    return this.database.getEventRatings(eventKey, scheduleKey);
  };
}
